import React, { useState, useEffect, useRef, useCallback, useMemo, useEffectEvent } from 'react';
import './LoanApplication.css';
import './LoginPage.css';
import './kioskTheme.css';
import './LoanApplication.theme.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from './apiBaseUrl';
import { buildAuthHeaders, clearUserSession, fetchUserDataWithAuth } from './authSession';
import { useLanguage } from './languageContext';
import { getLocalizedName } from './nameLocalization';
import { isKioskBridgeAvailable, launchExternalJourneyInKioskWrapper } from './kioskBridge';
import { FullPageLoader, useRouteEntryLoader } from './navigationLoader';
import { SalaryGuidanceCard, SalarySlipUploadModal } from './salaryGuidance';

const PROCESSING_FEE_RATE = 0.10;
const GST_ON_FEE_RATE = 0.18;
const DAILY_INTEREST_RATE = 0.01;
const DIGILOCKER_SESSION_STORAGE_KEY = 'pendingDigilockerSession';
const DIGILOCKER_POLL_LOCK_PREFIX = 'digilockerPollLock:';
const CAMS_CALLBACK_STORAGE_PREFIX = 'camsConsentCallbackLogged:';
const DIGILOCKER_POLL_INTERVAL_MS = 2000;
const DIGILOCKER_MIN_POLL_GAP_MS = 2000;
const DIGILOCKER_MAX_POLL_DURATION_MS = 15 * 60 * 1000;
const DIGILOCKER_POPUP_NAME = 'finncube-digilocker-verification';
const MONEYONE_POLL_INTERVAL_MS = 1000;
const MONEYONE_MAX_POLL_ATTEMPTS = 24;
const MONEYONE_CONSENT_REQUEST_TIMEOUT_MS = 30000;
const DEFAULT_MONEYONE_AA_HANDLE = 'onemoney';
const SIMPLE_AA_STATUS = Object.freeze({
    VERIFIED: 'VERIFIED',
    PENDING: 'PENDING',
    NOT_VERIFIED: 'NOT_VERIFIED'
});
const AA_STATUS_POLL_INTERVAL_MS = 3000;
const AA_STATUS_SETTLE_ATTEMPTS = 24;
const JOURNEY_AUTOSAVE_DEBOUNCE_MS = 800;
const DEFAULT_LOAN_PROGRESS_SAVE_ENABLED = false;
const LOAN_PROGRESS_STORAGE_KEY = 'loan_application_saved_state';
const DEMO_DISBURSAL_MOBILE = String(process.env.REACT_APP_DEMO_DISBURSAL_MOBILE || '').trim();
const JOURNEY_COMPLETION_STORAGE_PREFIX = 'loanJourneyCompleted:';
const AA_PENDING_WAIT_MAX_MS = 60 * 1000;
const AA_OFFER_REFRESH_TOTAL_MS = 60 * 1000;
const AA_DATA_TIMEOUT_LOGOUT_MESSAGE = 'Thanks for choosing Finncube!. Our team will reach you soon.';
const AA_DATA_TIMEOUT_LOGOUT_DELAY_MS = 4000;
const AA_DECISION_FETCH_LOADER_MESSAGE = 'We are fetching your data please wait for a while.';
const AA_DECISION_FETCH_COUNTDOWN_SECONDS = Math.max(1, Math.floor(AA_OFFER_REFRESH_TOTAL_MS / 1000));
const JOURNEY_STAGES = [
    { id: 1, title: 'Offer Details',       icon: '📋' },
    { id: 2, title: 'Account Aggregator',  icon: '🏦' },
    { id: 3, title: 'Loan Details',        icon: '🎚️' },
    { id: 4, title: 'Aadhaar Verify',      icon: '🛡️' },
    { id: 5, title: 'Selfie KYC',          icon: '🤳' },
    { id: 6, title: 'Loan Summary',        icon: '📑' },
    { id: 7, title: 'Penny Drop',          icon: '💳' },
    { id: 8, title: 'Disbursement',        icon: '💸' }
];

const DEFAULT_VERIFICATION_DATA = {
    aadhaarRequestId: '',
    officialEmail: '',
    selfieTempUrl: '',
    faceMatchDone: false,
    faceMatchSimilarity: null
};

const DEFAULT_AADHAAR_FETCH_STATE = {
    loading: false,
    fetched: false,
    details: null
};

const isAadhaarDetailsVerified = (details) => {
    if (!details || typeof details !== 'object') return false;
    const name = String(details.name || '').trim();
    if (!name || name.toLowerCase() === 'customer' || name === '--') return false;
    const hasIdentifier = Boolean(
        String(details.aadhaarLast4 || '').trim() ||
        String(details.maskedUid || '').trim() ||
        String(details.requestId || '').trim()
    );
    return hasIdentifier;
};

const DEFAULT_BANK_DETAILS = {
    accountNumber: '',
    ifscCode: ''
};

const IFSC_BANK_NAME_OVERRIDES = {
    HDFC: 'HDFC Bank',
    PUNB: 'Punjab National Bank',
    ICIC: 'ICICI Bank',
    SBIN: 'State Bank of India',
    UTIB: 'Axis Bank',
    KKBK: 'Kotak Mahindra Bank',
    CNRB: 'Canara Bank',
    BARB: 'Bank of Baroda',
    SYNB: 'Canara Bank',
    IDFB: 'IDFC FIRST Bank',
    IDIB: 'Indian Bank',
    IBKL: 'IDBI Bank',
    MAHB: 'Bank of Maharashtra'
};

const resolveBankNameFromIfsc = (ifscCode = '') => {
    const normalized = String(ifscCode || '').trim().toUpperCase();
    if (normalized.length < 4) return '';
    const bankCode = normalized.slice(0, 4);
    return IFSC_BANK_NAME_OVERRIDES[bankCode] || '';
};

const IFSC_CODE_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const DEFAULT_MONEYONE_CONFIG = {
    aaHandle: DEFAULT_MONEYONE_AA_HANDLE
};

const DEFAULT_MONEYONE_STATE = {
    connecting: false,
    polling: false,
    fetchingData: false,
    accountFetchComplete: false,
    accountFetchBlocked: false,
    consentWindowOpen: false,
    consentStartedAt: '',
    lastStatusCheckAt: '',
    nextStatusCheckAt: '',
    consentHandle: '',
    consentId: '',
    redirectUrl: '',
    status: '',
    accounts: [],
    selectedLinkRefNumber: '',
    selectedAccount: null,
    session: null
};

const MIN_LOAN_TENURE_DAYS = 7;
const MAX_LOAN_TENURE_DAYS = 45;
const DEMO_AADHAAR_VERIFICATION_ID = 'DEMO-AADHAAR-VERIFIED';

const getCustomerVerifiedEmailKey = (mobile) => {
    const clean = String(mobile || '').replace(/\D/g, '').slice(-10);
    return clean ? `kiosk_verified_email_${clean}` : '';
};

const getCustomerVerifiedEmail = (mobile) => {
    const key = getCustomerVerifiedEmailKey(mobile);
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.verified && parsed.email) {
            return String(parsed.email).trim().toLowerCase();
        }
    } catch (_e) {}
    return null;
};

const saveCustomerVerifiedEmail = (mobile, email, loanId = '') => {
    const key = getCustomerVerifiedEmailKey(mobile);
    if (!key || !email) return;
    try {
        localStorage.setItem(key, JSON.stringify({
            email: String(email).trim().toLowerCase(),
            verified: true,
            loanId: String(loanId || '').trim(),
            verifiedAt: new Date().toISOString()
        }));
    } catch (_e) {}
};

const clearCustomerVerifiedEmail = (mobile) => {
    const key = getCustomerVerifiedEmailKey(mobile);
    if (key) {
        try {
            localStorage.removeItem(key);
        } catch (_e) {}
    }
};

const clampLoanTenureDays = (value, baseDate = new Date(), maxLimit = MAX_LOAN_TENURE_DAYS) => {
    const maxBound = Math.max(MIN_LOAN_TENURE_DAYS, Number(maxLimit) || MAX_LOAN_TENURE_DAYS);
    const numeric = Number(value);
    const rawClamped = (!Number.isFinite(numeric) || numeric <= 0)
        ? maxBound
        : Math.min(maxBound, Math.max(MIN_LOAN_TENURE_DAYS, Math.round(numeric)));
    return resolveNonSundayTenure(rawClamped, { baseDate, minDays: MIN_LOAN_TENURE_DAYS, maxDays: maxBound, preferredDirection: -1 });
};

const normalizeIdentityName = (value) => String(value || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .trim();

const SALARY_CYCLE_DUE_DAY = 7;
const MAX_REPAYMENT_CUTOFF_DAY = 20;

const getNextSalaryCycleDueDate = (baseDate = new Date(), salaryCycleDueDay = SALARY_CYCLE_DUE_DAY) => {
    const anchor = new Date(baseDate);
    anchor.setHours(0, 0, 0, 0);
    const rawDueDay = Math.max(1, Math.min(31, Math.round(Number(salaryCycleDueDay || SALARY_CYCLE_DUE_DAY))));
    const targetMonthOffset = anchor.getDate() <= MAX_REPAYMENT_CUTOFF_DAY ? 1 : 2;
    const targetYear = anchor.getFullYear();
    const targetMonth = anchor.getMonth() + targetMonthOffset;
    const maxDaysInTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    const sanitizedDueDay = Math.min(rawDueDay, maxDaysInTargetMonth);

    const dueDate = new Date(targetYear, targetMonth, sanitizedDueDay);
    dueDate.setHours(0, 0, 0, 0);
    if (dueDate.getDay() === 0) {
        dueDate.setDate(dueDate.getDate() + 1);
    }
    return dueDate;
};

const getDaysUntilDate = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
};

const getDateAfterDays = (baseDate = new Date(), days = 0) => {
    const anchor = new Date(baseDate);
    anchor.setHours(0, 0, 0, 0);
    anchor.setDate(anchor.getDate() + Math.max(0, Math.round(Number(days || 0))));
    return anchor;
};

const isDateSunday = (baseDate = new Date(), days = 0) => {
    const target = getDateAfterDays(baseDate, days);
    return target.getDay() === 0;
};

const resolveNonSundayTenure = (
    targetTenure,
    {
        baseDate = new Date(),
        minDays = MIN_LOAN_TENURE_DAYS,
        maxDays = MAX_LOAN_TENURE_DAYS,
        preferredDirection = 0
    } = {}
) => {
    let tenure = Math.max(minDays, Math.min(maxDays, Math.round(Number(targetTenure) || minDays)));
    if (!isDateSunday(baseDate, tenure)) {
        return tenure;
    }

    if (preferredDirection > 0) {
        if (tenure + 1 <= maxDays && !isDateSunday(baseDate, tenure + 1)) return tenure + 1;
        if (tenure - 1 >= minDays && !isDateSunday(baseDate, tenure - 1)) return tenure - 1;
    } else if (preferredDirection < 0) {
        if (tenure - 1 >= minDays && !isDateSunday(baseDate, tenure - 1)) return tenure - 1;
        if (tenure + 1 <= maxDays && !isDateSunday(baseDate, tenure + 1)) return tenure + 1;
    } else {
        if (tenure + 1 <= maxDays && !isDateSunday(baseDate, tenure + 1)) return tenure + 1;
        if (tenure - 1 >= minDays && !isDateSunday(baseDate, tenure - 1)) return tenure - 1;
    }

    return tenure;
};

const formatDisplayDateWithDay = (date, isHindi = false) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const dateStr = d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
    const weekday = d.toLocaleDateString(isHindi ? 'hi-IN' : 'en-IN', { weekday: 'long' });
    return `${dateStr} (${weekday})`;
};

const normalizeRate = (value, fallback, { treatZeroAsMissing = false } = {}) => {
    if (value == null || value === '') return fallback;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return fallback;
    if (treatZeroAsMissing && numeric === 0) return fallback;
    return numeric >= 1 ? numeric / 100 : numeric;
};

const escapeSanctionHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatSanctionAmount = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
// Spin wheel now awards a variable processing-fee discount (5/10/15/20%) instead of a fixed 20%.
// Falls back to the old boolean flag so a reward earned before this change still applies at 20%.
const getSpinFeeDiscountPercent = () => {
    const storedPercent = Number(localStorage.getItem('spin_reward_fee_discount_percent'));
    if (Number.isFinite(storedPercent) && storedPercent > 0) return storedPercent;
    return localStorage.getItem('spin_reward_20_percent_fee') === 'true' ? 20 : 0;
};
const DEFAULT_LOAN_PRINCIPAL_AMOUNT = Number(process.env.REACT_APP_MIN_LOAN_AMOUNT || 10000);
const OFFER_DETAILS_PREVIEW_MAX_AMOUNT = 100000;

const shouldSuppressAaPopupMessage = (message) => {
    const normalized = String(message || '').trim().toLowerCase();
    return Boolean(
        normalized
        && (
            normalized.includes('periodic consent is not provided')
            || normalized.includes('consent for the periodic is not provided')
            || (normalized.includes('periodic') && normalized.includes('not provided') && normalized.includes('consent'))
        )
    );
};

const toPositiveNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const resolveLoanJourneyPrincipalAmount = (loanJourney, { requestedAmount = 0, approvedLimitAmount = 0 } = {}) => {
    const directAmount = toPositiveNumber(loanJourney?.sanctionedAmount ?? loanJourney?.sanctioned_amount);
    if (directAmount) return directAmount;

    const finalDisbursement = toPositiveNumber(loanJourney?.finalDisbursement ?? loanJourney?.final_disbursement);
    const processingFee = toPositiveNumber(loanJourney?.processingFee ?? loanJourney?.processing_fee);
    const gstAmount = toPositiveNumber(loanJourney?.gstAmount ?? loanJourney?.gst_amount);
    if (finalDisbursement != null && processingFee != null && gstAmount != null) {
        return finalDisbursement + processingFee + gstAmount;
    }

    return toPositiveNumber(approvedLimitAmount) || toPositiveNumber(requestedAmount) || DEFAULT_LOAN_PRINCIPAL_AMOUNT;
};

const resolveLoanJourneyTenureDays = (loanJourney, fetchedData = null, selectedTenure = null) => {
    const candidates = [
        selectedTenure,
        loanJourney?.selectedTenureDays,
        loanJourney?.selected_tenure_days,
        loanJourney?.tenureDays,
        loanJourney?.tenure_days,
        fetchedData?.latestLoanJourney?.selectedTenureDays,
        fetchedData?.latestLoanJourney?.selected_tenure_days,
        fetchedData?.latestLoanJourney?.tenureDays,
        fetchedData?.latestLoanJourney?.tenure_days
    ];

    for (const candidate of candidates) {
        const numeric = toPositiveNumber(String(candidate ?? '').replace(/\D/g, ''));
        if (numeric) return clampLoanTenureDays(numeric);
    }

    return null;
};

const patchSanctionLetterHtmlAmounts = (html, {
    principalAmount,
    processingFee,
    gstAmount,
    finalDisbursement,
    totalInterestCharge,
    totalRepaymentAmount,
    tenureDays
}) => {
    const sourceHtml = String(html || '').trim();
    if (!sourceHtml) return '';

    const formatPlainAmount = (amount) => Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    const totalFeesAmount = Number(processingFee || 0) + Number(gstAmount || 0);
    const isBrowserDomAvailable = typeof document !== 'undefined' && typeof document.createElement === 'function';

    const patchUsingRegexFallback = (input) => {
        const moneyPattern = '(?:₹|Rs\\.?\\s*)';
        const replaceNearLabel = (value, labels, amount) => {
            const formattedAmount = formatPlainAmount(amount);
            for (const label of labels) {
                const pattern = new RegExp(`(${label})([\\s\\S]{0,180}?${moneyPattern})([\\d,]+(?:\\.\\d+)?)`, 'i');
                if (pattern.test(value)) {
                    return value.replace(pattern, (_match, prefix, currency) => `${prefix}${currency}${formattedAmount}`);
                }
            }
            return value;
        };

        let patchedHtml = input;
        patchedHtml = replaceNearLabel(patchedHtml, [
            'loan amount',
            'sanction loan amount',
            'sanctioning you an amount of',
            'amount of'
        ], principalAmount);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'processing fee'
        ], processingFee);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'fees (inr)',
            'pf + gst (inr)'
        ], totalFeesAmount);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'gst on processing fee',
            'gst'
        ], gstAmount);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'total fees'
        ], totalFeesAmount);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'net disbursed amount'
        ], finalDisbursement);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'interest till due date',
            'total interest charge during the entire tenure of the loan',
            'interest charge during the entire tenure of the loan'
        ], totalInterestCharge);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'total repayment amount'
        ], totalRepaymentAmount);
        patchedHtml = replaceNearLabel(patchedHtml, [
            'repayable (inr)'
        ], totalRepaymentAmount);

        if (Number.isFinite(Number(tenureDays)) && Number(tenureDays) > 0) {
            patchedHtml = patchedHtml.replace(
                /(Tenure:\s*)(?:\d+\s*days?)/i,
                (_match, prefix) => `${prefix}${formatPlainAmount(tenureDays)} days`
            );
            patchedHtml = patchedHtml.replace(
                /(Tenure of the Loan \(in Days\)[\s\S]{0,200}?)(\d+)(\s*Days?)/i,
                (_match, prefix, _value, suffix) => `${prefix}${formatPlainAmount(tenureDays)}${suffix}`
            );
        }

        return patchedHtml;
    };

    if (!isBrowserDomAvailable) {
        return patchUsingRegexFallback(sourceHtml);
    }

    try {
        const template = document.createElement('template');
        template.innerHTML = sourceHtml;
        const root = template.content;
        const textNodes = [];
        const walker = document.createTreeWalker(root, 4);
        while (walker.nextNode()) {
            textNodes.push(walker.currentNode);
        }

        const replaceTextNodes = (replacements) => {
            for (const textNode of textNodes) {
                let nextText = String(textNode.nodeValue || '');
                let changed = false;
                for (const { pattern, replacement } of replacements) {
                    const updatedText = nextText.replace(pattern, replacement);
                    if (updatedText !== nextText) {
                        nextText = updatedText;
                        changed = true;
                    }
                }
                if (changed) {
                    textNode.nodeValue = nextText;
                }
            }
        };

        const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const rowCellTexts = (row) => Array.from(row.querySelectorAll('td,th')).map((cell) => normalize(cell.textContent));

        const patchLabeledRows = (rules) => {
            for (const row of Array.from(root.querySelectorAll('tr'))) {
                const cells = Array.from(row.querySelectorAll('td,th'));
                if (!cells.length) continue;
                const texts = rowCellTexts(row);
                for (const rule of rules) {
                    if (!rule.matchers.some((matcher) => texts.some(matcher))) {
                        continue;
                    }
                    const lastCell = cells[cells.length - 1];
                    if (lastCell) {
                        lastCell.textContent = rule.value;
                    }
                    break;
                }
            }
        };

        const patchRepaymentScheduleTables = () => {
            for (const table of Array.from(root.querySelectorAll('table'))) {
                const rows = Array.from(table.querySelectorAll('tr'));
                if (rows.length < 2) continue;

                const headerIndex = rows.findIndex((row) => {
                    const texts = rowCellTexts(row);
                    return texts.some((text) => text.includes('principal (inr)'))
                        && texts.some((text) => text.includes('interest (inr)'))
                        && texts.some((text) => text.includes('due date'))
                        && texts.some((text) => text.includes('repayable (inr)'))
                        && texts.some((text) => text.includes('fees (inr)') || text.includes('pf + gst (inr)'));
                });

                if (headerIndex < 0) continue;
                const dataRow = rows[headerIndex + 1];
                if (!dataRow) continue;

                const cells = Array.from(dataRow.querySelectorAll('td,th'));
                if (cells.length < 6) continue;

                cells[1].textContent = `Rs. ${formatPlainAmount(principalAmount)}`;
                cells[2].textContent = `Rs. ${formatPlainAmount(totalInterestCharge)}`;
                cells[3].textContent = `Rs. ${formatPlainAmount(totalFeesAmount)}`;
                cells[5].textContent = `Rs. ${formatPlainAmount(totalRepaymentAmount)}`;
            }
        };

        patchLabeledRows([
            {
                matchers: [
                    (text) => text.includes('loan amount'),
                    (text) => text.includes('sanction loan amount'),
                    (text) => text.includes('sanctioning you an amount of')
                ],
                value: `Rs. ${formatPlainAmount(principalAmount)}`
            },
            {
                matchers: [
                    (text) => text === 'processing fee'
                ],
                value: `Rs. ${formatPlainAmount(processingFee)}`
            },
            {
                matchers: [
                    (text) => text.includes('gst on processing fee'),
                    (text) => text === 'gst'
                ],
                value: `Rs. ${formatPlainAmount(gstAmount)}`
            },
            {
                matchers: [
                    (text) => text.includes('total fees')
                ],
                value: `Rs. ${formatPlainAmount(totalFeesAmount)}`
            },
            {
                matchers: [
                    (text) => text.includes('net disbursed amount')
                ],
                value: `Rs. ${formatPlainAmount(finalDisbursement)}`
            },
            {
                matchers: [
                    (text) => text.includes('interest till due date'),
                    (text) => text.includes('total interest charge during the entire tenure of the loan'),
                    (text) => text.includes('interest charge during the entire tenure of the loan')
                ],
                value: `Rs. ${formatPlainAmount(totalInterestCharge)}`
            },
            {
                matchers: [
                    (text) => text.includes('total repayment amount')
                ],
                value: `Rs. ${formatPlainAmount(totalRepaymentAmount)}`
            },
            {
                matchers: [
                    (text) => text.includes('tenure of the loan (in days)')
                ],
                value: `${formatPlainAmount(tenureDays)} Days`
            }
        ]);

        patchRepaymentScheduleTables();

        replaceTextNodes([
            {
                pattern: /(\bLoan Amount:\s*Rs\.\s*)[\d,]+/i,
                replacement: (_match, prefix) => `${prefix}${formatPlainAmount(principalAmount)}`
            },
            {
                pattern: /(\bSanction Loan Amount:\s*Rs\.\s*)[\d,]+/i,
                replacement: (_match, prefix) => `${prefix}${formatPlainAmount(principalAmount)}`
            },
            {
                pattern: /(\bsanctioning you an amount of\s*Rs\.\s*)[\d,]+/i,
                replacement: (_match, prefix) => `${prefix}${formatPlainAmount(principalAmount)}`
            },
            {
                pattern: /(\bNet Disbursed Amount:\s*Rs\.\s*)[\d,]+/i,
                replacement: (_match, prefix) => `${prefix}${formatPlainAmount(finalDisbursement)}`
            },
            {
                pattern: /(\bTotal Repayment Amount:\s*Rs\.\s*)[\d,]+/i,
                replacement: (_match, prefix) => `${prefix}${formatPlainAmount(totalRepaymentAmount)}`
            },
            {
                pattern: /(\bTenure:\s*)\d+\s*days?/i,
                replacement: (_match, prefix) => `${prefix}${formatPlainAmount(tenureDays)} days`
            }
        ]);

        const tenureRows = Array.from(root.querySelectorAll('tr')).filter((row) => {
            const texts = rowCellTexts(row);
            return texts.some((text) => text.includes('tenure of the loan (in days)'));
        });
        for (const row of tenureRows) {
            const cells = Array.from(row.querySelectorAll('td,th'));
            if (cells.length < 2) continue;
            cells[cells.length - 1].textContent = `${formatPlainAmount(tenureDays)} Days`;
        }

        return template.innerHTML || sourceHtml;
    } catch (_error) {
        return patchUsingRegexFallback(sourceHtml);
    }
};

const formatSanctionDateText = (value) => {
    const parsed = value ? new Date(value) : new Date();
    if (Number.isNaN(parsed.getTime())) return String(value || '--');
    const dateStr = parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
    const weekday = parsed.toLocaleDateString('en-GB', { weekday: 'long' });
    return `${dateStr} (${weekday})`;
};

const buildSanctionLetterPreviewHtml = ({
    loanId,
    customerName,
    customerAddress,
    dateOfBirth,
    mobile,
    email,
    panNumber,
    aadhaarLast4,
    bankName,
    ifscCode,
    accountLast4,
    borrowerSignatureDataUri = null,
    borrowerSignatureSignedAtText = null,
    sanctionedAmount,
    processingFee,
    gstAmount,
    finalDisbursement,
    totalInterestCharge,
    totalRepaymentAmount,
    repaymentDate,
    sanctionDate,
    tenureDays,
    hasProcessorFeeDiscount = false
}) => `
    <div style="margin:0;padding:24px;background:#f8fafc;font-family:Arial,sans-serif;color:#0f172a;">
        <div style="max-width:820px;margin:0 auto;background:#ffffff;border:1px solid #e5e7eb;border-radius:20px;overflow:hidden;box-shadow:0 18px 50px rgba(15,23,42,0.08);">
            <div style="padding:24px 28px 20px;background:#ffffff;border-bottom:1px solid #e5e7eb;">
                <div style="display:flex;justify-content:space-between;gap:20px;align-items:flex-start;">
                    <div>
                        <div style="font-size:14px;font-weight:800;letter-spacing:0.18em;text-transform:uppercase;color:#111827;">Pawansut Holdings Limited</div>
                        <div style="margin-top:8px;font-size:28px;font-weight:900;color:#9a3412;line-height:1.1;">Sanction Letter Cum Key Fact Statement</div>
                        <div style="margin-top:8px;font-size:13px;line-height:1.7;color:#475569;">
                            CIN: L65929DL1984PLC019506<br />
                            415, Usha Kiran Building, Commercial Complex, Azadpur, Delhi-110033
                        </div>
                    </div>
                    <div style="min-width:240px;padding:16px 18px;border-radius:18px;background:linear-gradient(180deg,#fff7ed 0%,#ffedd5 100%);border:1px solid #fed7aa;">
                        <div style="font-size:11px;font-weight:800;letter-spacing:0.18em;color:#c2410c;text-transform:uppercase;">Reference</div>
                        <div style="margin-top:6px;font-size:20px;font-weight:800;color:#111827;">${escapeSanctionHtml(loanId || '--')}</div>
                        <div style="margin-top:10px;font-size:13px;color:#7c2d12;">Date: ${escapeSanctionHtml(formatSanctionDateText(sanctionDate))}</div>
                        <div style="font-size:13px;color:#7c2d12;">Tenure: ${escapeSanctionHtml(tenureDays || '--')} days</div>
                    </div>
                </div>
            </div>
            <div style="padding:26px 28px;">
                <p style="margin:0 0 10px;font-size:15px;color:#111827;">Dear <strong>${escapeSanctionHtml(customerName || 'Customer')}</strong>,</p>
                <p style="margin:0 0 10px;font-size:14px;color:#334155;line-height:1.8;">Sub: Personal Loan Sanction.</p>
                <p style="margin:0 0 20px;font-size:14px;color:#475569;line-height:1.8;">This sanction letter is issued against your verified application details and approved credit terms. Please review the loan summary and key borrower details below.</p>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:22px;">
                    <div style="padding:14px 16px;border:1px solid #e5e7eb;border-radius:14px;background:#f8fafc;">
                        <div style="font-size:11px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:#94a3b8;">Borrower</div>
                        <div style="margin-top:8px;font-size:16px;font-weight:700;color:#111827;">${escapeSanctionHtml(customerName || '--')}</div>
                        <div style="margin-top:6px;font-size:13px;line-height:1.7;color:#475569;">PAN: ${escapeSanctionHtml(panNumber || '--')}<br />DOB: ${escapeSanctionHtml(dateOfBirth || '--')}<br />Aadhaar: ${escapeSanctionHtml(aadhaarLast4 ? `XXXX XXXX ${aadhaarLast4}` : '--')}</div>
                    </div>
                    <div style="padding:14px 16px;border:1px solid #e5e7eb;border-radius:14px;background:#f8fafc;">
                        <div style="font-size:11px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:#94a3b8;">Contact & Bank</div>
                        <div style="margin-top:8px;font-size:13px;line-height:1.8;color:#475569;">Mobile: ${escapeSanctionHtml(mobile || '--')}<br />Email: ${escapeSanctionHtml(email || '--')}<br />Bank: ${escapeSanctionHtml(bankName || '--')}<br />A/C: ${escapeSanctionHtml(accountLast4 ? `XXXXXX${accountLast4}` : '--')} | IFSC: ${escapeSanctionHtml(ifscCode || '--')}</div>
                    </div>
                </div>
                <div style="border:1px solid #e5e7eb;border-radius:16px;overflow:hidden;">
                    <div style="padding:14px 16px;background:#fff7ed;border-bottom:1px solid #fed7aa;font-size:13px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:#c2410c;">Loan Summary</div>
                    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="font-size:14px;border-collapse:collapse;">
                        <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;width:45%;">Loan Amount</td><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:700;color:#111827;">${formatSanctionAmount(sanctionedAmount)}</td></tr>
                        <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;">Processing Fee ${hasProcessorFeeDiscount ? '(20% OFF)' : ''}</td><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:right;color:#111827;">${formatSanctionAmount(processingFee)}</td></tr>
                        <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;">GST on Processing Fee</td><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:right;color:#111827;">${formatSanctionAmount(gstAmount)}</td></tr>
                        <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;">Total Fees</td><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:right;color:#111827;">${formatSanctionAmount(Number(processingFee || 0) + Number(gstAmount || 0))}</td></tr>
                        <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;">Net Disbursed Amount</td><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:right;color:#065f46;font-weight:700;">${formatSanctionAmount(finalDisbursement)}</td></tr>
                        <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;">Interest Till Due Date</td><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:right;color:#111827;">${formatSanctionAmount(totalInterestCharge)}</td></tr>
                        <tr><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;background:#f8fafc;color:#64748b;">Total Repayment Amount</td><td style="padding:12px 16px;border-bottom:1px solid #e5e7eb;text-align:right;color:#9a3412;font-weight:800;">${formatSanctionAmount(totalRepaymentAmount)}</td></tr>
                        <tr><td style="padding:12px 16px;background:#f8fafc;color:#64748b;">Repayment Date</td><td style="padding:12px 16px;text-align:right;color:#111827;font-weight:700;">${escapeSanctionHtml(formatSanctionDateText(repaymentDate))}</td></tr>
                    </table>
                </div>
                <div style="margin-top:18px;padding:16px 18px;border-radius:14px;background:#f8fafc;border:1px solid #e5e7eb;">
                    <div style="font-size:12px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:#94a3b8;">Registered Address</div>
                    <div style="margin-top:8px;font-size:13px;line-height:1.8;color:#475569;">${escapeSanctionHtml(customerAddress || '--')}</div>
                </div>
                <div style="margin-top:18px;padding:16px 18px;border-radius:14px;background:#fff7ed;border:1px solid #fed7aa;font-size:13px;line-height:1.8;color:#7c2d12;">
                    Interest applies until the due date as per approved pricing. If repayment is delayed beyond the due date, additional penal charges may apply as per lender terms.
                </div>
                <div style="margin-top:18px;padding:16px 18px;border-radius:14px;background:#fff7ed;border:1px solid #fed7aa;font-size:13px;line-height:1.8;color:#7c2d12;">
                    <div style="font-size:12px;font-weight:800;letter-spacing:0.12em;text-transform:uppercase;color:#9a3412;margin-bottom:8px;">Borrower Acceptance</div>
                    ${String(borrowerSignatureDataUri || '').trim() ? `<div style="margin:0 0 12px;"><img src="${escapeSanctionHtml(String(borrowerSignatureDataUri || '').trim())}" alt="Borrower signature" style="max-width:260px;max-height:100px;width:auto;height:auto;object-fit:contain;display:block;" /></div>` : ''}
                    <div><strong>Accepted by:</strong> ${escapeSanctionHtml(customerName || 'Customer')}</div>
                    <div>I accept the sanction letter and commercial terms.</div>
                    <div><strong>Accepted on:</strong> ${escapeSanctionHtml(borrowerSignatureSignedAtText || formatSanctionDateText(sanctionDate))}</div>
                </div>
            </div>
        </div>
    </div>
`;

const getDisplayMessage = (message, fallback = 'Something went wrong.') => {
    if (typeof message === 'string') {
        const trimmed = message.trim();
        return trimmed || fallback;
    }

    if (typeof message === 'number' || typeof message === 'boolean') {
        return String(message);
    }

    if (message && typeof message === 'object') {
        const nestedMessage = message.message || message.error || message.reason || message.details;
        if (nestedMessage && nestedMessage !== message) {
            return getDisplayMessage(nestedMessage, fallback);
        }
        try {
            return JSON.stringify(message);
        } catch (_error) {
            return fallback;
        }
    }

    return fallback;
};

const maskTrailingVisible = (value, visibleCount = 4, maskChar = '*') => {
    const normalized = String(value || '').trim();
    if (!normalized) {
        return '';
    }

    if (normalized.length <= visibleCount) {
        return normalized;
    }

    return `${maskChar.repeat(normalized.length - visibleCount)}${normalized.slice(-visibleCount)}`;
};

const maskMobileNumber = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length <= 4) return digits;
    return `${'•'.repeat(digits.length - 4)}${digits.slice(-4)}`;
};

const maskPanNumber = (value) => {
    const normalized = String(value || '').trim().toUpperCase();
    if (!normalized) return '';
    if (normalized.length <= 4) return normalized;
    return `${'•'.repeat(normalized.length - 4)}${normalized.slice(-4)}`;
};

const getJourneyCompletionStorageKey = (mobile) => {
    const normalizedMobile = String(mobile || '').replace(/\D/g, '').trim();
    return normalizedMobile ? `${JOURNEY_COMPLETION_STORAGE_PREFIX}${normalizedMobile}` : '';
};
const LOAN_JOURNEY_FORCE_FRESH_START_KEY = 'loan_journey_force_fresh_start';
// A declined customer gets a minute on the rejection screen to act (link another account,
// upload a salary slip) before the kiosk session closes itself.
const REJECTION_SCREEN_AUTO_LOGOUT_MS = 60000;
const markLoanJourneyForceFreshStart = () => {
    try {
        localStorage.setItem(LOAN_JOURNEY_FORCE_FRESH_START_KEY, '1');
    } catch (_error) {
    }
};
const consumeLoanJourneyForceFreshStart = () => {
    try {
        const value = localStorage.getItem(LOAN_JOURNEY_FORCE_FRESH_START_KEY);
        localStorage.removeItem(LOAN_JOURNEY_FORCE_FRESH_START_KEY);
        return value === '1';
    } catch (_error) {
        return false;
    }
};

const normalizeMoneyoneAccounts = (payload) => {
    const items = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : [];

    return items.map((item, index) => {
        const holder = item?.Profile?.Holders?.Holder?.[0] || {};
        const summary = item?.Summary || {};
        return {
            id: String(item?.linkReferenceNumber || item?.linkRefNumber || `moneyone-${index}`),
            linkRefNumber: String(item?.linkReferenceNumber || item?.linkRefNumber || '').trim(),
            maskedAccountNumber: String(item?.maskedAccountNumber || '').trim(),
            bank: String(item?.bank || '').trim(),
            fiType: String(item?.fiType || '').trim(),
            accountType: String(summary?.type || summary?.accountType || '').trim(),
            ifscCode: String(summary?.ifscCode || summary?.ifsc || '').trim().toUpperCase(),
            holderName: String(holder?.name || '').trim()
        };
    }).filter((item) => item.linkRefNumber);
};

const getLast4FromMaskedAccount = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const tail = raw.match(/(\d{4})$/);
    return tail ? tail[1] : '';
};

const normalizeAccountLast4 = (value) => String(value || '').replace(/\D/g, '').slice(-4);

const getExpectedAccountLast4Candidates = ({ selectedAccount = null, accounts = [], camsProfile = null, savedBankSuggestion = null } = {}) => {
    const candidates = [
        selectedAccount?.maskedAccountNumber,
        ...(Array.isArray(accounts) ? accounts.map((account) => account?.maskedAccountNumber) : []),
        camsProfile?.accountNumberMasked,
        camsProfile?.accountLast4,
        savedBankSuggestion?.accountNumber
    ]
        .map(normalizeAccountLast4)
        .filter(Boolean);

    return [...new Set(candidates)];
};

// Bank data (an AA pull or an uploaded statement) counts as current for this long; mirrors the
// backend's AA_DATA_VALIDITY_DAYS default. The name is historical: it used to be one day.
const AA_DATA_VALIDITY_MS = 30 * 24 * 60 * 60 * 1000;
const isWithinLast24Hours = (value) => {
    if (!value) return false;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return false;
    return (Date.now() - parsed.getTime()) <= AA_DATA_VALIDITY_MS;
};

const AA_CRIF_LOADER_MESSAGE = 'Processing your eligibility...';
const AA_CRIF_LOADER_MIN_MS = 800;
const AA_OFFER_REFRESH_INTERVAL_MS = 2000;
const PLACEHOLDER_IDENTITY_VALUES = new Set(['', '--', 'user', 'customer']);

const isPlaceholderIdentityValue = (value) => PLACEHOLDER_IDENTITY_VALUES.has(String(value || '').trim().toLowerCase());

const pickFirstRealIdentityValue = (...values) => {
    for (const value of values.flat()) {
        const normalized = String(value || '').trim();
        if (normalized && !isPlaceholderIdentityValue(normalized)) {
            return normalized;
        }
    }
    return '';
};

const getResolvedVerifiedIdentity = ({
    fetchedData = null,
    storedUserSnapshot = null,
    aadhaarDetails = null
} = {}) => {
    const camsProfile = fetchedData?.camsProfile || {};
    return {
        mobile: pickFirstRealIdentityValue(
            camsProfile.mobileNumber,
            fetchedData?.mobile,
            storedUserSnapshot?.mobile_number
        ),
        pan: pickFirstRealIdentityValue(
            camsProfile.panNumber,
            fetchedData?.pan,
            storedUserSnapshot?.pan_number
        ).toUpperCase(),
        name: pickFirstRealIdentityValue(
            camsProfile.fullName,
            aadhaarDetails?.name,
            fetchedData?.name,
            storedUserSnapshot?.full_name
        )
    };
};

const buildMoneyoneAccountFromCamsProfile = (camsProfile) => {
    if (!camsProfile?.accountNumberMasked) return null;
    const accountLast4 = String(camsProfile.accountLast4 || '').trim() || getLast4FromMaskedAccount(camsProfile.accountNumberMasked);
    return {
        id: `cams-profile-${accountLast4 || 'account'}`,
        linkRefNumber: `cams-profile-${accountLast4 || 'account'}`,
        maskedAccountNumber: camsProfile.accountNumberMasked,
        bank: camsProfile.bankName || '',
        fiType: 'DEPOSIT',
        accountType: camsProfile.accountType || '',
        ifscCode: camsProfile.ifscCode || '',
        holderName: camsProfile.fullName || '',
        source: 'CAMS_PROFILE'
    };
};

const waitForMs = (ms) => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
});

const formatElapsedAaTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return '';
    }
    if (seconds < 60) {
        return `${seconds}s`;
    }
    const mins = Math.floor(seconds / 60);
    const rem = seconds % 60;
    return rem ? `${mins}m ${rem}s` : `${mins}m`;
};

const getAaStatusViewModel = ({ aaStatus, hasFreshAaProfile, moneyOneState, trackingElapsedSeconds = 0, verifiedByOffer = false }) => {
    const backendStatus = String(aaStatus?.status || '').trim().toUpperCase();
    const backendPhase = String(aaStatus?.phase || '').trim().toUpperCase();
    const hasLinkedAccountsWithConsent = moneyOneState.accounts.length > 0
        && Boolean(String(moneyOneState.consentId || aaStatus?.consentId || '').trim());
    const hasVerifiedAaData = hasFreshAaProfile
        || Boolean(aaStatus?.hasFreshProfile)
        || Boolean(aaStatus?.hasPushData)
        || Boolean(aaStatus?.hasStatementOnFile)
        || backendPhase === 'VERIFIED'
        || backendStatus === SIMPLE_AA_STATUS.VERIFIED
        || hasLinkedAccountsWithConsent
        || verifiedByOffer;
    const formattedElapsed = formatElapsedAaTime(trackingElapsedSeconds);
    const hasPendingAaState = moneyOneState.connecting
        || moneyOneState.consentWindowOpen
        || moneyOneState.fetchingData
        || moneyOneState.status === 'PENDING'
        || moneyOneState.status === 'WAITING_FOR_CONSENT'
        || Boolean(moneyOneState.consentId)
        || backendStatus === SIMPLE_AA_STATUS.PENDING;

    if (hasVerifiedAaData) {
        const offerOnlyMessage = 'Your offer is already approved. Bank verification is on file.';
        return {
            tone: 'success',
            label: 'Verified',
            helperMessage: (verifiedByOffer && !hasFreshAaProfile && !aaStatus?.hasFreshProfile && !aaStatus?.hasPushData && !aaStatus?.hasStatementOnFile)
                ? offerOnlyMessage
                : (String(aaStatus?.helperMessage || '').trim() || 'Bank data is on file. Bank AA is verified.')
        };
    }

    if (backendPhase === 'CONSENT_ACTIVE') {
        return {
            tone: 'pending',
            label: 'Consent Active',
            helperMessage: String(aaStatus?.helperMessage || '').trim() || (formattedElapsed
                ? `Consent is active. We have been checking for ${formattedElapsed}. AA data usually arrives within 2 minutes.`
                : 'Consent is active. AA data usually arrives within 2 minutes.')
        };
    }

    if (backendPhase === 'DATA_PENDING') {
        return {
            tone: 'pending',
            label: 'Data Pending',
            helperMessage: String(aaStatus?.helperMessage || '').trim() || (trackingElapsedSeconds >= 20
                ? `Consent is approved. We are still waiting for AA bank data after ${formattedElapsed}. This is taking a little longer than usual, but we will keep checking automatically.`
                : 'Consent is approved. Waiting for AA bank data to arrive. This usually takes up to 2 minutes.')
        };
    }

    if (hasPendingAaState) {
        return {
            tone: 'pending',
            label: 'Pending',
            helperMessage: String(aaStatus?.helperMessage || '').trim() || (moneyOneState.connecting
                ? 'We opened the CAMS validation journey in a separate window.'
                : moneyOneState.consentWindowOpen
                    ? 'Complete the CAMS consent in this verification window. We will keep checking the status automatically.'
                    : moneyOneState.status === 'WAITING_FOR_CONSENT'
                        ? 'CAMS consent is still pending. Complete the AA window and we will continue checking automatically.'
                        : moneyOneState.fetchingData
                            ? 'Consent is complete. Fetching bank statement data automatically.'
                            : moneyOneState.consentId && !moneyOneState.accounts.length
                                ? (trackingElapsedSeconds >= 20
                                    ? `Consent is complete. We have been waiting ${formattedElapsed} for AA bank data and will keep tracking it automatically.`
                                    : 'Consent is complete. Waiting for AA bank data and tracking it automatically.')
                                : 'CAMS verification is in progress. We will keep checking automatically.')
        };
    }

    return {
        tone: 'idle',
        label: 'Not Verified',
        helperMessage: 'Start CAMS bank verification to continue.'
    };
};

const isAaVerified = ({ aaStatus, hasFreshAaProfile, moneyOneState }) => {
    const backendStatus = String(aaStatus?.status || '').trim().toUpperCase();
    const backendPhase = String(aaStatus?.phase || '').trim().toUpperCase();
    return hasFreshAaProfile
        || Boolean(aaStatus?.hasFreshProfile)
        || Boolean(aaStatus?.hasPushData)
        || Boolean(aaStatus?.hasStatementOnFile)
        || backendPhase === 'VERIFIED'
        || backendStatus === SIMPLE_AA_STATUS.VERIFIED;
};

const hasConfirmedAaVerificationSignal = (aaStatus = null) => {
    const backendStatus = String(aaStatus?.status || '').trim().toUpperCase();
    const backendPhase = String(aaStatus?.phase || '').trim().toUpperCase();
    return Boolean(
        aaStatus?.hasFreshProfile
        || aaStatus?.hasPushData
        || backendPhase === 'VERIFIED'
        || backendStatus === SIMPLE_AA_STATUS.VERIFIED
    );
};

const hasConfirmedAaConsentSignal = (aaStatus = null) => {
    return Boolean(String(aaStatus?.consentId || '').trim());
};

// A session/consent that ended in one of these states is done - unsuccessfully -
// not "still pending." Mirrors backend/routes/supportRoutes.js's
// AA_TERMINAL_FAILURE_STATUSES so both sides agree on what "stale" means.
const AA_TERMINAL_FAILURE_STATUSES = ['FAILED', 'REJECTED', 'EXPIRED', 'DENIED', 'REVOKED', 'CANCELLED'];

const shouldResetStaleAaSession = ({ aaStatus = null, camsProfile = null, moneyOneState = null }) => {
    const consentStatus = String(
        moneyOneState?.status ||
        aaStatus?.consentStatus ||
        aaStatus?.status ||
        ''
    ).trim().toUpperCase();
    const consentId = String(moneyOneState?.consentId || aaStatus?.consentId || '').trim();
    const hasAccounts = Array.isArray(moneyOneState?.accounts) && moneyOneState.accounts.length > 0;
    const hasFreshProfile = isWithinLast24Hours(camsProfile?.createdAt);

    if (hasFreshProfile || hasAccounts) return false;

    // A terminally-failed session is stale regardless of whether a consentId is
    // still attached to it - that consentId belongs to a dead attempt and must not
    // keep blocking a fresh one from starting (it otherwise looks, forever, like
    // verification is still "in progress").
    if (AA_TERMINAL_FAILURE_STATUSES.includes(consentStatus)) return true;

    return !consentId && consentStatus === 'PENDING';
};

const isAaBankDataPending = ({ aaStatus = null, moneyOneState = null, hasVerifiedAaData = false } = {}) => {
    if (hasVerifiedAaData) return false;

    const backendStatus = String(
        aaStatus?.status ||
        aaStatus?.consentStatus ||
        moneyOneState?.status ||
        ''
    ).trim().toUpperCase();
    const backendPhase = String(aaStatus?.phase || '').trim().toUpperCase();
    // Defense in depth alongside shouldResetStaleAaSession: if we already know the
    // backend's consent/session outcome was a terminal failure, a merely-present
    // moneyOneState.consentId (e.g. hydrated before the reset ran) must not, on its
    // own, count as "still pending" - that would trap the UI in an endless wait
    // loader for a consent that's already dead. Live in-flight flags (connecting /
    // polling / fetchingData) are NOT gated by this - those reflect an operation
    // happening right now in this session, regardless of any stale backend snapshot.
    const isKnownTerminalFailure = AA_TERMINAL_FAILURE_STATUSES.includes(backendStatus)
        || AA_TERMINAL_FAILURE_STATUSES.includes(backendPhase);

    return [
        'PENDING',
        'CONSENT_ACTIVE',
        'DATA_PENDING',
        'WAITING_FOR_CONSENT'
    ].includes(backendStatus)
        || [
            'PENDING',
            'CONSENT_ACTIVE',
            'DATA_PENDING'
        ].includes(backendPhase)
        || Boolean(moneyOneState?.connecting)
        || Boolean(moneyOneState?.polling)
        || Boolean(moneyOneState?.fetchingData)
        || (Boolean(moneyOneState?.consentId) && !isKnownTerminalFailure);
};

const buildDemoLoanJourney = ({ fetchedData, storedUser, principal, tenureDays }) => {
    const processingFee = principal * PROCESSING_FEE_RATE;
    const gstAmount = processingFee * GST_ON_FEE_RATE;
    const totalInterestCharge = principal * DAILY_INTEREST_RATE * tenureDays;
    const finalDisbursement = principal - processingFee - gstAmount;
    const repaymentDate = getDateAfterDays(new Date(), tenureDays);
    const sanctionDate = new Date().toISOString();
    const customerName = fetchedData?.name || storedUser.full_name || 'Demo Disbursal User';
    const customerAddress = fetchedData?.panAddress || 'Demo Address, Bengaluru, Karnataka, 560001';
    const dateOfBirth = fetchedData?.dob || '1995-01-15';
    const email = storedUser.email || 'demo.disbursal@finncube.test';
    const mobile = storedUser.mobile_number || DEMO_DISBURSAL_MOBILE;
    const panNumber = storedUser.pan_number || fetchedData?.pan || '';
    const aadhaarLast4 = fetchedData?.aadhaarLast4 || '1234';
    const bankName = fetchedData?.camsProfile?.bankName || 'HDFC Bank';
    const ifscCode = fetchedData?.camsProfile?.ifscCode || 'HDFC0001234';
    const accountLast4 = fetchedData?.camsProfile?.accountLast4 || '9012';
    const totalRepaymentAmount = principal + totalInterestCharge;
    const loanId = 'DEMO-LOAN-50000';

    return {
        loanId,
        stage: 'DISBURSEMENT_READY',
        customerName,
        customerAddress,
        dateOfBirth,
        email,
        mobile,
        panNumber,
        aadhaarLast4,
        bankName,
        ifscCode,
        accountLast4,
        sanctionedAmount: principal,
        processingFee,
        gstAmount,
        finalDisbursement,
        sanctionDate,
        repaymentDate: repaymentDate.toISOString(),
        tenureDays,
        selectedTenureDays: tenureDays,
        totalInterestCharge,
        totalRepaymentAmount,
        sanctionLetterHtml: buildSanctionLetterPreviewHtml({
            loanId,
            customerName,
            customerAddress,
            dateOfBirth,
            mobile,
            email,
            panNumber,
            aadhaarLast4,
            bankName,
            ifscCode,
            accountLast4,
            sanctionedAmount: principal,
            processingFee,
            gstAmount,
            finalDisbursement,
            totalInterestCharge,
            totalRepaymentAmount,
            repaymentDate: repaymentDate.toISOString(),
            sanctionDate,
            tenureDays
        })
    };
};

const buildDemoAadhaarDetails = ({ fetchedData, storedUserFullName }) => {
    const demoName = String(
        fetchedData?.name
        || storedUserFullName
        || 'Demo Disbursal User'
    ).trim();

    return {
        requestId: DEMO_AADHAAR_VERIFICATION_ID,
        name: demoName || 'Demo Disbursal User',
        maskedUid: 'XXXX XXXX 1234',
        aadhaarLast4: '1234',
        dob: String(fetchedData?.dob || '1995-01-15').trim(),
        gender: 'M',
        address: String(fetchedData?.panAddress || 'Demo Address, Bengaluru, Karnataka, 560001').trim(),
        district: 'Bengaluru',
        city: 'Bengaluru',
        state: 'Karnataka',
        pincode: '560001',
        country: 'India',
        photoUrl: '',
        aadhaarPdfUrl: '',
        aadhaarJpegUrl: '',
        x509SubjectName: '',
        validAadhaarDsc: ''
    };
};

const getFirstSearchParam = (searchParams, keys = []) => {
    for (const key of keys) {
        const value = String(searchParams.get(key) || '').trim();
        if (value) {
            return value;
        }
    }
    return '';
};

const appendCallbackParamsFromString = (target, input = '') => {
    const raw = String(input || '').trim();
    if (!raw) return;

    let normalized = raw;
    if (normalized.startsWith('#')) normalized = normalized.slice(1);
    if (normalized.startsWith('?')) normalized = normalized.slice(1);
    const hashIndex = normalized.indexOf('#');
    if (hashIndex >= 0) {
        normalized = normalized.slice(0, hashIndex);
    }
    const queryIndex = normalized.indexOf('?');
    if (queryIndex >= 0) {
        normalized = normalized.slice(queryIndex + 1);
    }
    normalized = normalized.replace(/^\/+/, '');
    if (!normalized) return;

    const params = new URLSearchParams(normalized);
    for (const [key, value] of params.entries()) {
        target.append(key, value);
    }
};

const buildCallbackSearchParams = ({ search = '', hash = '', currentUrl = '' } = {}) => {
    const merged = new URLSearchParams();
    appendCallbackParamsFromString(merged, search);
    appendCallbackParamsFromString(merged, hash);

    if (currentUrl) {
        try {
            const url = new URL(currentUrl, window.location.origin);
            appendCallbackParamsFromString(merged, url.search);
            appendCallbackParamsFromString(merged, url.hash);
        } catch (_error) {
            // Ignore malformed callback URLs and fall back to the direct inputs.
        }
    }

    return merged;
};

const isCompletedMoneyoneConsentStatus = (status) => {
    const normalized = String(status || '').trim().toUpperCase();
    return [
        'CONSENT_APPROVED',
        'APPROVED',
        'ACTIVE',
        'SUCCESS',
        'COMPLETED',
        'DATA_READY',
        'FI_DATA_READY'
    ].includes(normalized);
};

const isRejectedMoneyoneConsentStatus = (status) => {
    const normalized = String(status || '').trim().toUpperCase();
    return [
        'CONSENT_REJECTED',
        'REJECTED',
        'DENIED',
        'CANCELLED',
        'CONSENT_REVOKED',
        'CONSENT_EXPIRED',
        'FAILED',
        'ERROR'
    ].includes(normalized);
};

const buildPersistedLoanApplicationState = ({
    userMobile,
    step,
    salaryCompanyNameHint,
    salaryBracketHint,
    reference1,
    reference2,
    fetchedData,
    verificationData,
    aadhaarFetchState,
    selectedOffer,
    selectedAmount,
    selectedTenure,
    loanJourney,
    bankDetails,
    savedBankSuggestion,
    bankVerificationCompleted,
    moneyOneConfig,
    moneyOneState
}) => ({
    userMobile,
    step,
    salaryCompanyNameHint: salaryCompanyNameHint || '',
    salaryBracketHint: salaryBracketHint || '',
    reference1: reference1 || { name: '', mobile: '', relation: 'Father' },
    reference2: reference2 || { name: '', mobile: '', relation: 'Mother' },
    fetchedData: fetchedData
        ? {
            name: fetchedData.name || '',
            pan: fetchedData.pan || '',
            mobile: fetchedData.mobile || '',
            dob: fetchedData.dob || '',
            cibil: fetchedData.cibil ?? '--',
            panAddress: fetchedData.panAddress || '',
            aadhaarLast4: fetchedData.aadhaarLast4 || '',
            camsProfile: fetchedData.camsProfile || null,
                    latestSanctionLetter: fetchedData.latestSanctionLetter
                ? {
                    loanId: fetchedData.latestSanctionLetter.loanId || '',
                    customerName: fetchedData.latestSanctionLetter.customerName || '',
                    sanctionedAmount: fetchedData.latestSanctionLetter.sanctionedAmount ?? null,
                    finalDisbursement: fetchedData.latestSanctionLetter.finalDisbursement ?? null,
                    repaymentDate: fetchedData.latestSanctionLetter.repaymentDate || '',
                    sanctionDate: fetchedData.latestSanctionLetter.sanctionDate || fetchedData.latestSanctionLetter.sentAt || '',
                    html: fetchedData.latestSanctionLetter.html || '',
                    sentAt: fetchedData.latestSanctionLetter.sentAt || '',
                    viewUrl: fetchedData.latestSanctionLetter.viewUrl || '',
                    downloadUrl: fetchedData.latestSanctionLetter.downloadUrl || ''
                }
                : null,
            aaStatus: fetchedData.aaStatus
                ? {
                    status: fetchedData.aaStatus.status || '',
                    phase: fetchedData.aaStatus.phase || '',
                    consentStatus: fetchedData.aaStatus.consentStatus || '',
                    consentId: fetchedData.aaStatus.consentId || '',
                    consentHandle: fetchedData.aaStatus.consentHandle || '',
                    hasFreshProfile: Boolean(fetchedData.aaStatus.hasFreshProfile),
                    hasPushData: Boolean(fetchedData.aaStatus.hasPushData)
                }
                : null
        }
        : null,
    verificationData: verificationData || null,
    aadhaarFetchState: aadhaarFetchState
        ? {
            loading: Boolean(aadhaarFetchState.loading),
            fetched: Boolean(aadhaarFetchState.fetched),
            details: aadhaarFetchState.details || null
        }
        : null,
    selectedOffer: selectedOffer || null,
    selectedAmount,
    selectedTenure,
    loanJourney: loanJourney
        ? {
            loanId: loanJourney.loanId || '',
            termsRevision: loanJourney.termsRevision || 0,
            stage: loanJourney.stage || '',
            customerName: loanJourney.customerName || '',
            customerAddress: loanJourney.customerAddress || '',
            dateOfBirth: loanJourney.dateOfBirth || '',
            email: loanJourney.email || '',
            mobile: loanJourney.mobile || '',
            panNumber: loanJourney.panNumber || '',
            aadhaarRequestId: loanJourney.aadhaarRequestId || '',
            aadhaarLast4: loanJourney.aadhaarLast4 || '',
            bankName: loanJourney.bankName || '',
            ifscCode: loanJourney.ifscCode || '',
            accountLast4: loanJourney.accountLast4 || '',
            sanctionedAmount: loanJourney.sanctionedAmount ?? null,
            processingFee: loanJourney.processingFee ?? null,
            gstAmount: loanJourney.gstAmount ?? null,
            finalDisbursement: loanJourney.finalDisbursement ?? null,
            sanctionDate: loanJourney.sanctionDate || '',
            repaymentDate: loanJourney.repaymentDate || '',
            tenureDays: loanJourney.tenureDays ?? null,
            totalInterestCharge: loanJourney.totalInterestCharge ?? null,
            totalRepaymentAmount: loanJourney.totalRepaymentAmount ?? null
        }
        : null,
    bankDetails: bankDetails || null,
    savedBankSuggestion: savedBankSuggestion || null,
    bankVerificationCompleted: Boolean(bankVerificationCompleted),
    moneyOneConfig: moneyOneConfig || null,
    moneyOneState: moneyOneState
        ? {
            consentHandle: moneyOneState.consentHandle || '',
            consentId: moneyOneState.consentId || '',
            redirectUrl: moneyOneState.redirectUrl || '',
            status: moneyOneState.status || '',
            accounts: Array.isArray(moneyOneState.accounts) ? moneyOneState.accounts : [],
            selectedLinkRefNumber: moneyOneState.selectedLinkRefNumber || '',
            selectedAccount: moneyOneState.selectedAccount || null,
            session: moneyOneState.session
                ? {
                    consent_id: moneyOneState.session.consent_id || '',
                    session_id: moneyOneState.session.session_id || '',
                    status: moneyOneState.session.status || '',
                    last_event_status: moneyOneState.session.last_event_status || ''
                }
                : null,
            consentStartedAt: moneyOneState.consentStartedAt || '',
            lastStatusCheckAt: moneyOneState.lastStatusCheckAt || '',
            nextStatusCheckAt: moneyOneState.nextStatusCheckAt || '',
            consentWindowOpen: Boolean(moneyOneState.consentWindowOpen)
        }
        : null
});

const persistLoanApplicationState = (state) => {
    return fetch(`${getApiBaseUrl()}/api/loan-progress/save`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
            mobile: state?.userMobile || '',
            panNumber: state?.fetchedData?.pan || state?.loanJourney?.panNumber || '',
            currentStep: state?.step || 1,
            journeyState: state
        })
    }).then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
            throw new Error(data.message || data.error || 'Unable to save journey progress.')
        }
        return data.progress || null;
    });
};

const fetchPersistedLoanApplicationState = async (mobile) => {
    if (!mobile) return null;

    const response = await fetch(`${getApiBaseUrl()}/api/loan-progress?mobile=${encodeURIComponent(mobile)}`, {
        headers: buildAuthHeaders()
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Unable to load journey progress.')
    }
    return data.progress?.journeyState || null;
};

const sanitizeHydratedLoanJourney = (loanJourney, { resumeDisbursement = false } = {}) => {
    if (!loanJourney || typeof loanJourney !== 'object') return null;
    return {
        ...loanJourney
    };
};

const resolveLoanJourneyBankDetails = (loanJourney, fetchedData, manualBankDetails = null) => {
    const journeySnapshot = loanJourney
        || fetchedData?.latestLoanJourney
        || fetchedData?.latest_loan_journey
        || fetchedData?.latestSanctionLetter
        || null;
    const loanJourneyBankName = String(journeySnapshot?.bankName || journeySnapshot?.bank_name || '').trim();
    const loanJourneyIfsc = String(journeySnapshot?.ifscCode || journeySnapshot?.ifsc_code || '').trim().toUpperCase();
    const loanJourneyAccountLast4 = String(journeySnapshot?.accountLast4 || journeySnapshot?.account_last4 || '').replace(/\D/g, '').slice(-4);
    const loanJourneyAccountNumber = String(journeySnapshot?.accountNumber || journeySnapshot?.account_number || '').replace(/\D/g, '').trim();
    const savedBankSuggestion = fetchedData?.savedBankSuggestion || fetchedData?.saved_bank_suggestion || null;
    const camsProfile = fetchedData?.camsProfile || fetchedData?.cams_profile || null;

    const suggestedBankName = String(
        savedBankSuggestion?.bankName
        || savedBankSuggestion?.bank_name
        || camsProfile?.bankName
        || ''
    ).trim();
    const suggestedIfsc = String(
        savedBankSuggestion?.ifscCode
        || savedBankSuggestion?.ifsc_code
        || savedBankSuggestion?.ifsc
        || camsProfile?.ifscCode
        || ''
    ).trim().toUpperCase();
    const suggestedAccountNumber = String(
        savedBankSuggestion?.accountNumber
        || savedBankSuggestion?.account_number
        || camsProfile?.accountNumber
        || ''
    ).replace(/\D/g, '').trim();
    const suggestedAccountLast4 = String(
        savedBankSuggestion?.accountLast4
        || savedBankSuggestion?.account_last4
        || camsProfile?.accountLast4
        || ''
    ).replace(/\D/g, '').slice(-4);
    const manualBankName = String(
        manualBankDetails?.bankName
        || manualBankDetails?.bank_name
        || ''
    ).trim();
    const manualIfsc = String(
        manualBankDetails?.ifscCode
        || manualBankDetails?.ifsc_code
        || ''
    ).trim().toUpperCase();
    const manualAccountNumber = String(
        manualBankDetails?.accountNumber
        || manualBankDetails?.account_number
        || ''
    ).replace(/\D/g, '').trim();
    const manualAccountLast4 = String(
        manualBankDetails?.accountLast4
        || manualBankDetails?.account_last4
        || manualAccountNumber
    ).replace(/\D/g, '').slice(-4);
    const derivedBankNameFromIfsc = resolveBankNameFromIfsc(
        loanJourneyIfsc || manualIfsc || suggestedIfsc || camsProfile?.ifscCode || savedBankSuggestion?.ifscCode || ''
    );

    return {
        bankName: loanJourneyBankName || manualBankName || suggestedBankName || derivedBankNameFromIfsc || '',
        ifscCode: loanJourneyIfsc || manualIfsc || suggestedIfsc || '',
        accountNumber: loanJourneyAccountNumber || manualAccountNumber || suggestedAccountNumber || '',
        accountLast4: loanJourneyAccountLast4 || manualAccountLast4 || suggestedAccountLast4 || ''
    };
};

const hasRenderableBankJourneyDetails = (loanJourney, fetchedData, savedBankSuggestion = null, manualBankDetails = null) => {
    const details = resolveLoanJourneyBankDetails(loanJourney, {
        ...fetchedData,
        savedBankSuggestion: savedBankSuggestion || fetchedData?.savedBankSuggestion || fetchedData?.saved_bank_suggestion || null
    }, manualBankDetails);
    return Boolean(
        details.ifscCode
        && details.ifscCode !== '--'
        && details.ifscCode.length === 11
        && /^\d{2,4}$/.test(details.accountLast4)
    );
};

const clearLoanApplicationProgress = async (identity) => {
    const payload = {};
    const mobile = typeof identity === 'object' && identity !== null
        ? String(identity.mobile || identity.mobile_number || '').trim()
        : String(identity || '').trim();
    const userId = typeof identity === 'object' && identity !== null
        ? Number(identity.userId || identity.id)
        : NaN;
    const panNumber = typeof identity === 'object' && identity !== null
        ? String(identity.panNumber || identity.pan || identity.pan_number || '').trim()
        : '';
    const loanId = typeof identity === 'object' && identity !== null
        ? String(identity.loanId || identity.loan_id || '').trim()
        : '';

    if (mobile) payload.mobile = mobile;
    if (Number.isFinite(userId)) payload.userId = userId;
    if (panNumber) payload.panNumber = panNumber;
    if (loanId) payload.loanId = loanId;

    if (!Object.keys(payload).length) return false;

    const response = await fetch(`${getApiBaseUrl()}/api/loan-progress/clear`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Unable to clear journey progress.')
    }
    return true;
};

const clearPersistedLoanApplicationState = () => {
    localStorage.removeItem(LOAN_PROGRESS_STORAGE_KEY);
    sessionStorage.removeItem(DIGILOCKER_SESSION_STORAGE_KEY);

    Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(CAMS_CALLBACK_STORAGE_PREFIX)) {
            sessionStorage.removeItem(key);
        }
    });
};

const getDigilockerPollLockKey = (requestId) => `${DIGILOCKER_POLL_LOCK_PREFIX}${String(requestId || '').trim()}`;

const readDigilockerPollLock = (requestId) => {
    const key = getDigilockerPollLockKey(requestId);
    if (!key) return 0;
    try {
        return Number(localStorage.getItem(key) || 0);
    } catch (_error) {
        return 0;
    }
};

const writeDigilockerPollLock = (requestId) => {
    const key = getDigilockerPollLockKey(requestId);
    if (!key) return;
    try {
        localStorage.setItem(key, String(Date.now()));
    } catch (_error) {
    }
};

const clearDigilockerPollLock = (requestId) => {
    const key = getDigilockerPollLockKey(requestId);
    if (!key) return;
    try {
        localStorage.removeItem(key);
    } catch (_error) {
    }
};

const VisibilityIcon = ({ visible }) => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
        <path d="M2 12C4.4 7.8 8 5.7 12 5.7S19.6 7.8 22 12c-2.4 4.2-6 6.3-10 6.3S4.4 16.2 2 12Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
        {visible ? null : <path d="M4 4l16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
    </svg>
);

function LoanApplication() {
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    const isHindi = language === 'hi';
    const entryLoaderMessage = useRouteEntryLoader('Opening your loan application...');
    const requestedAmountFromState = Number(location.state?.requestedAmount || 0);
    const salaryCompanyNameHint = String(location.state?.salaryCompanyName || '').trim();
    const salaryBracketHint = String(location.state?.salaryBracket || '').trim();
    const resumeLoanJourneyId = String(
        location.state?.resumeLoanJourney
        || location.state?.resumeLoanJourneyId
        || location.state?.loanId
        || ''
    ).trim();
    const isDemoDisbursalFlow = Boolean(location.state?.demoDisbursal);
    const storedUserSnapshot = JSON.parse(localStorage.getItem('user') || '{}');
    const storedUserSnapshotEmail = String(storedUserSnapshot.email || '').trim();
    const storedUserSnapshotFullName = String(storedUserSnapshot.full_name || '').trim();
    const storedUserSnapshotMobile = String(storedUserSnapshot.mobile_number || '').trim();
    const storedUserSnapshotPan = String(storedUserSnapshot.pan_number || '').trim();
    const isDemoDisbursalUser = Boolean(DEMO_DISBURSAL_MOBILE) && String(storedUserSnapshot.mobile_number || '').trim() === DEMO_DISBURSAL_MOBILE;
    const isDemoAadhaarBypassEnabled = isDemoDisbursalFlow && isDemoDisbursalUser;

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [livePhoto, setLivePhoto] = useState(null);
    const [selfieRecaptureBanner, setSelfieRecaptureBanner] = useState('');
    const [loanProgressSaveEnabled, setLoanProgressSaveEnabled] = useState(DEFAULT_LOAN_PROGRESS_SAVE_ENABLED);
    const [runtimeFeatureConfigReady, setRuntimeFeatureConfigReady] = useState(false);
    const [fetchedData, setFetchedData] = useState(null);
    const [verificationData, setVerificationData] = useState(DEFAULT_VERIFICATION_DATA);
    const [bankConsent, setBankConsent] = useState(false);
    const [aadhaarConsent, setAadhaarConsent] = useState(false);
    const [sanctionConsent, setSanctionConsent] = useState(false);
    const [aadhaarFetchState, setAadhaarFetchState] = useState(DEFAULT_AADHAAR_FETCH_STATE);
    const [digilockerState, setDigilockerState] = useState({
        creating: false,
        url: '',
        requestId: '',
        polling: false
    });
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [selectedAmount, setSelectedAmount] = useState(requestedAmountFromState > 0 ? requestedAmountFromState : 0);
    const [selectedTenure, setSelectedTenure] = useState(clampLoanTenureDays(MAX_LOAN_TENURE_DAYS));
    const [loanJourney, setLoanJourney] = useState(null);
    const [reference1, setReference1] = useState(() => {
        const fromLoc = location.state?.reference1;
        if (fromLoc && (fromLoc.name || fromLoc.mobile)) return fromLoc;
        return { name: '', mobile: '', relation: 'Father' };
    });
    const [reference2, setReference2] = useState(() => {
        const fromLoc = location.state?.reference2;
        if (fromLoc && (fromLoc.name || fromLoc.mobile)) return fromLoc;
        return { name: '', mobile: '', relation: 'Mother' };
    });
    const [pendingDisbursalVoucher, setPendingDisbursalVoucher] = useState(null);
    const [bankDetails, setBankDetails] = useState(DEFAULT_BANK_DETAILS);
    const [bankDetailsError, setBankDetailsError] = useState('');
    const [accountNumberError, setAccountNumberError] = useState('');
    const [ifscCodeError, setIfscCodeError] = useState('');
    const [savedBankSuggestion, setSavedBankSuggestion] = useState(null);
    // Silently pre-fill account number/IFSC from a prior successful bank verification as soon as
    // it's available. Previously this data (savedBankSuggestion, sourced from /api/user-data ->
    // bank_verification_logs) only reached the form via the "Use Saved Details" button or when
    // resuming a saved-progress session — a repeat customer starting a brand-new session saw a
    // blank form even though the backend already had their verified account on file. Only fills
    // empty fields, so it never overwrites something the customer already typed.
    useEffect(() => {
        if (!savedBankSuggestion) return;
        setBankDetails((prev) => {
            if (prev.accountNumber || prev.ifscCode) return prev;
            return {
                ...prev,
                accountNumber: String(savedBankSuggestion.accountNumber || '').replace(/\D/g, ''),
                ifscCode: String(savedBankSuggestion.ifscCode || '').trim().toUpperCase()
            };
        });
    }, [savedBankSuggestion]);
    const [bankVerificationCompleted, setBankVerificationCompleted] = useState(false);
    const [bankReviewPending, setBankReviewPending] = useState({ isPending: false, message: '', accountHolderName: '', panHolderName: '' });
    const [identityReviewPending, setIdentityReviewPending] = useState({ isPending: false, aadhaarName: '', expectedName: '' });
    const [moneyOneConfig] = useState(DEFAULT_MONEYONE_CONFIG);
    const [moneyOneState, setMoneyOneState] = useState(DEFAULT_MONEYONE_STATE);
    const [isProgressHydrated, setIsProgressHydrated] = useState(false);
    const forceFreshStartRef = useRef(consumeLoanJourneyForceFreshStart());
    // "Link another bank account": start a fresh AA consent even though today's profile exists, and
    // keep the old (declined) decision from ending the session until the new statement has arrived.
    const restartAaRequested = Boolean(location.state?.restartAa);
    const aaRestartRef = useRef({ active: restartAaRequested, startedAt: restartAaRequested ? Date.now() : 0, stepApplied: false });
    const [showSalarySlipModal, setShowSalarySlipModal] = useState(false);
    const autoSaveTimeoutRef = useRef(null);
    const lastAutoSavedSnapshotRef = useRef('');
    const currentOfferAmountSource = fetchedData?.currentOffer?.offeredAmount ?? fetchedData?.currentOffer?.amount ?? 0;
    const approvedOfferAmount = Number(currentOfferAmountSource || 0);
    const isCurrentOfferApproved = fetchedData?.currentOffer?.isEligible ?? fetchedData?.currentOffer?.approved;
    const hasBackendApprovedOffer = Boolean(
        (typeof isCurrentOfferApproved === 'boolean' ? isCurrentOfferApproved : approvedOfferAmount > 0)
        && approvedOfferAmount > 0
    );
    const approvedLimitAmount = hasBackendApprovedOffer ? approvedOfferAmount : 0;
    const requestedAmount = approvedLimitAmount > 0
        ? (requestedAmountFromState > 0
            ? Math.min(requestedAmountFromState, approvedLimitAmount)
            : approvedLimitAmount)
        : DEFAULT_LOAN_PRINCIPAL_AMOUNT;
    const currentOfferPricing = fetchedData?.currentOffer?.pricing || null;
    const interestPerDayRate = normalizeRate(
        currentOfferPricing?.interestPerDay ?? fetchedData?.currentOffer?.interestRatePerDay ?? fetchedData?.currentOffer?.interestPerDay,
        DAILY_INTEREST_RATE,
        { treatZeroAsMissing: true }
    );
    const rawPfPercent = currentOfferPricing?.processingFeePercent
        ?? fetchedData?.currentOffer?.processingFeePercent
        ?? fetchedData?.currentOffer?.processingFee
        ?? fetchedData?.currentOffer?.processing_fee
        ?? null;
    const processingFeeRate = normalizeRate(
        rawPfPercent != null ? Number(rawPfPercent) / 100 : null,
        PROCESSING_FEE_RATE,
        { treatZeroAsMissing: true }
    );
    const gstApplicableOnProcessingFee = currentOfferPricing?.gstApplicable !== false;
    const processingFeePercentLabel = `${Number((processingFeeRate * 100).toFixed(2))}%`;
    const interestPerDayLabel = `${interestPerDayRate >= 1 ? interestPerDayRate : interestPerDayRate * 100}%`;
    const derivedDisbursementBankName = resolveBankNameFromIfsc(
        loanJourney?.ifscCode
        || loanJourney?.ifsc_code
        || bankDetails.ifscCode
        || fetchedData?.camsProfile?.ifscCode
        || fetchedData?.savedBankSuggestion?.ifscCode
        || ''
    );
    const resolvedDisbursementBankName = String(
        loanJourney?.bankName ||
        moneyOneState.selectedAccount?.bank ||
        fetchedData?.camsProfile?.bankName ||
        selectedOffer?.bank ||
        derivedDisbursementBankName ||
        ''
    ).trim();

    const calculationAnchorDate = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const loanDate = new Date(calculationAnchorDate);
    loanDate.setHours(0, 0, 0, 0);
    const rawCrmTenure = fetchedData?.currentOffer?.tenureDays
        ?? (fetchedData?.currentOffer?.crmTenureOverride ? fetchedData?.currentOffer?.maxTenureDays : null)
        ?? (fetchedData?.currentOffer?.brePassApplied && fetchedData?.currentOffer?.maxTenureDays && Number(fetchedData.currentOffer.maxTenureDays) !== MAX_LOAN_TENURE_DAYS ? fetchedData.currentOffer.maxTenureDays : null)
        ?? selectedOffer?.tenureDays
        ?? null;
    const parsedCrmTenure = Number(rawCrmTenure);
    const crmTenureDays = Number.isFinite(parsedCrmTenure) && parsedCrmTenure > 0
        ? Math.round(parsedCrmTenure)
        : null;
    const hasCustomCrmTenure = crmTenureDays !== null && crmTenureDays > 0;

    const detectedAaSalaryCreditDay = (() => {
        const offerSalaryDay = Number(fetchedData?.currentOffer?.salaryCreditDayOfMonth);
        if (Number.isFinite(offerSalaryDay) && offerSalaryDay >= 1 && offerSalaryDay <= 31) {
            return Math.round(offerSalaryDay);
        }
        const camsSalaryDay = Number(fetchedData?.camsProfile?.salaryCreditDayOfMonth);
        if (Number.isFinite(camsSalaryDay) && camsSalaryDay >= 1 && camsSalaryDay <= 31) {
            return Math.round(camsSalaryDay);
        }
        const camsLatestDate = fetchedData?.camsProfile?.latestSalaryDate;
        if (camsLatestDate) {
            const d = new Date(camsLatestDate);
            if (!isNaN(d.getTime())) {
                const day = d.getDate();
                if (day >= 1 && day <= 31) return day;
            }
        }
        const hasAaSalaryAmount = Number(fetchedData?.currentOffer?.salaryAmount || fetchedData?.camsProfile?.salaryAmount || 0) > 0;
        if (hasAaSalaryAmount) {
            return SALARY_CYCLE_DUE_DAY;
        }
        return null;
    })();

    let maxAllowedTenureDays;
    if (hasCustomCrmTenure) {
        maxAllowedTenureDays = Math.max(MIN_LOAN_TENURE_DAYS, crmTenureDays);
    } else if (detectedAaSalaryCreditDay !== null) {
        const salaryDueDate = getNextSalaryCycleDueDate(calculationAnchorDate, detectedAaSalaryCreditDay);
        const daysToSalary = Math.max(MIN_LOAN_TENURE_DAYS, getDaysUntilDate(calculationAnchorDate, salaryDueDate));
        maxAllowedTenureDays = daysToSalary;
    } else {
        maxAllowedTenureDays = MAX_LOAN_TENURE_DAYS;
    }

    const maxRepaymentTenureDays = resolveNonSundayTenure(maxAllowedTenureDays, {
        baseDate: calculationAnchorDate,
        minDays: MIN_LOAN_TENURE_DAYS,
        maxDays: maxAllowedTenureDays,
        preferredDirection: -1
    });
    const maxRepaymentDate = getDateAfterDays(calculationAnchorDate, maxRepaymentTenureDays);
    const selectedTenureDays = resolveNonSundayTenure(selectedTenure, {
        baseDate: calculationAnchorDate,
        minDays: MIN_LOAN_TENURE_DAYS,
        maxDays: maxRepaymentTenureDays,
        preferredDirection: 0
    });
    const selectedRepaymentDate = getDateAfterDays(calculationAnchorDate, selectedTenureDays);
    const loanDateLabel = formatDisplayDateWithDay(loanDate, isHindi);
    const maxRepaymentDateLabel = formatDisplayDateWithDay(maxRepaymentDate, isHindi);
    const selectedRepaymentDateLabel = formatDisplayDateWithDay(selectedRepaymentDate, isHindi);
    const resolvedDisbursalProductCode = String(
        fetchedData?.currentOffer?.productCode ||
        fetchedData?.currentOffer?.product ||
        selectedOffer?.productCode ||
        selectedOffer?.product ||
        loanJourney?.productCode ||
        loanJourney?.productId ||
        ''
    ).trim();

    useEffect(() => {
        let cancelled = false;

        const loadRuntimeFeatureConfig = async () => {
            try {
                const response = await fetch(`${getApiBaseUrl()}/api/runtime-feature-config`);
                const data = await response.json().catch(() => ({}));
                if (!cancelled && response.ok && data?.success) {
                    setLoanProgressSaveEnabled(Boolean(data?.config?.loanProgressSaveEnabled));
                }
            } catch (error) {
                console.error('Runtime feature config load failed:', error);
            } finally {
                if (!cancelled) {
                    setRuntimeFeatureConfigReady(true);
                }
            }
        };

        loadRuntimeFeatureConfig();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const resolvedLoanPrincipalAmount = resolveLoanJourneyPrincipalAmount(loanJourney, {
            requestedAmount,
            approvedLimitAmount
        });
        const storedJourneyPrincipalAmount = toPositiveNumber(loanJourney?.sanctionedAmount ?? loanJourney?.sanctioned_amount) || 0;

        setSelectedAmount((prev) => {
            const current = Number(prev || 0);

            if (!hasBackendApprovedOffer) {
                if (storedJourneyPrincipalAmount > 0) return storedJourneyPrincipalAmount;
                if (loanJourney?.loanId) return current > 0 ? current : (requestedAmount > 0 ? requestedAmount : DEFAULT_LOAN_PRINCIPAL_AMOUNT);
                return requestedAmount > 0 ? requestedAmount : DEFAULT_LOAN_PRINCIPAL_AMOUNT;
            }

            if (!(current > 0)) {
                return requestedAmount > 0 ? requestedAmount : resolvedLoanPrincipalAmount;
            }
            return Math.min(current, approvedLimitAmount);
        });
    }, [hasBackendApprovedOffer, approvedLimitAmount, requestedAmount, loanJourney, loanJourney?.loanId, loanJourney?.sanctionedAmount, loanJourney?.sanctioned_amount]);

    useEffect(() => {
        if (!hasBackendApprovedOffer || !approvedLimitAmount) {
            return;
        }

        setSelectedOffer((prev) => {
            if (!prev) return prev;
            const previousAmount = Number(String(prev.amount || '').replace(/[^\d.-]/g, '')) || 0;
            if (!(previousAmount > approvedLimitAmount)) {
                return prev;
            }

            return {
                ...prev,
                amount: formatINR(approvedLimitAmount)
            };
        });
    }, [hasBackendApprovedOffer, approvedLimitAmount]);

    useEffect(() => {
        if (!isDemoDisbursalFlow || !isDemoDisbursalUser || !fetchedData) {
            return;
        }

        const principal = Number(
            requestedAmountFromState
            || fetchedData?.currentOffer?.offeredAmount
            || fetchedData?.currentOffer?.amount
            || 50000
        );
        const tenureDays = clampLoanTenureDays(
            fetchedData?.latestLoanJourney?.selectedTenureDays
            || fetchedData?.latestLoanJourney?.selected_tenure_days
            || fetchedData?.latestLoanJourney?.tenureDays
            || fetchedData?.latestLoanJourney?.tenure_days
            || selectedTenure
            || MAX_LOAN_TENURE_DAYS
        );

        setSelectedAmount(principal);
        setSelectedTenure(tenureDays);
        setSelectedOffer({
            bank: fetchedData?.camsProfile?.bankName || 'HDFC Bank',
            amount: formatINR(principal),
            tenure: `${tenureDays} Days`,
            interest: `${DAILY_INTEREST_RATE * 100}% per day`,
            interestPerDay: DAILY_INTEREST_RATE,
            processingFeePercent: PROCESSING_FEE_RATE * 100,
            gstApplicable: true
        });
        setBankDetails({
            accountNumber: '',
            ifscCode: ''
        });
        setVerificationData((prev) => ({
            ...prev,
            officialEmail: prev.officialEmail || storedUserSnapshotEmail || 'demo.disbursal@finncube.test',
            selfieTempUrl: prev.selfieTempUrl || 'demo-selfie.png',
            faceMatchDone: true,
            faceMatchSimilarity: prev.faceMatchSimilarity ?? 99
        }));
        setLoanJourney(buildDemoLoanJourney({
            fetchedData,
            storedUser: {
                email: storedUserSnapshotEmail,
                full_name: storedUserSnapshotFullName,
                mobile_number: storedUserSnapshotMobile,
                pan_number: storedUserSnapshotPan
            },
            principal,
            tenureDays
        }));
    }, [isDemoDisbursalFlow, isDemoDisbursalUser, fetchedData, requestedAmountFromState, selectedTenure, step, storedUserSnapshotEmail, storedUserSnapshotFullName, storedUserSnapshotMobile, storedUserSnapshotPan]);

    const applyDemoAadhaarBypass = useEffectEvent(() => {
        if (!isDemoAadhaarBypassEnabled || !fetchedData) {
            return;
        }

        const demoAadhaarDetails = buildDemoAadhaarDetails({
            fetchedData,
            storedUserFullName: storedUserSnapshotFullName
        });
        const demoEmail = String(storedUserSnapshotEmail || 'demo.disbursal@finncube.test').trim().toLowerCase();

        setVerificationData((prev) => {
            const nextRequestId = prev.aadhaarRequestId || DEMO_AADHAAR_VERIFICATION_ID;
            const nextEmail = prev.officialEmail || demoEmail;

            if (prev.aadhaarRequestId === nextRequestId && prev.officialEmail === nextEmail) {
                return prev;
            }

            return {
                ...prev,
                aadhaarRequestId: nextRequestId,
                officialEmail: nextEmail
            };
        });

        setAadhaarFetchState((prev) => {
            if (prev.fetched && prev.details?.name === demoAadhaarDetails.name) {
                return prev;
            }

            return {
                loading: false,
                fetched: true,
                details: demoAadhaarDetails
            };
        });

        setFetchedData((prev) => ({
            ...prev,
            name: demoAadhaarDetails.name || prev?.name || '',
            dob: demoAadhaarDetails.dob || prev?.dob || '',
            panAddress: demoAadhaarDetails.address || prev?.panAddress || '',
            aadhaarLast4: demoAadhaarDetails.aadhaarLast4 || prev?.aadhaarLast4 || '',
            aadhaarVerification: demoAadhaarDetails
        }));

        setAadhaarConsent(true);
        setStep((prevStep) => (prevStep === 5 ? prevStep : 5));
    });

    useEffect(() => {
        if (!isDemoAadhaarBypassEnabled || !fetchedData) {
            return;
        }

        applyDemoAadhaarBypass();
    }, [applyDemoAadhaarBypass, fetchedData, isDemoAadhaarBypassEnabled]);

    useEffect(() => {
        setSelectedTenure((prev) => {
            const current = Number(prev) || maxRepaymentTenureDays;
            const bounded = Math.min(maxRepaymentTenureDays, Math.max(MIN_LOAN_TENURE_DAYS, current));
            const nextValue = resolveNonSundayTenure(bounded, {
                baseDate: calculationAnchorDate,
                minDays: MIN_LOAN_TENURE_DAYS,
                maxDays: maxRepaymentTenureDays,
                preferredDirection: -1
            });
            return nextValue === prev ? prev : nextValue;
        });
    }, [maxRepaymentTenureDays, calculationAnchorDate]);

    useEffect(() => {
        if (!runtimeFeatureConfigReady) {
            return;
        }

        if (!loanProgressSaveEnabled) {
            setIsProgressHydrated(true);
            return;
        }

        let cancelled = false;

        const hydrateJourneyProgress = async () => {
            try {
                localStorage.removeItem(LOAN_PROGRESS_STORAGE_KEY);
            } catch (_error) {
            }

            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const mobile = String(storedUser.mobile_number || '').trim();
            if (!mobile) {
                if (!cancelled) {
                    setIsProgressHydrated(true);
                }
                return;
            }

            try {
                const savedState = await fetchPersistedLoanApplicationState(mobile);
                if (cancelled || !savedState || savedState.userMobile !== mobile) {
                    return;
                }

                const savedStep = Number(savedState.step || 1);
                const savedBankVerified = Boolean(savedState.bankVerificationCompleted);
                const resumeDisbursement = savedStep >= 8;
                const restoredStep = isDemoAadhaarBypassEnabled
                    ? Math.max(5, resumeDisbursement ? 1 : (savedStep || 1))
                    : (resumeDisbursement ? 1 : savedStep || 1);
                const bankVerificationGatedStep = !savedBankVerified && restoredStep > 2 ? 2 : restoredStep;
                // Borrower already chose amount/tenure (step 3) and moved on, but never finished the
                // journey -- send them back to step 3 instead of wherever they stopped, so they can
                // confirm or change the amount/tenure before continuing.
                const hydratedStep = savedState.loanJourney?.termsRevision ? Math.min(bankVerificationGatedStep, 6) : !resumeDisbursement && bankVerificationGatedStep > 3
                    ? 3
                    : bankVerificationGatedStep
                setStep(hydratedStep);
                const savedAaStatus = savedState.fetchedData?.aaStatus || null;
                const shouldClearHydratedAaArtifacts = !hasConfirmedAaVerificationSignal(savedAaStatus)
                    && !hasConfirmedAaConsentSignal(savedAaStatus);
                const sanitizedFetchedData = shouldClearHydratedAaArtifacts
                    ? {
                        ...(savedState.fetchedData || {}),
                        aaStatus: {
                            ...(savedState.fetchedData?.aaStatus || {}),
                            status: SIMPLE_AA_STATUS.PENDING,
                            phase: 'PENDING',
                            hasFreshProfile: false,
                            hasPushData: false,
                            consentId: '',
                            consentHandle: ''
                        },
                        camsProfile: null
                    }
                    : (savedState.fetchedData || null);

                setFetchedData(sanitizedFetchedData);
                setVerificationData(savedState.verificationData || DEFAULT_VERIFICATION_DATA);
                setAadhaarFetchState((savedState.aadhaarFetchState?.details && isAadhaarDetailsVerified(savedState.aadhaarFetchState.details))
                    ? {
                        loading: false,
                        fetched: Boolean(savedState.aadhaarFetchState.fetched),
                        details: savedState.aadhaarFetchState.details
                    }
                    : DEFAULT_AADHAAR_FETCH_STATE);
                const savedApprovedLimit = Number(
                    savedState.fetchedData?.currentOffer?.offeredAmount
                    || savedState.fetchedData?.currentOffer?.amount
                    || 0
                );
                const hydratedSelectedAmount = Number(savedState.selectedAmount || requestedAmount || 0);
                const cappedHydratedAmount = savedApprovedLimit > 0
                    ? Math.min(hydratedSelectedAmount || savedApprovedLimit, savedApprovedLimit)
                    : hydratedSelectedAmount;
                const hydratedSelectedOfferAmount = Number(
                    String(savedState.selectedOffer?.amount || '').replace(/[^\d.-]/g, '')
                ) || 0;
                const hydratedSelectedOffer = savedState.selectedOffer
                    ? {
                        ...savedState.selectedOffer,
                        amount: savedApprovedLimit > 0 && hydratedSelectedOfferAmount > savedApprovedLimit
                            ? formatINR(savedApprovedLimit)
                            : savedState.selectedOffer.amount
                    }
                    : null;

                setSelectedOffer(hydratedSelectedOffer);
                setSelectedAmount(cappedHydratedAmount || requestedAmount);
                setSelectedTenure(resolveLoanJourneyTenureDays(
                    savedState.loanJourney || null,
                    savedState.fetchedData || null,
                    savedState.selectedTenure || null
                ));
                {
                    const hydratedLoanJourney = sanitizeHydratedLoanJourney(savedState.loanJourney || null, { resumeDisbursement });
                    const restoredBankDetails = resolveLoanJourneyBankDetails(
                        hydratedLoanJourney,
                        {
                            ...(savedState.fetchedData || {}),
                            savedBankSuggestion: savedState.savedBankSuggestion || null
                        }
                    );
                    setLoanJourney(hydratedLoanJourney ? {
                        ...hydratedLoanJourney,
                        bankName: hydratedLoanJourney.bankName || restoredBankDetails.bankName || '',
                        ifscCode: hydratedLoanJourney.ifscCode || restoredBankDetails.ifscCode || '',
                        accountLast4: hydratedLoanJourney.accountLast4 || restoredBankDetails.accountLast4 || '',
                        sanctionDate: hydratedLoanJourney.sanctionDate || savedState.fetchedData?.latestSanctionLetter?.sentAt || savedState.fetchedData?.latestSanctionLetter?.sanctionDate || ''
                    } : hydratedLoanJourney);
                }
                setBankDetails(resumeDisbursement
                    ? DEFAULT_BANK_DETAILS
                    : {
                        ...(savedState.bankDetails || DEFAULT_BANK_DETAILS),
                        accountNumber: String(
                            savedState.bankDetails?.accountNumber
                            || savedState.savedBankSuggestion?.accountNumber
                            || savedState.saved_bank_suggestion?.accountNumber
                            || ''
                        ).replace(/\D/g, ''),
                        ifscCode: String(
                            savedState.bankDetails?.ifscCode
                            || savedState.savedBankSuggestion?.ifscCode
                            || savedState.saved_bank_suggestion?.ifscCode
                            || ''
                        ).trim().toUpperCase()
                    });
    setSavedBankSuggestion(savedState.savedBankSuggestion || savedState.saved_bank_suggestion || null);
                setBankVerificationCompleted(savedBankVerified);
                const hydratedMoneyOneState = {
                    ...DEFAULT_MONEYONE_STATE,
                    ...(savedState.moneyOneState || {}),
                    connecting: false,
                    polling: false,
                    fetchingData: false
                };
                const resetStaleAaSession = aaRestartRef.current.active || shouldResetStaleAaSession({
                    aaStatus: savedAaStatus,
                    camsProfile: sanitizedFetchedData?.camsProfile || null,
                    moneyOneState: hydratedMoneyOneState
                });
                setMoneyOneState({
                    ...hydratedMoneyOneState,
                    consentHandle: resetStaleAaSession ? '' : hydratedMoneyOneState.consentHandle,
                    consentId: resetStaleAaSession ? '' : hydratedMoneyOneState.consentId,
                    redirectUrl: resetStaleAaSession ? '' : hydratedMoneyOneState.redirectUrl,
                    status: resetStaleAaSession ? '' : hydratedMoneyOneState.status,
                    session: resetStaleAaSession ? null : hydratedMoneyOneState.session,
                    accounts: (resetStaleAaSession || shouldClearHydratedAaArtifacts) ? [] : hydratedMoneyOneState.accounts,
                    selectedAccount: (resetStaleAaSession || shouldClearHydratedAaArtifacts) ? null : hydratedMoneyOneState.selectedAccount,
                    selectedLinkRefNumber: (resetStaleAaSession || shouldClearHydratedAaArtifacts) ? '' : hydratedMoneyOneState.selectedLinkRefNumber,
                    consentWindowOpen: false,
                    consentStartedAt: resetStaleAaSession ? '' : hydratedMoneyOneState.consentStartedAt,
                    lastStatusCheckAt: resetStaleAaSession ? '' : hydratedMoneyOneState.lastStatusCheckAt,
                    nextStatusCheckAt: resetStaleAaSession ? '' : hydratedMoneyOneState.nextStatusCheckAt,
                    accountFetchComplete: (resetStaleAaSession || shouldClearHydratedAaArtifacts) ? false : Boolean(hydratedMoneyOneState.accountFetchComplete),
                    accountFetchBlocked: (resetStaleAaSession || shouldClearHydratedAaArtifacts) ? false : Boolean(hydratedMoneyOneState.accountFetchBlocked)
                });
                if (savedState.reference1 && (savedState.reference1.name || savedState.reference1.mobile)) {
                    setReference1(savedState.reference1);
                }
                if (savedState.reference2 && (savedState.reference2.name || savedState.reference2.mobile)) {
                    setReference2(savedState.reference2);
                }
                lastAutoSavedSnapshotRef.current = JSON.stringify(savedState);
            } catch (error) {
                console.error('Progress hydrate failed:', error);
            } finally {
                if (!cancelled) {
                    setIsProgressHydrated(true);
                }
            }
        };

        hydrateJourneyProgress();

        return () => {
            cancelled = true;
        };
    }, [isDemoAadhaarBypassEnabled, requestedAmount, loanProgressSaveEnabled, runtimeFeatureConfigReady]);

    useEffect(() => {
        if (!loanProgressSaveEnabled) return;
        if (!isProgressHydrated) return;

        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (!storedUser.mobile_number) return;

        const stateToSave = buildPersistedLoanApplicationState({
            userMobile: storedUser.mobile_number,
            step,
            salaryCompanyNameHint,
            salaryBracketHint,
            reference1,
            reference2,
            fetchedData,
            verificationData,
            aadhaarFetchState,
            selectedOffer,
            selectedAmount,
            selectedTenure,
            loanJourney,
            bankDetails,
            savedBankSuggestion,
            bankVerificationCompleted,
            moneyOneConfig,
            moneyOneState
        });
        const snapshot = JSON.stringify(stateToSave);

        if (snapshot === lastAutoSavedSnapshotRef.current) {
            return;
        }

        if (autoSaveTimeoutRef.current) {
            window.clearTimeout(autoSaveTimeoutRef.current);
        }

        autoSaveTimeoutRef.current = window.setTimeout(() => {
            try {
                persistLoanApplicationState(stateToSave)
                    .then(() => {
                        lastAutoSavedSnapshotRef.current = snapshot;
                    })
                    .catch((error) => {
                        console.error('Auto-save failed:', error);
                    });
            } catch (error) {
                console.error('Auto-save failed:', error);
            } finally {
                autoSaveTimeoutRef.current = null;
            }
        }, JOURNEY_AUTOSAVE_DEBOUNCE_MS);

        return () => {
            if (autoSaveTimeoutRef.current) {
                window.clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = null;
            }
        };
    }, [loanProgressSaveEnabled, isProgressHydrated, step, fetchedData, verificationData, aadhaarFetchState, selectedOffer, selectedAmount, selectedTenure, loanJourney, bankDetails, savedBankSuggestion, bankVerificationCompleted, moneyOneConfig, moneyOneState, salaryCompanyNameHint, salaryBracketHint, reference1, reference2]);
    const [pennyDropStatus, setPennyDropStatus] = useState({
        started: false,
        completed: false,
        message: ''
    });
    const [showSanctionPreview, setShowSanctionPreview] = useState(false);
    const [isSavingProgress, setIsSavingProgress] = useState(false);

    const getInitialEmailState = () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const cleanMobile = String(storedUser.mobile_number || storedUser.mobile || '').replace(/\D/g, '').slice(-10);
            if (cleanMobile) {
                const savedEmail = getCustomerVerifiedEmail(cleanMobile);
                if (savedEmail) {
                    return { verified: true, email: savedEmail };
                }
            }
            if (storedUser.isEmailVerified && storedUser.email) {
                return { verified: true, email: String(storedUser.email).trim().toLowerCase() };
            }
        } catch (_e) {}
        return { verified: false, email: '' };
    };

    const initialEmailState = getInitialEmailState();
    const [officialEmailDraft, setOfficialEmailDraft] = useState(initialEmailState.email || '');
    const [isEmailEditing, setIsEmailEditing] = useState(false);

    const getEmailValidationMessage = (value) => {
        const email = String(value || '').trim();
        if (!email) return '';
        if (email.length > 254) return 'Maximum email length should be 254 characters.';
        if (/\s/.test(email)) return 'Email must not contain spaces.';
        if (email.startsWith('.') || email.endsWith('.')) return 'Email must not start or end with a dot.';
        if (email.includes('..')) return 'Email must not contain consecutive dots.';

        const atMatches = email.match(/@/g) || [];
        if (atMatches.length !== 1) return 'Email must contain exactly one "@" symbol.';

        const [localPart = '', domainPart = ''] = email.split('@');
        if (!localPart) return 'There must be at least one character before "@".';
        if (!/^[A-Za-z0-9._%+-]+$/.test(localPart)) {
            return 'Only valid characters are allowed before "@".';
        }
        if (!domainPart) return 'There must be a valid domain after "@".';
        if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
            return 'Domain must not start or end with a dot.';
        }
        if (!/^[A-Za-z0-9.-]+$/.test(domainPart)) {
            return 'Domain contains invalid characters.';
        }
        if (!domainPart.includes('.')) return 'Domain must contain at least one "." (dot).';

        const domainParts = domainPart.split('.');
        if (domainParts.some((part) => !part)) return 'There must be a valid domain after "@".';

        const topLevelDomain = domainParts[domainParts.length - 1] || '';
        if (topLevelDomain.length < 2) {
            return 'Top-level domain must be at least 2 characters long.';
        }

        return '';
    };

    const isValidEmail = (value) => !getEmailValidationMessage(value);
    // eslint-disable-next-line no-unused-vars
    const [isCheckingEmailActive, setIsCheckingEmailActive] = useState(false);
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailOtp, setEmailOtp] = useState('');
    const [isEmailVerified, setIsEmailVerified] = useState(initialEmailState.verified);
    const [verifiedEmailAddress, setVerifiedEmailAddress] = useState(initialEmailState.email || '');
    const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
    const [isVerifyingEmailOtp, setIsVerifyingEmailOtp] = useState(false);
    const [emailOtpCountdown, setEmailOtpCountdown] = useState(0);

    useEffect(() => {
        let timer;
        if (emailOtpCountdown > 0) {
            timer = setTimeout(() => setEmailOtpCountdown((prev) => prev - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [emailOtpCountdown]);

    const handleSendEmailOtp = async () => {
        const email = String(officialEmailDraft || verificationData.officialEmail || '').trim().toLowerCase();
        if (!email) {
            showPopup("Please enter an email address.", "error");
            return;
        }
        if (!isValidEmail(email)) {
            showPopup(getEmailValidationMessage(email) || "Please enter a valid email address.", "error");
            return;
        }

        setIsSendingEmailOtp(true);
        try {
            const baseUrl = getApiBaseUrl();
            const res = await fetch(`${baseUrl}/api/send-email-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
                setEmailOtpSent(true);
                setEmailOtpCountdown(30);
                showPopup(`OTP sent to ${email}`, "success");
            } else {
                showPopup(data.message || "Failed to send email OTP. Please retry.", "error");
            }
        } catch (err) {
            showPopup("Network error while sending email OTP.", "error");
        } finally {
            setIsSendingEmailOtp(false);
        }
    };

    const handleVerifyEmailOtp = async () => {
        const email = String(officialEmailDraft || verificationData.officialEmail || '').trim().toLowerCase();
        const otp = String(emailOtp || '').trim();
        if (!email) {
            showPopup("Please enter an email address.", "error");
            return;
        }
        if (!otp || otp.length !== 4) {
            showPopup("Please enter a valid 4-digit OTP.", "error");
            return;
        }

        setIsVerifyingEmailOtp(true);
        try {
            const baseUrl = getApiBaseUrl();
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const cleanMobile = String(storedUser.mobile_number || storedUser.mobile || fetchedData?.mobile || '').replace(/\D/g, '').slice(-10);
            const userDbId = storedUser.id || fetchedData?.id || fetchedData?.userId || fetchedData?.user?.id || null;

            const res = await fetch(`${baseUrl}/api/verify-email-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    otp,
                    mobile: cleanMobile,
                    userId: userDbId
                })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
                setIsEmailVerified(true);
                setVerifiedEmailAddress(email);
                setVerificationData((prev) => ({
                    ...prev,
                    officialEmail: email
                }));
                if (cleanMobile) {
                    saveCustomerVerifiedEmail(cleanMobile, email, loanJourney?.loanId);
                }
                try {
                    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
                    localUser.email = email;
                    localUser.isEmailVerified = true;
                    localStorage.setItem('user', JSON.stringify(localUser));
                } catch (_e) {}
                showPopup("Email verified successfully!", "success");
            } else {
                showPopup(data.message || "Invalid or expired OTP. Please try again.", "error");
            }
        } catch (err) {
            showPopup("Network error while verifying OTP.", "error");
        } finally {
            setIsVerifyingEmailOtp(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const verifyEmailDeliverability = async (emailToVerify) => {
        const email = String(emailToVerify || '').trim().toLowerCase();
        if (!email) return { deliverable: true };
        if (!isValidEmail(email)) {
            return {
                deliverable: false,
                message: getEmailValidationMessage(email) || 'Please enter a valid email format.'
            };
        }
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/verify-email-deliverability`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.success) {
                if (data.deliverable === false) {
                    return {
                        deliverable: false,
                        message: data.message || 'The email address is inactive or undeliverable. Please enter an active email.'
                    };
                }
                return { deliverable: true };
            }
            if (data && data.deliverable === false) {
                return {
                    deliverable: false,
                    message: data.message || 'The email entered appears inactive. Please enter an active email.'
                };
            }
            return { deliverable: true };
        } catch (_err) {
            return { deliverable: true };
        }
    };

    useEffect(() => {
        if (isEmailEditing) return;
        const nextEmail = String(verificationData.officialEmail || '').trim().toLowerCase();
        if (nextEmail) {
            setOfficialEmailDraft(nextEmail);
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const cleanMobile = String(storedUser.mobile_number || storedUser.mobile || fetchedData?.mobile || '').replace(/\D/g, '').slice(-10);
            const savedEmail = getCustomerVerifiedEmail(cleanMobile);
            if (savedEmail && savedEmail.toLowerCase() === nextEmail.toLowerCase()) {
                setIsEmailVerified(true);
                setVerifiedEmailAddress(nextEmail);
            }
        }
    }, [verificationData.officialEmail, isEmailEditing, fetchedData?.mobile]);

    // UI states for sensitive fields
    const [showSensitiveFields, setShowSensitiveFields] = useState({
        accountNumber: false,
        ifscCode: false,
        mobileNumber: false,
        panNumber: false,
        cibilScore: false
    });

    const handleBackToDashboard = async () => {
        if (isSavingProgress) return;
        try {
            stopDigilockerPolling({ closePopup: true, clearSession: true });
            stopMoneyonePolling();
            stopAaStatusPolling();
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            if (autoSaveTimeoutRef.current) {
                window.clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = null;
            }
            clearPersistedLoanApplicationState();
            lastAutoSavedSnapshotRef.current = '';
            markLoanJourneyForceFreshStart();
            if (storedUser.mobile_number) {
                clearLoanApplicationProgress({
                    mobile: storedUser.mobile_number,
                    id: storedUser.id || null,
                    panNumber: storedUser.pan_number || fetchedData?.pan || '',
                    loanId: loanJourney?.loanId || ''
                }).catch((error) => {
                    console.error('Progress clear failed:', error);
                });
            }
        } finally {
            setIsSavingProgress(false);
            window.location.replace('/dashboard');
        }
    };
    const renderDashboardSaveButton = (options = {}) => (
        <div style={{ marginTop: options.marginTop ?? '12px', display: 'flex', justifyContent: options.justifyContent || 'flex-start' }}>
            <button
                type="button"
                className={`secondary-btn dashboard-save-btn${options.fullWidth ? ' dashboard-save-btn--full-width' : ''}`}
                onClick={handleBackToDashboard}
                style={{ color: '#000' }}
                disabled={isSavingProgress}
            >
                {isSavingProgress ? 'Returning...' : 'Back to Dashboard'}
            </button>
        </div>
    );
    const hasFreshAaProfile = isWithinLast24Hours(fetchedData?.camsProfile?.createdAt);
    const hasRecoveredSalarySignal = Number(fetchedData?.currentOffer?.salaryAmount || 0) > 0;
    const hasStrictAaVerified = isAaVerified({
        aaStatus: fetchedData?.aaStatus,
        hasFreshAaProfile,
        moneyOneState
    });
    // An approved offer (engine or admin BRE pass) already stands on whatever bank data the
    // backend had; the AA step must not demand a new consent from that customer.
    const hasVerifiedAaData = hasStrictAaVerified || hasRecoveredSalarySignal || hasBackendApprovedOffer;
    const currentOfferDecisionStatus = String(fetchedData?.currentOffer?.decisionStatus || '').trim().toUpperCase();
    const currentOfferRuleCode = String(fetchedData?.currentOffer?.ruleCode || '').trim().toUpperCase();
    const isCurrentOfferPendingAa = currentOfferDecisionStatus === 'PENDING' || currentOfferRuleCode === 'AA_PENDING';
    // Trust the backend's own decision status rather than requiring a separate,
    // independently-computed aaStatus signal (hasStrictAaVerified) to also agree --
    // that second signal can lag behind a decision the backend has already made,
    // leaving the UI stuck showing "retry" (and no rejection reason) even though a
    // final decision already exists.
    const isAaDecisionReady = Boolean(currentOfferDecisionStatus) && !isCurrentOfferPendingAa;
    // Step 1 is a demo/indicative screen. Keep it fixed so backend offer decisions
    // only appear after AA verification on the later step.
    const offerDetailsPreviewAmount = OFFER_DETAILS_PREVIEW_MAX_AMOUNT;

    useEffect(() => {
        const camsAccount = buildMoneyoneAccountFromCamsProfile(fetchedData?.camsProfile);
        if (!camsAccount || !hasVerifiedAaData) return;

        setMoneyOneState((prev) => ({
            ...prev,
            status: 'SUCCESS',
            accountFetchBlocked: false,
            accounts: prev.accounts.length ? prev.accounts : [camsAccount],
            selectedLinkRefNumber: prev.selectedLinkRefNumber || camsAccount.linkRefNumber,
            selectedAccount: prev.selectedAccount || camsAccount
        }));

    }, [fetchedData?.camsProfile, hasVerifiedAaData, moneyOneState.accountFetchBlocked]);

    const toggleFieldVisibility = (fieldName) => {
        setShowSensitiveFields((prev) => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };
    const [confetti, setConfetti] = useState([]);
    const [popup, setPopup] = useState({ show: false, message: '', type: 'info', dismissible: true });
    const [fetchDetailsCountdown, setFetchDetailsCountdown] = useState(AA_DECISION_FETCH_COUNTDOWN_SECONDS);
    const [aaWaiting, setAaWaiting] = useState({ active: false, countdown: 60, timedOut: false });
    const [rejectionThankYou, setRejectionThankYou] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [moneyOneModal, setMoneyOneModal] = useState({ open: false, url: '' });
    const [digilockerModal, setDigilockerModal] = useState({ open: false, url: '' });
    const [cameraReady, setCameraReady] = useState(false);
    const [aaTrackerNow, setAaTrackerNow] = useState(Date.now());
    const videoRef = useRef(null);
    const cameraRetryTimeoutRef = useRef(null);
    const digilockerPollRef = useRef(null);
    const digilockerPopupRef = useRef(null);
    const digilockerPopupCloseTimerRef = useRef(null);
    const digilockerPollingRequestRef = useRef('');
    const salaryNotFoundLogoutTimerRef = useRef(null);
    const aaTimeoutGraceTimerRef = useRef(null);
    const decisionLoaderPopupActiveRef = useRef(false);
    const decisionRejectHandledRef = useRef(false);
    const rejectionGraceTimerRef = useRef(null);
    const borrowerSignatureCanvasRef = useRef(null);
    const borrowerSignatureStrokeRef = useRef({ drawing: false, lastX: 0, lastY: 0 });
    // Persists drawn strokes as a data URL so re-renders (e.g. consent checkbox toggle) don't wipe the canvas
    const borrowerSignatureDataRef = useRef('');
    const borrowerSignaturePathRef = useRef('');
    const digilockerPollingStartedAtRef = useRef(0);
    const digilockerStatusRequestRef = useRef({ inFlight: false, requestId: '' });
    const digilockerTerminalRequestRef = useRef('');
    const moneyOnePollRef = useRef(null);
    const moneyOnePollingConsentRef = useRef('');
    const moneyOneStatusRequestRef = useRef({ inFlight: false, consentHandle: '' });
    const moneyOnePollMetaRef = useRef({ attempts: 0 });
    const moneyOneAutoFetchRef = useRef('');
    const moneyOneFetchRetryRef = useRef(null);
    const moneyOneFetchRetryDeadlineRef = useRef(0);
    const aaFetchRetryCountRef = useRef(0);
    const aaCompletionRefreshRef = useRef({ timerId: null, attempts: 0, key: '' });
    const aaStatusPollRef = useRef(null);
    const aaStatusRequestRef = useRef(false);
    const moneyOneIframeRef = useRef(null);
    const aaStatusRefreshAtRef = useRef(0);
    const aaVerifiedSyncRef = useRef('');
    const userDataRefreshAtRef = useRef(0);
    const [borrowerSignaturePresent, setBorrowerSignaturePresent] = useState(false);
    const [borrowerSignaturePreviewDataUrl, setBorrowerSignaturePreviewDataUrl] = useState('');
    const [borrowerSignaturePreviewSignedAtText, setBorrowerSignaturePreviewSignedAtText] = useState('');
    const [borrowerSignatureSubmitted, setBorrowerSignatureSubmitted] = useState(false);
    const [borrowerSignaturePath, setBorrowerSignaturePath] = useState('');
    const [borrowerSignatureSurfaceSize, setBorrowerSignatureSurfaceSize] = useState({ width: 900, height: 220 });
    const [sanctionPreviewHtmlSnapshot, setSanctionPreviewHtmlSnapshot] = useState('');
    const borrowerSignatureStorageKey = loanJourney?.loanId ? `borrower_signature_${loanJourney.loanId}_${loanJourney.termsRevision || 0}` : '';
    const borrowerSignatureStepRef = useRef(step);
    const profileStepFetchRef = useRef(false);
    const bankStepSyncRef = useRef(false);
    // Tracks the terminal (closed/paid) prior loan id we've already reset-to-step-1
    // for, so that reset only fires once per stale reference instead of on every
    // background refreshUserData call (step change, window focus) while the user
    // is progressing through a brand-new application — see isTerminalJourney below.
    const terminalJourneyResetHandledIdRef = useRef('');
    const accountFieldRef = useRef(null);
    const ifscFieldRef = useRef(null);
    const expectedAccountLast4Candidates = getExpectedAccountLast4Candidates({
        selectedAccount: moneyOneState.selectedAccount,
        accounts: moneyOneState.accounts,
        camsProfile: fetchedData?.camsProfile,
        savedBankSuggestion
    });
    const expectedAccountLast4 = expectedAccountLast4Candidates[0] || '';
    const displayMobileNumber = String(fetchedData?.mobile || '').trim();
    const displayPanNumber = String(fetchedData?.pan || '').trim().toUpperCase();
    const hasStoredAadhaarVerification = Boolean(
        isAadhaarDetailsVerified(fetchedData?.aadhaarVerification)
        || (aadhaarFetchState.fetched && isAadhaarDetailsVerified(aadhaarFetchState.details))
    );
    const isAadhaarVerified = Boolean(hasStoredAadhaarVerification);

    const validateEnteredAccountAgainstCams = () => {
        if (!expectedAccountLast4Candidates.length) return true;
        const hasVerifiedAaBankMatch = Boolean(
            String(fetchedData?.aaStatus?.status || '').trim().toUpperCase() === 'VERIFIED'
            && (
                moneyOneState.selectedAccount?.maskedAccountNumber
                || moneyOneState.accounts.some((account) => String(account?.maskedAccountNumber || '').trim())
                || fetchedData?.camsProfile?.accountNumberMasked
                || savedBankSuggestion?.accountNumber
            )
        );
        if (hasVerifiedAaBankMatch) return true;
        const enteredLast4 = normalizeAccountLast4(
            bankDetails.accountNumber
            || savedBankSuggestion?.accountNumber
            || loanJourney?.accountNumber
            || ''
        );
        if (enteredLast4 && expectedAccountLast4Candidates.includes(enteredLast4)) return true;
        showPopup(
            expectedAccountLast4Candidates.length === 1
                ? `Please enter the correct account number ending with ${expectedAccountLast4}.`
                : "Please enter the correct bank account number for one of your verified linked accounts.",
            "error"
        );
        return false;
    };
    const validateEnteredAccountAgainstCamsEvent = useEffectEvent(() => validateEnteredAccountAgainstCams());
    const showPopup = (message, type = 'info', options = {}) => {
        if (shouldSuppressAaPopupMessage(message)) return;
        setPopup({
            show: true,
            message: getDisplayMessage(message),
            type,
            dismissible: type === 'loading' ? false : options.dismissible !== false,
            onClose: typeof options.onClose === 'function' ? options.onClose : null
        });
    };
    const isBlockingFetchPopup = useCallback((popupState) => {
        const currentMessage = String(popupState?.message || '').trim().toLowerCase();
        return currentMessage.includes('we are fetching your data please wait for a while');
    }, []);
    const closePopup = useCallback((force = false) => setPopup((p) => {
        if (force || (p.dismissible && !isBlockingFetchPopup(p))) {
            if (typeof p.onClose === 'function') {
                try {
                    p.onClose();
                } catch (_err) {
                    console.error('Popup onClose error:', _err);
                }
            }
            return { ...p, show: false, dismissible: true, onClose: null };
        }
        return p;
    }), [isBlockingFetchPopup]);
    useEffect(() => {
        if (step !== 2 && popup.show && popup.type === 'loading') {
            closePopup(true);
        }
    }, [step, popup.show, popup.type, closePopup]);
    const isBlockingFetchVisible = popup.show && isBlockingFetchPopup(popup);
    useEffect(() => {
        if (!isBlockingFetchVisible) {
            setFetchDetailsCountdown(AA_DECISION_FETCH_COUNTDOWN_SECONDS);
            return undefined;
        }

        setFetchDetailsCountdown(AA_DECISION_FETCH_COUNTDOWN_SECONDS);
        const timerId = window.setInterval(() => {
            setFetchDetailsCountdown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => window.clearInterval(timerId);
    }, [isBlockingFetchVisible]);
    const clearSalaryNotFoundLogoutTimer = useCallback(() => {
        if (salaryNotFoundLogoutTimerRef.current) {
            window.clearTimeout(salaryNotFoundLogoutTimerRef.current);
            salaryNotFoundLogoutTimerRef.current = null;
        }
    }, []);
    const logoutAfterSessionEnd = useCallback((message, delayMs = 4000, type = 'error', dismissible = true) => {
        clearSalaryNotFoundLogoutTimer();
        setLoading(false);
        showPopup(
            message || 'We are unable to approve the offer right now. Our team will reach out to you soon.',
            type,
            { dismissible }
        );
        salaryNotFoundLogoutTimerRef.current = window.setTimeout(() => {
            clearUserSession();
            navigate('/login', { replace: true });
            window.setTimeout(() => {
                if (window.location.pathname !== '/login') {
                    window.location.replace('/login');
                }
            }, 250);
        }, delayMs);
    }, [clearSalaryNotFoundLogoutTimer, navigate]);
    const logoutAfterSalaryNotFound = (message) => {
        logoutAfterSessionEnd(message, AA_DATA_TIMEOUT_LOGOUT_DELAY_MS, 'info', false);
    };
    const showRejectionThankYouAndLogout = useCallback((reason = '') => {
        clearSalaryNotFoundLogoutTimer();
        setLoading(false);
        setAaWaiting({ active: false, countdown: 60, timedOut: false });
        setRejectionReason(String(reason || '').trim());
        setRejectionThankYou(true);
        salaryNotFoundLogoutTimerRef.current = window.setTimeout(() => {
            clearUserSession();
            navigate('/login', { replace: true });
            window.setTimeout(() => {
                if (window.location.pathname !== '/login') {
                    window.location.replace('/login');
                }
            }, 250);
        }, REJECTION_SCREEN_AUTO_LOGOUT_MS);
    }, [clearSalaryNotFoundLogoutTimer, navigate]);
    const beginAaRestart = useCallback(() => {
        clearSalaryNotFoundLogoutTimer();
        aaRestartRef.current = { active: true, startedAt: aaRestartRef.current.startedAt || Date.now(), stepApplied: true };
        if (moneyOnePollRef.current) {
            window.clearInterval(moneyOnePollRef.current);
            moneyOnePollRef.current = null;
        }
        moneyOneAutoFetchRef.current = '';
        setMoneyOneState({ ...DEFAULT_MONEYONE_STATE });
        setAaWaiting({ active: false, countdown: 60, timedOut: false });
        setRejectionThankYou(false);
        setRejectionReason('');
        setLoading(false);
        setSelectedAmount(0);
        setSelectedTenure(clampLoanTenureDays(MAX_LOAN_TENURE_DAYS));
        setStep(2);
    }, [clearSalaryNotFoundLogoutTimer]);
    useEffect(() => {
        if (!aaRestartRef.current.active || aaRestartRef.current.stepApplied || !fetchedData) return;
        aaRestartRef.current.stepApplied = true;
        beginAaRestart();
    }, [fetchedData, beginAaRestart]);
    const openSalarySlipUploadFromRejection = () => {
        clearSalaryNotFoundLogoutTimer();
        setShowSalarySlipModal(true);
    };
    const handleSalarySlipUploaded = (status) => {
        if (status?.status !== 'VERIFIED') return;
        setRejectionThankYou(false);
        setRejectionReason('');
        refreshUserData({ withLoading: true });
    };
    useEffect(() => {
        if (fetchDetailsCountdown > 0 || !isBlockingFetchVisible) return;
        if (aaFetchRetryCountRef.current < 1) {
            aaFetchRetryCountRef.current += 1;
            closePopup(true);
            decisionLoaderPopupActiveRef.current = false;
            setAaWaiting({ active: true, countdown: 60, timedOut: true });
        } else {
            // Pull the latest decision before declaring rejection -- by the time this
            // fires, the backend may already have computed (and stored) a real reason
            // that hasn't been polled into fetchedData yet.
            refreshUserData({ withLoading: false })
                .then((latestData) => {
                    showRejectionThankYouAndLogout(
                        latestData?.currentOffer?.displayReason
                        || latestData?.currentOffer?.reason
                        || ''
                    );
                })
                .catch(() => showRejectionThankYouAndLogout());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchDetailsCountdown, isBlockingFetchVisible, closePopup, showRejectionThankYouAndLogout]);
    const processMoneyOneIframeReturn = useEffectEvent(() => {
        const iframe = moneyOneIframeRef.current;
        if (!iframe) return false;
        try {
            const iframeLoc = iframe.contentWindow?.location;
            const iframeHref = iframeLoc?.href || '';
            // Only process when CAMS has redirected back to our own callback URL
            const callbackBase = `${window.location.origin}/apply-loan`;
            if (!iframeHref.startsWith(callbackBase)) return false;
            const iframeSearch = iframeLoc?.search || '';
            const iframeHash = iframeLoc?.hash || '';
            const params = new URLSearchParams(iframeSearch);
            const consentId = params.get('consentId') || params.get('consent_id') || params.get('ConsentID');
            const status = params.get('status') || params.get('eventStatus') || params.get('consentStatus');
            if (!consentId && !status) return false;
            applyMoneyoneJourneyReturn({ search: iframeSearch, hash: iframeHash, currentUrl: iframeHref, replaceHistory: false });
            return true;
        } catch (_e) {
            // Cross-origin iframe (CAMS page) — can't read location, which is expected
            return false;
        }
    });

    const closeMoneyOneModal = () => {
        const handledReturn = processMoneyOneIframeReturn();
        const wasAbandoned = !handledReturn && !moneyOneState.consentId && moneyOneState.consentWindowOpen;
        setMoneyOneModal({ open: false, url: '' });
        setMoneyOneState((prev) => ({
            ...prev,
            consentWindowOpen: false,
            status: wasAbandoned ? 'WAITING_FOR_CONSENT' : prev.status
        }));
        if (wasAbandoned) {
            stopMoneyonePolling();
            showPopup('You have closed the Account Aggregator window. Tap "Reopen AA Window" to complete verification.', 'warning');
        }
    };

    const handleMoneyOneModalDone = () => {
        processMoneyOneIframeReturn();
        setMoneyOneModal({ open: false, url: '' });
        setMoneyOneState((prev) => ({
            ...prev,
            consentWindowOpen: false
        }));
        setAaWaiting((prev) => prev.active ? prev : { active: true, countdown: 60, timedOut: false });
    };
    const closeDigilockerModal = () => setDigilockerModal({ open: false, url: '' });
    const closeDigilockerPopupWindow = () => {
        if (digilockerPopupCloseTimerRef.current) {
            window.clearTimeout(digilockerPopupCloseTimerRef.current);
            digilockerPopupCloseTimerRef.current = null;
        }
        if (digilockerPopupRef.current && !digilockerPopupRef.current.closed) {
            digilockerPopupRef.current.close();
        }
        digilockerPopupRef.current = null;
    };
    const scheduleDigilockerPopupClose = (delayMs = 2000) => {
        if (digilockerPopupCloseTimerRef.current) {
            window.clearTimeout(digilockerPopupCloseTimerRef.current);
        }
        digilockerPopupCloseTimerRef.current = window.setTimeout(() => {
            closeDigilockerPopupWindow();
            digilockerPopupCloseTimerRef.current = null;
        }, Math.max(0, Number(delayMs || 0)));
    };
    const primeDigilockerPopup = () => {
        const popupWidth = 520;
        const popupHeight = 760;
        const left = Math.max(0, Math.round((window.screen.width - popupWidth) / 2));
        const top = Math.max(0, Math.round((window.screen.height - popupHeight) / 2));
        const popupFeatures = `popup=yes,width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`;

        let popupWindow = digilockerPopupRef.current;
        if (!popupWindow || popupWindow.closed) {
            popupWindow = window.open('', DIGILOCKER_POPUP_NAME, popupFeatures);
            digilockerPopupRef.current = popupWindow;
        } else {
            popupWindow.focus();
        }

        if (!popupWindow) {
            return null;
        }

        try {
            popupWindow.document.title = 'Aadhaar Verification';
            popupWindow.document.body.innerHTML = '<div style="font-family:Inter,sans-serif;padding:24px;text-align:center;color:#0f172a;">Preparing DigiLocker verification...</div>';
        } catch (_error) {
            // Ignore same-origin initialization errors before navigation.
        }

        return popupWindow;
    };
    const openDigilockerPopup = (url) => {
        const popupWindow = primeDigilockerPopup();

        if (!popupWindow) {
            return { ok: false, reason: 'popup-blocked' };
        }

        try {
            popupWindow.document.title = 'Aadhaar Verification';
            popupWindow.document.body.innerHTML = '<div style="font-family:Inter,sans-serif;padding:24px;text-align:center;color:#0f172a;">Redirecting to DigiLocker...</div>';
        } catch (_error) {
            // Ignore same-origin initialization errors before navigation.
        }

        popupWindow.location.replace(url);
        popupWindow.focus();
        return { ok: true };
    };
    const scrollFieldIntoKeyboardView = useEffectEvent((target) => {
        const node = target?.current || target;
        if (!node || typeof node.scrollIntoView !== 'function') return;

        window.setTimeout(() => {
            try {
                node.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });
            } catch (_error) {
                node.scrollIntoView(true);
            }
        }, 260);
    });
    const clearDigilockerPendingState = useEffectEvent(() => {
        if (digilockerPollRef.current) {
            window.clearInterval(digilockerPollRef.current);
            digilockerPollRef.current = null;
        }
        digilockerPollingRequestRef.current = '';
        digilockerPollingStartedAtRef.current = 0;
        closeDigilockerPopupWindow();
        setDigilockerModal({ open: false, url: '' });
    });
    const stopDigilockerPolling = ({ closePopup = false, clearSession = false } = {}) => {
        const activeRequestId = String(digilockerPollingRequestRef.current || digilockerState.requestId || verificationData.aadhaarRequestId || '').trim();
        if (digilockerPollRef.current) {
            window.clearInterval(digilockerPollRef.current);
            digilockerPollRef.current = null;
        }
        digilockerPollingRequestRef.current = '';
        digilockerPollingStartedAtRef.current = 0;
        digilockerStatusRequestRef.current = { inFlight: false, requestId: '' };
        if (digilockerPopupCloseTimerRef.current) {
            window.clearTimeout(digilockerPopupCloseTimerRef.current);
            digilockerPopupCloseTimerRef.current = null;
        }
        if (activeRequestId) {
            clearDigilockerPollLock(activeRequestId);
        }
        if (closePopup) {
            setDigilockerModal({ open: false, url: '' });
        }
        if (clearSession) {
            clearStoredDigilockerSession();
        }
        setDigilockerState((prev) => ({ ...prev, polling: false }));
    };
    const saveDigilockerSession = (session) => {
        if (!session?.requestId) return;
        sessionStorage.setItem(DIGILOCKER_SESSION_STORAGE_KEY, JSON.stringify(session));
    };
    const clearStoredDigilockerSession = () => {
        sessionStorage.removeItem(DIGILOCKER_SESSION_STORAGE_KEY);
    };
    const stopMoneyonePolling = useEffectEvent(() => {
        if (moneyOnePollRef.current) {
            window.clearInterval(moneyOnePollRef.current);
            moneyOnePollRef.current = null;
        }
        moneyOnePollingConsentRef.current = '';
        moneyOneStatusRequestRef.current = { inFlight: false, consentHandle: '' };
        moneyOnePollMetaRef.current = { attempts: 0 };
        setMoneyOneState((prev) => ({ ...prev, polling: false, nextStatusCheckAt: '' }));
    });
    const stopAaCompletionRefresh = useEffectEvent(() => {
        if (aaCompletionRefreshRef.current.timerId) {
            window.clearTimeout(aaCompletionRefreshRef.current.timerId);
        }
        aaCompletionRefreshRef.current = { timerId: null, attempts: 0, key: '' };
    });
    const stopAaStatusPolling = useEffectEvent(() => {
        if (aaStatusPollRef.current) {
            window.clearInterval(aaStatusPollRef.current);
            aaStatusPollRef.current = null;
        }
    });
    const clearMoneyoneFetchRetry = useEffectEvent(() => {
        if (moneyOneFetchRetryRef.current) {
            window.clearTimeout(moneyOneFetchRetryRef.current);
            moneyOneFetchRetryRef.current = null;
        }
    });
    const resetMoneyoneFetchRetryWait = useEffectEvent(() => {
        clearMoneyoneFetchRetry();
        moneyOneFetchRetryDeadlineRef.current = 0;
    });
    const scheduleMoneyoneFetchRetry = useEffectEvent((retryFn, waitMessage = AA_DECISION_FETCH_LOADER_MESSAGE) => {
        if (aaWaiting.active || isAaDecisionReady) return;
        const now = Date.now();
        const currentDeadline = Number(moneyOneFetchRetryDeadlineRef.current || 0);
        const deadline = currentDeadline > now ? currentDeadline : (now + AA_PENDING_WAIT_MAX_MS);
        moneyOneFetchRetryDeadlineRef.current = deadline;

        const remainingMs = deadline - now;
        if (remainingMs <= 0) {
            resetMoneyoneFetchRetryWait();
            refreshUserData({ withLoading: false })
                .then((latestData) => {
                    const amount = Number(latestData?.currentOffer?.offeredAmount || latestData?.currentOffer?.amount || 0);
                    const approved = Boolean(latestData?.currentOffer?.isEligible || latestData?.currentOffer?.approved) && amount > 0;
                    if (approved) {
                        if (bankVerificationCompleted) {
                            closePopup(true);
                            setStep(3);
                        } else {
                            showPopup("AA data received. Please enter account number and IFSC to continue.", "info");
                        }
                        return;
                    }
                    logoutAfterSessionEnd(AA_DATA_TIMEOUT_LOGOUT_MESSAGE, AA_DATA_TIMEOUT_LOGOUT_DELAY_MS, 'info', false);
                })
                .catch(() => logoutAfterSessionEnd(AA_DATA_TIMEOUT_LOGOUT_MESSAGE, AA_DATA_TIMEOUT_LOGOUT_DELAY_MS, 'info', false));
            return;
        }

        clearMoneyoneFetchRetry();
        showPopup(waitMessage, 'loading', { dismissible: false });
        moneyOneFetchRetryRef.current = window.setTimeout(() => {
            if (Date.now() >= moneyOneFetchRetryDeadlineRef.current) {
                resetMoneyoneFetchRetryWait();
                refreshUserData({ withLoading: false })
                    .then((latestData) => {
                        const amount = Number(latestData?.currentOffer?.offeredAmount || latestData?.currentOffer?.amount || 0);
                        const approved = Boolean(latestData?.currentOffer?.isEligible || latestData?.currentOffer?.approved) && amount > 0;
                        if (approved) {
                            if (bankVerificationCompleted) {
                                closePopup(true);
                                setStep(3);
                            } else {
                                showPopup("AA data received. Please enter account number and IFSC to continue.", "info");
                            }
                            return;
                        }
                        logoutAfterSessionEnd(AA_DATA_TIMEOUT_LOGOUT_MESSAGE, AA_DATA_TIMEOUT_LOGOUT_DELAY_MS, 'info', false);
                    })
                    .catch(() => logoutAfterSessionEnd(AA_DATA_TIMEOUT_LOGOUT_MESSAGE, AA_DATA_TIMEOUT_LOGOUT_DELAY_MS, 'info', false));
                return;
            }
            retryFn();
        }, Math.min(1000, remainingMs));
    });
    // ----- Signature: pure SVG, pointer-events implementation -----
    const drawBorrowerSignatureCanvas = useEffectEvent(() => {
        // no-op, SVG renders reactively
    });
    const resizeBorrowerSignatureCanvas = useEffectEvent(() => {
        const container = borrowerSignatureCanvasRef.current;
        if (!container) return;
        const displayWidth = container.offsetWidth || 600;
        const displayHeight = container.offsetHeight || 220;
        setBorrowerSignatureSurfaceSize({ width: displayWidth, height: displayHeight });
    });
    const getBorrowerSignaturePoint = useCallback((event) => {
        const container = borrowerSignatureCanvasRef.current;
        if (!container) return null;
        const rect = container.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        const source = event.touches?.[0] || event.changedTouches?.[0] || event;
        // SVG viewBox matches CSS pixels 1:1, so exact offsets work perfectly
        return {
            x: source.clientX - rect.left,
            y: source.clientY - rect.top
        };
    }, []);
    const startBorrowerSignatureStroke = useCallback((event) => {
        const container = borrowerSignatureCanvasRef.current;
        const point = getBorrowerSignaturePoint(event);
        if (!container || !point) return;
        
        setBorrowerSignaturePreviewDataUrl('');
        setBorrowerSignaturePreviewSignedAtText('');
        setBorrowerSignatureSubmitted(false);
        setShowSanctionPreview(false);
        setBorrowerSignaturePresent(true);
        
        borrowerSignatureStrokeRef.current = {
            drawing: true,
            lastX: point.x,
            lastY: point.y
        };
        
        // Use a tiny line to represent a dot if it's just a tap
        const startPath = `M ${point.x.toFixed(1)} ${point.y.toFixed(1)} L ${(point.x + 0.1).toFixed(1)} ${(point.y + 0.1).toFixed(1)}`;
        if (!borrowerSignaturePathRef.current) {
            borrowerSignaturePathRef.current = startPath;
            setBorrowerSignaturePath(startPath);
        } else {
            const nextPath = `${borrowerSignaturePathRef.current} ${startPath}`;
            borrowerSignaturePathRef.current = nextPath;
            setBorrowerSignaturePath(nextPath);
        }
    }, [getBorrowerSignaturePoint]);
    const continueBorrowerSignatureStroke = useCallback((event) => {
        if (!borrowerSignatureStrokeRef.current.drawing) return;
        const point = getBorrowerSignaturePoint(event);
        if (!point) return;
        
        setBorrowerSignaturePresent(true);
        
        const nextPath = `${borrowerSignaturePathRef.current || ''} L ${point.x.toFixed(1)} ${point.y.toFixed(1)}`;
        borrowerSignaturePathRef.current = nextPath;
        setBorrowerSignaturePath(nextPath);
        
        borrowerSignatureStrokeRef.current.lastX = point.x;
        borrowerSignatureStrokeRef.current.lastY = point.y;
    }, [getBorrowerSignaturePoint]);
    const endBorrowerSignatureStroke = useCallback(() => {
        borrowerSignatureStrokeRef.current.drawing = false;
        if (borrowerSignaturePathRef.current) {
            borrowerSignatureDataRef.current = 'has-data';
        } else {
            setBorrowerSignaturePresent(false);
            borrowerSignatureDataRef.current = '';
        }
    }, []);
    const clearBorrowerSignature = () => {
        borrowerSignatureDataRef.current = '';
        borrowerSignaturePathRef.current = '';
        setBorrowerSignaturePath('');
        setBorrowerSignaturePresent(false);
        setBorrowerSignaturePreviewDataUrl('');
        setBorrowerSignaturePreviewSignedAtText('');
        setBorrowerSignatureSubmitted(false);
        setSanctionPreviewHtmlSnapshot('');
        if (borrowerSignatureStorageKey) {
            try { localStorage.removeItem(borrowerSignatureStorageKey); } catch (_error) {}
        }
        setShowSanctionPreview(false);
    };
    
    // Attach pointer events to the SVG container
    useEffect(() => {
        const container = borrowerSignatureCanvasRef.current;
        if (!container || step !== 6 || borrowerSignatureSubmitted) return undefined;

        const onDown = (event) => {
            event.preventDefault();
            try { container.setPointerCapture(event.pointerId); } catch (_) {}
            startBorrowerSignatureStroke(event);
        };
        const onMove = (event) => {
            event.preventDefault();
            continueBorrowerSignatureStroke(event);
        };
        const onUp = (event) => {
            event.preventDefault();
            endBorrowerSignatureStroke();
            try { container.releasePointerCapture(event.pointerId); } catch (_) {}
        };
        const onCancel = () => endBorrowerSignatureStroke();

        container.addEventListener('pointerdown', onDown, { passive: false });
        container.addEventListener('pointermove', onMove, { passive: false });
        container.addEventListener('pointerup', onUp, { passive: false });
        container.addEventListener('pointercancel', onCancel, { passive: false });

        return () => {
            container.removeEventListener('pointerdown', onDown);
            container.removeEventListener('pointermove', onMove);
            container.removeEventListener('pointerup', onUp);
            container.removeEventListener('pointercancel', onCancel);
        };
    }, [step, borrowerSignatureSubmitted, startBorrowerSignatureStroke, continueBorrowerSignatureStroke, endBorrowerSignatureStroke]);

    const captureBorrowerSignatureDataUrl = () => {
        const container = borrowerSignatureCanvasRef.current;
        const path = String(borrowerSignaturePathRef.current || borrowerSignaturePath || '').trim();
        if (!container || !borrowerSignaturePresent || !path) return '';

        const width = Math.max(1, Math.round(borrowerSignatureSurfaceSize.width || container.offsetWidth || 600));
        const height = Math.max(1, Math.round(borrowerSignatureSurfaceSize.height || container.offsetHeight || 220));
        const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
                <rect width="100%" height="100%" fill="#ffffff" />
                <path d="${path}" fill="none" stroke="#0f172a" stroke-width="${Math.max(2, width / 220)}" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        `.trim();

        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    };
    useEffect(() => {
        if (step !== 6) return undefined;
        const timer = window.setTimeout(() => resizeBorrowerSignatureCanvas(), 0);
        return () => window.clearTimeout(timer);
    }, [step, resizeBorrowerSignatureCanvas]);
    useEffect(() => {
        const enteredStepSix = step === 6 && borrowerSignatureStepRef.current !== 6;
        borrowerSignatureStepRef.current = step;
        if (enteredStepSix) {
            setShowSanctionPreview(false);
            setSanctionPreviewHtmlSnapshot('');
            setBorrowerSignaturePresent(false);
            setBorrowerSignatureSubmitted(false);
            setBorrowerSignaturePreviewDataUrl('');
            setBorrowerSignaturePreviewSignedAtText('');
            borrowerSignatureDataRef.current = '';
            borrowerSignaturePathRef.current = '';
            setBorrowerSignaturePath('');
            borrowerSignatureStrokeRef.current = { drawing: false, lastX: 0, lastY: 0 };
            window.setTimeout(() => drawBorrowerSignatureCanvas(), 0);
        }
    }, [step, drawBorrowerSignatureCanvas]);
    useEffect(() => {
        if (!borrowerSignatureStorageKey) return;
        if (borrowerSignatureSubmitted && borrowerSignaturePreviewDataUrl) return;

        try {
            const stored = JSON.parse(localStorage.getItem(borrowerSignatureStorageKey) || 'null');
            if (stored?.dataUrl) {
                setBorrowerSignaturePreviewDataUrl(stored.dataUrl);
                setBorrowerSignaturePreviewSignedAtText(stored.signedAtText || '');
                setBorrowerSignatureSubmitted(true);
            }
        } catch (_error) {
        }
    }, [borrowerSignatureStorageKey, borrowerSignatureSubmitted, borrowerSignaturePreviewDataUrl]);
    const persistMoneyoneJourneyCallback = useEffectEvent(async ({
        debugEvent = '',
        skipDedupe = false,
        consentId = '',
        sessionId = '',
        requestId = '',
        consentStatus = '',
        currentUrl = '',
        queryObject = null
    } = {}) => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const callbackSearch = queryObject ? new URLSearchParams(queryObject).toString() : '';
        const callbackKey = `${CAMS_CALLBACK_STORAGE_PREFIX}${debugEvent || 'event'}:${callbackSearch ? `?${callbackSearch}` : currentUrl}`;

        if (!skipDedupe && sessionStorage.getItem(callbackKey)) {
            return;
        }

        const baseUrl = getApiBaseUrl();
        await fetch(`${baseUrl}/api/webhooks/cams-consent-callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                debugEvent,
                source: 'frontend-return',
                consentHandle: sessionId || requestId || moneyOneState.consentHandle || '',
                consentId,
                sessionId: sessionId || requestId || '',
                status: consentStatus || '',
                userId: storedUser.id || null,
                mobile: storedUser.mobile_number || fetchedData?.mobile || '',
                redirectTarget: `${window.location.origin}${location.pathname}`,
                query: queryObject || {},
                currentUrl
            })
        }).catch((error) => {
            console.error('CAMS callback persistence failed:', error);
        });

        sessionStorage.setItem(callbackKey, '1');
    });
    const applyMoneyoneJourneyReturn = useEffectEvent(async ({
        search = '',
        hash = '',
        currentUrl = '',
        replaceHistory = false
    } = {}) => {
        const searchParams = buildCallbackSearchParams({ search, hash, currentUrl });
        const consentId = getFirstSearchParam(searchParams, ['consentId', 'consent_id', 'ConsentID', 'consentid', 'consent_id']);
        const sessionId = getFirstSearchParam(searchParams, ['sessionId', 'session_id', 'SessionID', 'sessionid', 'requestId', 'request_id']);
        const requestId = getFirstSearchParam(searchParams, ['requestId', 'request_id', 'RequestID']);
        const consentStatus = getFirstSearchParam(searchParams, ['status', 'eventStatus', 'consentStatus', 'event_status', 'consent_status']);
        const hasApprovedSignal = Boolean(consentId) || isCompletedMoneyoneConsentStatus(consentStatus);
        const hasRejectedSignal = isRejectedMoneyoneConsentStatus(consentStatus);
        const hasCompletionSignal = hasApprovedSignal || hasRejectedSignal;

        if (!consentId && !sessionId && !requestId && !consentStatus) {
            return false;
        }

        await persistMoneyoneJourneyCallback({
            debugEvent: hasCompletionSignal ? 'popup_return_completed' : 'popup_return_ignored',
            skipDedupe: true,
            consentId,
            sessionId,
            requestId,
            consentStatus,
            currentUrl,
            queryObject: Object.fromEntries(searchParams.entries())
        });

        if (!hasCompletionSignal) {
            setMoneyOneState((prev) => ({
                ...prev,
                connecting: false,
                polling: false,
                consentWindowOpen: false,
                status: 'WAITING_FOR_CONSENT',
                lastStatusCheckAt: '',
                nextStatusCheckAt: ''
            }));
            return false;
        }

        await persistMoneyoneJourneyCallback({
            debugEvent: 'popup_return_accepted',
            skipDedupe: true,
            consentId,
            sessionId,
            requestId,
            consentStatus,
            currentUrl,
            queryObject: Object.fromEntries(searchParams.entries())
        });

        stopMoneyonePolling();
        moneyOnePollMetaRef.current = { attempts: 0 };
        const acceptedAtIso = new Date().toISOString();
        if (hasApprovedSignal) {
            setMoneyOneState((prev) => ({
                ...prev,
                connecting: false,
                polling: true,
                consentWindowOpen: false,
                consentHandle: sessionId || requestId || prev.consentHandle,
                consentId: consentId || prev.consentId,
                status: 'DATA_PENDING',
                consentStartedAt: prev.consentStartedAt || acceptedAtIso,
                lastStatusCheckAt: acceptedAtIso,
                nextStatusCheckAt: new Date(Date.now() + 2000).toISOString(),
                session: {
                    ...(prev.session || {}),
                    consent_id: consentId || prev.session?.consent_id || '',
                    session_id: sessionId || requestId || prev.session?.session_id || ''
                }
            }));
            setFetchedData((prev) => ({
                ...prev,
                aaStatus: {
                    ...(prev?.aaStatus || {}),
                    consentId: consentId || prev?.aaStatus?.consentId || '',
                    consentHandle: sessionId || requestId || prev?.aaStatus?.consentHandle || '',
                    status: SIMPLE_AA_STATUS.PENDING,
                    phase: 'DATA_PENDING',
                    consentStatus: consentStatus || prev?.aaStatus?.consentStatus || 'CONSENT_APPROVED'
                }
            }));
            setMoneyOneModal({ open: false, url: '' });
            setAaWaiting((prev) => prev.active ? prev : { active: true, countdown: 60, timedOut: false });
        } else {
            setMoneyOneState((prev) => ({
                ...prev,
                connecting: false,
                polling: false,
                consentWindowOpen: false,
                status: 'WAITING_FOR_CONSENT',
                consentId: '',
                lastStatusCheckAt: '',
                nextStatusCheckAt: ''
            }));
            setFetchedData((prev) => ({
                ...prev,
                aaStatus: {
                    ...(prev?.aaStatus || {}),
                    consentId: '',
                    status: SIMPLE_AA_STATUS.PENDING,
                    phase: 'PENDING',
                    consentStatus: consentStatus || prev?.aaStatus?.consentStatus || 'PENDING'
                }
            }));
            showPopup('CAMS consent was not completed. Please tap Start Validation again.', 'warning');
        }

        if (replaceHistory) {
            window.history.replaceState({}, document.title, location.pathname);
        }

        refreshAaStatus();
        scheduleAaCompletionRefresh(consentId || sessionId || requestId || 'popup-return');

        return true;
    });
    useEffect(() => {
        applyMoneyoneJourneyReturn({
            search: location.search || '',
            hash: location.hash || '',
            currentUrl: window.location.href,
            replaceHistory: true
        }).catch((error) => {
            console.error('Moneyone callback handling failed:', error);
        });
    }, [applyMoneyoneJourneyReturn, location.search, location.hash]);
    useEffect(() => {
        const shouldTrack = step === 2 && (
            moneyOneState.connecting ||
            moneyOneState.polling ||
            moneyOneState.fetchingData ||
            Boolean(moneyOneState.consentId) ||
            ['PENDING', 'CONSENT_ACTIVE', 'DATA_PENDING'].includes(String(fetchedData?.aaStatus?.phase || '').trim().toUpperCase()) ||
            String(fetchedData?.aaStatus?.status || '').trim().toUpperCase() === SIMPLE_AA_STATUS.PENDING
        );

        if (!shouldTrack) {
            return undefined;
        }

        setAaTrackerNow(Date.now());
        // Keep the AA status clock in sync with the polling cadence to avoid a jittery UI.
        const timer = window.setInterval(() => {
            setAaTrackerNow(Date.now());
        }, AA_STATUS_POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(timer);
        };
    }, [
        step,
        moneyOneState.connecting,
        moneyOneState.polling,
        moneyOneState.fetchingData,
        moneyOneState.consentId,
        fetchedData?.aaStatus?.phase,
        fetchedData?.aaStatus?.status
    ]);
    useEffect(() => {
        const hasAaJourneyStarted = Boolean(
            moneyOneState.consentStartedAt
            || moneyOneState.consentId
            || moneyOneState.consentHandle
            || moneyOneState.connecting
            || moneyOneState.polling
            || moneyOneState.fetchingData
            || moneyOneState.consentWindowOpen
        );
        const shouldShowDecisionLoader = step === 2
            && !isAaDecisionReady
            && !rejectionThankYou
            && !moneyOneState.connecting
            && !moneyOneState.consentWindowOpen
            && moneyOneState.status !== 'WAITING_FOR_CONSENT'
            && fetchedData !== null
            && (hasAaJourneyStarted || bankVerificationCompleted)
            && (
                isCurrentOfferPendingAa
                || isAaBankDataPending({
                    aaStatus: fetchedData?.aaStatus,
                    moneyOneState,
                    hasVerifiedAaData
                })
            );

        if (shouldShowDecisionLoader) {
            if (!aaWaiting.active && !decisionLoaderPopupActiveRef.current) {
                showPopup(AA_DECISION_FETCH_LOADER_MESSAGE, 'loading', { dismissible: false });
                decisionLoaderPopupActiveRef.current = true;
            }
            return;
        }

        if (decisionLoaderPopupActiveRef.current) {
            closePopup(true);
            decisionLoaderPopupActiveRef.current = false;
        }
    }, [
        step,
        isAaDecisionReady,
        rejectionThankYou,
        isCurrentOfferPendingAa,
        moneyOneState,
        moneyOneState.consentHandle,
        moneyOneState.consentStartedAt,
        moneyOneState.connecting,
        moneyOneState.polling,
        moneyOneState.fetchingData,
        moneyOneState.consentWindowOpen,
        moneyOneState.consentId,
        moneyOneState.status,
        fetchedData,
        fetchedData?.aaStatus,
        hasVerifiedAaData,
        bankVerificationCompleted,
        aaWaiting.active,
        closePopup
    ]);
    useEffect(() => {
        if (!aaWaiting.active || aaWaiting.timedOut) return undefined;
        const timer = window.setInterval(() => {
            setAaWaiting((prev) => {
                if (!prev.active || prev.timedOut) return prev;
                const next = prev.countdown - 1;
                return next <= 0
                    ? { ...prev, countdown: 0, timedOut: true }
                    : { ...prev, countdown: next };
            });
        }, 1000);
        return () => window.clearInterval(timer);
    }, [aaWaiting.active, aaWaiting.timedOut]);
    useEffect(() => {
        if (!aaWaiting.timedOut || isAaDecisionReady) {
            if (aaTimeoutGraceTimerRef.current) {
                window.clearTimeout(aaTimeoutGraceTimerRef.current);
                aaTimeoutGraceTimerRef.current = null;
            }
            return;
        }
        aaTimeoutGraceTimerRef.current = window.setTimeout(() => {
            if (isAaDecisionReady) return;
            refreshUserData({ withLoading: false })
                .then((latestData) => {
                    if (isAaDecisionReady) return;
                    showRejectionThankYouAndLogout(
                        latestData?.currentOffer?.displayReason
                        || latestData?.currentOffer?.reason
                        || fetchedData?.currentOffer?.displayReason
                        || fetchedData?.currentOffer?.reason
                        || ''
                    );
                })
                .catch(() => {
                    if (!isAaDecisionReady) {
                        showRejectionThankYouAndLogout(fetchedData?.currentOffer?.displayReason || fetchedData?.currentOffer?.reason || '');
                    }
                });
        }, 15000);
        return () => {
            if (aaTimeoutGraceTimerRef.current) {
                window.clearTimeout(aaTimeoutGraceTimerRef.current);
                aaTimeoutGraceTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aaWaiting.timedOut, isAaDecisionReady, showRejectionThankYouAndLogout, fetchedData?.currentOffer?.displayReason, fetchedData?.currentOffer?.reason]);
    useEffect(() => {
        if (!isAaDecisionReady) return;
        if (aaWaiting.active) {
            setAaWaiting((prev) => ({ ...prev, active: false }));
        }
        closePopup(true);
        if (moneyOneFetchRetryRef.current) {
            window.clearTimeout(moneyOneFetchRetryRef.current);
            moneyOneFetchRetryRef.current = null;
        }
        moneyOneFetchRetryDeadlineRef.current = 0;
    }, [isAaDecisionReady, aaWaiting.active, closePopup]);
    useEffect(() => {
        // HOLD_INCOME_PROOF (BRE v3): no salary verified yet; shown like a decline with the
        // upload / relink guidance so the customer knows what unblocks the offer.
        const terminalRejectStatuses = new Set(['DECLINED', 'REJECTED', 'FAILED', 'NOT_ELIGIBLE', 'HOLD_INCOME_PROOF']);
        const hasExplicitRejectedDecision = terminalRejectStatuses.has(currentOfferDecisionStatus);
        const shouldHandleRejectedDecision = step === 2
            && bankVerificationCompleted
            && !isCurrentOfferPendingAa
            && hasExplicitRejectedDecision
            && !isCurrentOfferApproved;

        if (!shouldHandleRejectedDecision) {
            decisionRejectHandledRef.current = false;
            if (rejectionGraceTimerRef.current) {
                window.clearTimeout(rejectionGraceTimerRef.current);
                rejectionGraceTimerRef.current = null;
            }
            return;
        }

        if (decisionRejectHandledRef.current) return;
        decisionRejectHandledRef.current = true;

        // A NOT_ELIGIBLE/DECLINED snapshot can be a transient Push_Data-before-Processed_Data
        // race (salary lands moments later and flips the decision to approved), so wait briefly
        // and re-pull the decision before treating it as final -- mirrors the grace re-check
        // already used above for the AA-timeout and fetch-retry decline paths.
        rejectionGraceTimerRef.current = window.setTimeout(() => {
            rejectionGraceTimerRef.current = null;
            refreshUserData({ withLoading: false })
                .then((latestData) => {
                    const latestStatus = String(latestData?.currentOffer?.decisionStatus || '').trim().toUpperCase();
                    const latestRuleCode = String(latestData?.currentOffer?.ruleCode || '').trim().toUpperCase();
                    const latestApproved = Boolean(latestData?.currentOffer?.isEligible || latestData?.currentOffer?.approved);
                    const latestPendingAa = latestStatus === 'PENDING' || latestRuleCode === 'AA_PENDING';
                    if (latestApproved || latestPendingAa || !terminalRejectStatuses.has(latestStatus)) {
                        decisionRejectHandledRef.current = false;
                        return;
                    }
                    showRejectionThankYouAndLogout(
                        latestData?.currentOffer?.displayReason
                        || latestData?.currentOffer?.reason
                        || fetchedData?.currentOffer?.displayReason
                        || fetchedData?.currentOffer?.reason
                        || ''
                    );
                })
                .catch(() => {
                    showRejectionThankYouAndLogout(fetchedData?.currentOffer?.displayReason || fetchedData?.currentOffer?.reason || '');
                });
        }, 6000);

        return () => {
            if (rejectionGraceTimerRef.current) {
                // Timer never fired -- nothing was actually handled, so free the guard
                // to let a later re-run (still declined) re-arm a fresh grace check.
                window.clearTimeout(rejectionGraceTimerRef.current);
                rejectionGraceTimerRef.current = null;
                decisionRejectHandledRef.current = false;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, bankVerificationCompleted, isCurrentOfferPendingAa, isCurrentOfferApproved, currentOfferDecisionStatus, logoutAfterSessionEnd]);
    const applyFetchedAadhaarDetails = useEffectEvent((details) => {
        if (!details || !isAadhaarDetailsVerified(details)) return;
        setAadhaarFetchState({
            loading: false,
            fetched: true,
            details
        });
        setFetchedData((prev) => ({
            ...prev,
            name: details.name || prev?.name || '',
            dob: details.dob || prev?.dob || '',
            panAddress: details.address || prev?.panAddress || '',
            aadhaarVerification: details
        }));
    });
    const refreshUserData = useEffectEvent(async ({ withLoading = false, fresh = false } = {}) => {
        const now = Date.now();
        if (!withLoading && !fresh && userDataRefreshAtRef.current && (now - userDataRefreshAtRef.current) < 5000) {
            return fetchedData;
        }
        userDataRefreshAtRef.current = now;
        if (withLoading) {
            setLoading(true);
        }

        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const mobile = storedUser.mobile_number;
            if (!mobile) {
                const fallbackData = {
                    name: storedUser.full_name || 'User',
                    pan: storedUser.pan_number || '--',
                    mobile: '--',
                    cibil: '--',
                    currentOffer: null
                };
                setVerificationData((prev) => ({
                    ...prev,
                    officialEmail: prev.officialEmail || storedUser.email || ''
                }));
                setFetchedData(fallbackData);
                return fallbackData;
            }

            const r = await fetchUserDataWithAuth(mobile, {
                salaryCompanyName: salaryCompanyNameHint,
                salaryBracket: salaryBracketHint,
                fresh
            });
            if (r.status === 401 || r.status === 403 || r.status === 404) {
                clearLoanApplicationProgress(mobile).catch((_error) => { });
                clearPersistedLoanApplicationState();
                clearUserSession();
                navigate('/login', { replace: true });
                return null;
            }

            const d = await r.json();
            if (d.success && d.user) {
                const savedSuggestion = d.user.saved_bank_suggestion || null;
                const savedAadhaar = d.user.aadhaar_verification || null;
                const camsProfile = d.user.cams_profile || null;
                const aaStatus = d.user.aa_status || null;
                const latestSanctionLetter = d.user.latest_sanction_letter || null;
                const latestLoanJourney = d.user.latest_loan_journey || null;
                const latestLoanJourneyId = String(latestLoanJourney?.loanId || latestLoanJourney?.loan_id || '').trim();
                const latestLoanJourneyStage = String(latestLoanJourney?.stage || '').trim().toUpperCase();
                const latestLoanJourneyPaymentStatus = String(latestLoanJourney?.paymentStatus || latestLoanJourney?.payment_status || '').trim().toUpperCase();
                const hasPendingLoanJourney = Boolean(
                    latestLoanJourneyId
                    && latestLoanJourneyStage
                    && latestLoanJourneyStage !== 'DISBURSED'
                    && !['PAID', 'OVERPAID', 'CLOSED'].includes(latestLoanJourneyPaymentStatus)
                );
                const normalizedAadhaarState = (savedAadhaar && isAadhaarDetailsVerified(savedAadhaar))
                    ? {
                        loading: false,
                        fetched: true,
                        details: {
                            requestId: savedAadhaar.requestId || '',
                            name: savedAadhaar.name || '',
                            maskedUid: savedAadhaar.maskedUid || '',
                            aadhaarLast4: savedAadhaar.aadhaarLast4 || '',
                            dob: savedAadhaar.dob || '',
                            gender: savedAadhaar.gender || '',
                            address: savedAadhaar.address || '',
                            district: savedAadhaar.district || '',
                            city: savedAadhaar.city || '',
                            state: savedAadhaar.state || '',
                            pincode: savedAadhaar.pincode || '',
                            country: savedAadhaar.country || '',
                            photoUrl: savedAadhaar.photoUrl || '',
                            aadhaarPdfUrl: savedAadhaar.aadhaarPdfUrl || '',
                            aadhaarJpegUrl: savedAadhaar.aadhaarJpegUrl || '',
                            x509SubjectName: savedAadhaar.x509SubjectName || '',
                            validAadhaarDsc: savedAadhaar.validAadhaarDsc || ''
                        }
                    }
                    : DEFAULT_AADHAAR_FETCH_STATE;
                const nextCurrentOfferFromResponse = d.user.current_offer || null;
                const activeLoanJourneySnapshot = loanJourney || null;
                const nextLatestLoanJourney = latestLoanJourney
                    ? {
                        loanId: latestLoanJourney.loanId || latestLoanJourney.loan_id || latestLoanJourneyId,
                        stage: latestLoanJourney.stage || latestLoanJourney.payment_status || latestLoanJourneyStage || '',
                        paymentStatus: latestLoanJourney.paymentStatus || latestLoanJourney.payment_status || latestLoanJourneyPaymentStatus || '',
                        disbursedAt: latestLoanJourney.disbursedAt || latestLoanJourney.disbursed_at || null,
                        closedAt: latestLoanJourney.closedAt || latestLoanJourney.closed_at || null,
                        sanctionedAmount: latestLoanJourney.sanctionedAmount ?? latestLoanJourney.sanctioned_amount ?? activeLoanJourneySnapshot?.sanctionedAmount ?? null,
                        processingFee: latestLoanJourney.processingFee ?? latestLoanJourney.processing_fee ?? activeLoanJourneySnapshot?.processingFee ?? null,
                        gstAmount: latestLoanJourney.gstAmount ?? latestLoanJourney.gst_amount ?? activeLoanJourneySnapshot?.gstAmount ?? null,
                        finalDisbursement: latestLoanJourney.finalDisbursement ?? latestLoanJourney.final_disbursement ?? activeLoanJourneySnapshot?.finalDisbursement ?? null,
                        selectedTenureDays: latestLoanJourney.selectedTenureDays ?? latestLoanJourney.selected_tenure_days ?? activeLoanJourneySnapshot?.selectedTenureDays ?? null,
                        tenureDays: latestLoanJourney.tenureDays ?? latestLoanJourney.tenure_days ?? activeLoanJourneySnapshot?.tenureDays ?? null,
                        totalInterestCharge: latestLoanJourney.totalInterestCharge ?? latestLoanJourney.total_interest_charge ?? activeLoanJourneySnapshot?.totalInterestCharge ?? null,
                        totalRepaymentAmount: latestLoanJourney.totalRepaymentAmount ?? latestLoanJourney.total_repayment_amount ?? activeLoanJourneySnapshot?.totalRepaymentAmount ?? null,
                        repaymentDate: latestLoanJourney.repaymentDate || latestLoanJourney.repayment_date || activeLoanJourneySnapshot?.repaymentDate || '',
                        sanctionDate: latestLoanJourney.sanctionDate || latestLoanJourney.sanction_date || latestSanctionLetter?.sentAt || latestSanctionLetter?.sanction_letter_sent_at || activeLoanJourneySnapshot?.sanctionDate || '',
                        bankName: latestLoanJourney.bankName || latestLoanJourney.bank_name || activeLoanJourneySnapshot?.bankName || '',
                        ifscCode: latestLoanJourney.ifscCode || latestLoanJourney.ifsc_code || activeLoanJourneySnapshot?.ifscCode || '',
                        accountLast4: latestLoanJourney.accountLast4 || latestLoanJourney.account_last4 || activeLoanJourneySnapshot?.accountLast4 || '',
                        customerName: latestLoanJourney.customerName || latestLoanJourney.customer_name || activeLoanJourneySnapshot?.customerName || d.user.full_name || storedUser.full_name || '',
                        customerAddress: latestLoanJourney.customerAddress || latestLoanJourney.customer_address || activeLoanJourneySnapshot?.customerAddress || d.user.pan_address || '',
                        dateOfBirth: latestLoanJourney.dateOfBirth || latestLoanJourney.date_of_birth || activeLoanJourneySnapshot?.dateOfBirth || d.user.dob || '',
                        aadhaarRequestId: latestLoanJourney.aadhaarRequestId || latestLoanJourney.aadhaar_request_id || activeLoanJourneySnapshot?.aadhaarRequestId || '',
                        aadhaarLast4: latestLoanJourney.aadhaarLast4 || latestLoanJourney.aadhaar_last4 || activeLoanJourneySnapshot?.aadhaarLast4 || '',
                        productCode: latestLoanJourney.productCode || latestLoanJourney.product_code || activeLoanJourneySnapshot?.productCode || nextCurrentOfferFromResponse?.productCode || nextCurrentOfferFromResponse?.product || null
                    }
                    : null;
                const restoredBankDetails = resolveLoanJourneyBankDetails(
                    activeLoanJourneySnapshot,
                    {
                        camsProfile,
                        savedBankSuggestion: savedSuggestion,
                        latestLoanJourney: nextLatestLoanJourney,
                        latestSanctionLetter
                    }
                );
                const nextFetchedData = {
                    name: d.user.full_name || storedUser.full_name || 'User',
                    pan: d.user.pan_number || storedUser.pan_number || '--',
                    mobile: d.user.mobile_number || mobile,
                    dob: d.user.dob || '',
                    cibil: (Number.isFinite(Number(d.user.cibil_score)) && Number(d.user.cibil_score) > 0 ? Number(d.user.cibil_score) : '--'),
                    panAddress: d.user.pan_address || '',
                    creditReport: d.user.credit_report || null,
                    aadhaarLast4: d.user.aadhaar_last4 || '',
                    aadhaarVerification: isAadhaarDetailsVerified(d.user.aadhaar_verification) ? d.user.aadhaar_verification : null,
                    camsProfile,
                    savedBankSuggestion: savedSuggestion,
                    latestLoanJourney: nextLatestLoanJourney,
                    latestSanctionLetter: latestSanctionLetter
                        ? {
                            loanId: latestSanctionLetter.loanId || latestSanctionLetter.loan_id || '',
                            customerName: latestSanctionLetter.customerName || latestSanctionLetter.customer_name || '',
                            sanctionedAmount: latestSanctionLetter.sanctionedAmount ?? latestSanctionLetter.sanctioned_amount ?? null,
                            finalDisbursement: latestSanctionLetter.finalDisbursement ?? latestSanctionLetter.final_disbursement ?? null,
                            repaymentDate: latestSanctionLetter.repaymentDate || latestSanctionLetter.repayment_date || '',
                            sanctionDate: latestSanctionLetter.sanctionDate || latestSanctionLetter.sanction_date || latestSanctionLetter.sentAt || latestSanctionLetter.sanction_letter_sent_at || nextLatestLoanJourney?.sanctionDate || '',
                            bankName: latestSanctionLetter.bankName || latestSanctionLetter.bank_name || nextLatestLoanJourney?.bankName || '',
                            ifscCode: latestSanctionLetter.ifscCode || latestSanctionLetter.ifsc_code || nextLatestLoanJourney?.ifscCode || '',
                            accountLast4: latestSanctionLetter.accountLast4 || latestSanctionLetter.account_last4 || nextLatestLoanJourney?.accountLast4 || '',
                            html: String(latestSanctionLetter.html || latestSanctionLetter.sanction_letter_html || '').trim(),
                            sentAt: latestSanctionLetter.sentAt || latestSanctionLetter.sanction_letter_sent_at || '',
                            viewUrl: latestSanctionLetter.viewUrl || '',
                            downloadUrl: latestSanctionLetter.downloadUrl || ''
                        }
                        : null,
                    aaStatus,
                    marketingLimit: Number(d.user.marketing_limit || d.user.loan_target || 0),
                    loanTarget: Number(d.user.loan_target || d.user.marketing_limit || 0),
                    currentOffer: d.user.current_offer || null
                };
                const isDemoUserPayload = Boolean(DEMO_DISBURSAL_MOBILE) && String(nextFetchedData.mobile || '').trim() === DEMO_DISBURSAL_MOBILE;
                const demoAadhaarVerification = isDemoUserPayload
                    ? buildDemoAadhaarDetails({
                        fetchedData: {
                            ...nextFetchedData,
                            dob: nextFetchedData.dob || '1995-01-15',
                            panAddress: nextFetchedData.panAddress || 'Demo Address, Bengaluru, Karnataka, 560001'
                        },
                        storedUserFullName: nextFetchedData.name
                    })
                    : null;
                const nextCurrentOffer = nextFetchedData.currentOffer || null;
                if (aaRestartRef.current.active) {
                    const profileCreatedMs = new Date(nextFetchedData?.camsProfile?.createdAt || 0).getTime();
                    if (profileCreatedMs > aaRestartRef.current.startedAt) aaRestartRef.current.active = false;
                }
                setFetchedData(isDemoUserPayload
                    ? {
                        ...nextFetchedData,
                        aadhaarLast4: demoAadhaarVerification.aadhaarLast4 || nextFetchedData.aadhaarLast4 || '',
                        aadhaarVerification: demoAadhaarVerification
                    }
                    : nextFetchedData);
                setAadhaarFetchState(isDemoUserPayload
                    ? {
                        loading: false,
                        fetched: true,
                        details: demoAadhaarVerification
                    }
                    : normalizedAadhaarState);
                const cleanCustomerMobile = String(mobile || d.user.mobile_number || storedUser.mobile_number || '').replace(/\D/g, '').slice(-10);
                const savedVerifiedEmail = getCustomerVerifiedEmail(cleanCustomerMobile);
                const serverEmail = String(d.user.email || storedUser.email || '').trim().toLowerCase();
                const resolvedVerifiedEmail = (savedVerifiedEmail && isValidEmail(savedVerifiedEmail))
                    ? savedVerifiedEmail
                    : ((d.user.email_verified || storedUser.isEmailVerified) && serverEmail && isValidEmail(serverEmail))
                        ? serverEmail
                        : '';

                if (resolvedVerifiedEmail) {
                    setIsEmailVerified(true);
                    setVerifiedEmailAddress(resolvedVerifiedEmail);
                    setOfficialEmailDraft(resolvedVerifiedEmail);
                    setVerificationData((prev) => ({
                        ...prev,
                        officialEmail: resolvedVerifiedEmail,
                        aadhaarRequestId: prev.aadhaarRequestId || savedAadhaar?.requestId || (isDemoUserPayload ? DEMO_AADHAAR_VERIFICATION_ID : '')
                    }));
                    if (cleanCustomerMobile && !savedVerifiedEmail) {
                        saveCustomerVerifiedEmail(cleanCustomerMobile, resolvedVerifiedEmail, latestLoanJourneyId);
                    }
                } else {
                    setVerificationData((prev) => ({
                        ...prev,
                        officialEmail: prev.officialEmail || serverEmail || '',
                        aadhaarRequestId: prev.aadhaarRequestId || savedAadhaar?.requestId || (isDemoUserPayload ? DEMO_AADHAAR_VERIFICATION_ID : '')
                    }));
                }
                const nextCurrentOfferAmount = Number(
                    nextCurrentOffer?.offeredAmount
                    || nextCurrentOffer?.amount
                    || 0
                );
                const nextCurrentOfferApproved = Boolean(
                    nextCurrentOffer?.isEligible
                    || nextCurrentOffer?.approved
                ) && nextCurrentOfferAmount > 0;
                const nextCurrentOfferStatus = String(nextCurrentOffer?.decisionStatus || '').trim().toUpperCase();
                const nextCurrentOfferRule = String(nextCurrentOffer?.ruleCode || '').trim().toUpperCase();
                const nextCurrentOfferPendingAa = nextCurrentOfferStatus === 'PENDING' || nextCurrentOfferRule === 'AA_PENDING';
                const priorCurrentOffer = fetchedData?.currentOffer || null;
                const priorCurrentOfferAmount = Number(
                    priorCurrentOffer?.offeredAmount
                    || priorCurrentOffer?.amount
                    || 0
                );
                const priorCurrentOfferApproved = Boolean(
                    priorCurrentOffer?.isEligible
                    || priorCurrentOffer?.approved
                ) && priorCurrentOfferAmount > 0;
                const isTerminalJourney = Boolean(latestLoanJourneyId) && (
                    latestLoanJourneyStage === 'CLOSED'
                    || ['PAID', 'OVERPAID', 'CLOSED'].includes(latestLoanJourneyPaymentStatus)
                );
                const shouldForceDeclinedOfferExit = step >= 3
                    || moneyOneState.accountFetchComplete
                    || moneyOneState.accountFetchBlocked
                    || Boolean(moneyOneState.consentId);
                if (nextCurrentOffer && !nextCurrentOfferApproved && !nextCurrentOfferPendingAa && shouldForceDeclinedOfferExit && !priorCurrentOfferApproved && !aaRestartRef.current.active) {
                    clearPersistedLoanApplicationState();
                    const completionKey = getJourneyCompletionStorageKey(mobile);
                    if (completionKey) {
                        try {
                            localStorage.removeItem(completionKey);
                        } catch (_error) {
                        }
                    }
                    clearLoanApplicationProgress({
                        mobile,
                        userId: d.user.id || storedUser.id || null,
                        panNumber: d.user.pan_number || storedUser.pan_number || ''
                    }).catch((error) => {
                        console.error('Failed to clear declined-offer progress:', error);
                    });
                    setLoanJourney(null);
                    setSelectedOffer(null);
                    setSelectedAmount(DEFAULT_LOAN_PRINCIPAL_AMOUNT);
                    setSelectedTenure(clampLoanTenureDays(MAX_LOAN_TENURE_DAYS));
                    showRejectionThankYouAndLogout(nextCurrentOffer?.displayReason || nextCurrentOffer?.reason || '');
                    return nextFetchedData;
                }
                // Only reset once per distinct terminal (closed/paid) loan id. Without this
                // guard, every background refreshUserData call (step change, window focus —
                // e.g. right after landing on the Account Aggregator step) re-fetches the SAME
                // stale terminal loan reference from the backend (a fresh loan_journey row for
                // the NEW application isn't created until later steps), re-derives
                // isTerminalJourney === true, and bounces the user back to step 1 every time —
                // discarding an in-progress new application (e.g. right as the AA iframe was
                // about to launch).
                if (isTerminalJourney && terminalJourneyResetHandledIdRef.current !== latestLoanJourneyId) {
                    terminalJourneyResetHandledIdRef.current = latestLoanJourneyId;
                    const completionKey = getJourneyCompletionStorageKey(mobile);
                    if (completionKey) {
                        try {
                            localStorage.removeItem(completionKey);
                        } catch (_error) {
                        }
                    }
                    clearPersistedLoanApplicationState();
                    clearLoanApplicationProgress({
                        mobile,
                        id: d.user.id || storedUser.id || null,
                        panNumber: d.user.pan_number || storedUser.pan_number || '',
                        loanId: latestLoanJourneyId
                    }).catch((error) => {
                        console.error('Failed to clear terminal journey progress:', error);
                    });
                    setLoanJourney(null);
                    setSelectedOffer(null);
                    setSelectedAmount(requestedAmount || DEFAULT_LOAN_PRINCIPAL_AMOUNT);
                    setSelectedTenure(clampLoanTenureDays(MAX_LOAN_TENURE_DAYS));
                    setStep(1);
                    lastAutoSavedSnapshotRef.current = '';
                    return;
                }
                const shouldForceFreshStart = forceFreshStartRef.current;
                if (shouldForceFreshStart) {
                    if (cleanCustomerMobile) {
                        clearCustomerVerifiedEmail(cleanCustomerMobile);
                    }
                    setIsEmailVerified(false);
                    setVerifiedEmailAddress('');
                }
                if (hasPendingLoanJourney && shouldForceFreshStart) {
                    forceFreshStartRef.current = false;
                    setStep(1);
                } else if (hasPendingLoanJourney && (!resumeLoanJourneyId || latestLoanJourneyId === resumeLoanJourneyId)) {
                    const resolvedJourneyTenureDays = resolveLoanJourneyTenureDays(latestLoanJourney, nextFetchedData, selectedTenure)
                        || clampLoanTenureDays(selectedTenure || MAX_LOAN_TENURE_DAYS);
                    const hydratedCurrentOfferAmount = Number(
                        nextCurrentOffer?.offeredAmount
                        || nextCurrentOffer?.amount
                        || latestLoanJourney.sanctionedAmount
                        || latestLoanJourney.sanctioned_amount
                        || approvedLimitAmount
                        || requestedAmount
                        || 0
                    );
                    const hydratedCurrentOfferPricing = nextCurrentOffer?.pricing || {};
                    const resumedInterestPerDayRate = normalizeRate(
                        hydratedCurrentOfferPricing.interestPerDay,
                        DAILY_INTEREST_RATE,
                        { treatZeroAsMissing: true }
                    );
                    const hydratedLoanJourney = sanitizeHydratedLoanJourney({
                        ...(activeLoanJourneySnapshot || {}),
                        loanId: latestLoanJourney.loanId || latestLoanJourney.loan_id || latestLoanJourneyId || activeLoanJourneySnapshot?.loanId || '',
                        sanctionedAmount: latestLoanJourney.sanctionedAmount ?? latestLoanJourney.sanctioned_amount ?? activeLoanJourneySnapshot?.sanctionedAmount ?? null,
                        processingFee: latestLoanJourney.processingFee ?? latestLoanJourney.processing_fee ?? activeLoanJourneySnapshot?.processingFee ?? null,
                        gstAmount: latestLoanJourney.gstAmount ?? latestLoanJourney.gst_amount ?? activeLoanJourneySnapshot?.gstAmount ?? null,
                        finalDisbursement: latestLoanJourney.finalDisbursement ?? latestLoanJourney.final_disbursement ?? activeLoanJourneySnapshot?.finalDisbursement ?? null,
                        tenureDays: resolvedJourneyTenureDays,
                        totalInterestCharge: latestLoanJourney.totalInterestCharge ?? latestLoanJourney.total_interest_charge ?? activeLoanJourneySnapshot?.totalInterestCharge ?? null,
                        totalRepaymentAmount: latestLoanJourney.totalRepaymentAmount ?? latestLoanJourney.total_repayment_amount ?? activeLoanJourneySnapshot?.totalRepaymentAmount ?? null,
                        repaymentDate: latestLoanJourney.repaymentDate || latestLoanJourney.repayment_date || activeLoanJourneySnapshot?.repaymentDate || '',
                        bankName: latestLoanJourney.bankName || latestLoanJourney.bank_name || activeLoanJourneySnapshot?.bankName || restoredBankDetails.bankName || '',
                        ifscCode: latestLoanJourney.ifscCode || latestLoanJourney.ifsc_code || activeLoanJourneySnapshot?.ifscCode || restoredBankDetails.ifscCode || '',
                        accountLast4: latestLoanJourney.accountLast4 || latestLoanJourney.account_last4 || activeLoanJourneySnapshot?.accountLast4 || restoredBankDetails.accountLast4 || '',
                        customerName: latestLoanJourney.customerName || latestLoanJourney.customer_name || activeLoanJourneySnapshot?.customerName || d.user.full_name || storedUser.full_name || '',
                        customerAddress: latestLoanJourney.customerAddress || latestLoanJourney.customer_address || activeLoanJourneySnapshot?.customerAddress || d.user.pan_address || '',
                        dateOfBirth: latestLoanJourney.dateOfBirth || latestLoanJourney.date_of_birth || activeLoanJourneySnapshot?.dateOfBirth || d.user.dob || '',
                        aadhaarRequestId: latestLoanJourney.aadhaarRequestId || latestLoanJourney.aadhaar_request_id || activeLoanJourneySnapshot?.aadhaarRequestId || '',
                        aadhaarLast4: latestLoanJourney.aadhaarLast4 || latestLoanJourney.aadhaar_last4 || activeLoanJourneySnapshot?.aadhaarLast4 || '',
                        productCode: latestLoanJourney.productCode || latestLoanJourney.product_code || activeLoanJourneySnapshot?.productCode || nextCurrentOffer?.productCode || nextCurrentOffer?.product || null,
                        sanctionDate: latestLoanJourney.sanctionDate || latestLoanJourney.sanction_date || activeLoanJourneySnapshot?.sanctionDate || latestSanctionLetter?.sentAt || latestSanctionLetter?.sanction_letter_sent_at || '',
                        sanctionLetterHtml: activeLoanJourneySnapshot?.sanctionLetterHtml || latestSanctionLetter?.html || activeLoanJourneySnapshot?.sanction_letter_html || ''
                    });
                    if (!selectedOffer && nextCurrentOffer) {
                        setSelectedOffer({
                            bank: nextCurrentOffer.bankName || nextCurrentOffer.bank || latestLoanJourney.bankName || latestLoanJourney.bank_name || '',
                            amount: formatINR(hydratedCurrentOfferAmount),
                            tenure: `${resolvedJourneyTenureDays} Days`,
                            interest: `${resumedInterestPerDayRate >= 1 ? resumedInterestPerDayRate : resumedInterestPerDayRate * 100}% per day`,
                            interestPerDay: resumedInterestPerDayRate,
                        processingFeePercent: Number(
                            hydratedCurrentOfferPricing.processingFeePercent != null
                                ? hydratedCurrentOfferPricing.processingFeePercent
                                : PROCESSING_FEE_RATE * 100
                            ) || (PROCESSING_FEE_RATE * 100),
                        gstApplicable: hydratedCurrentOfferPricing.gstApplicable !== false,
                        productCode: nextCurrentOffer.productCode || nextCurrentOffer.product || null
                    });
                }
                    setLoanJourney(hydratedLoanJourney ? {
                        ...(activeLoanJourneySnapshot || {}),
                        ...hydratedLoanJourney,
                        bankName: hydratedLoanJourney.bankName || activeLoanJourneySnapshot?.bankName || restoredBankDetails.bankName || '',
                        ifscCode: hydratedLoanJourney.ifscCode || activeLoanJourneySnapshot?.ifscCode || restoredBankDetails.ifscCode || '',
                        accountLast4: hydratedLoanJourney.accountLast4 || activeLoanJourneySnapshot?.accountLast4 || restoredBankDetails.accountLast4 || '',
                        sanctionDate: hydratedLoanJourney.sanctionDate || activeLoanJourneySnapshot?.sanctionDate || latestSanctionLetter?.sentAt || latestSanctionLetter?.sanction_letter_sent_at || '',
                        sanctionLetterHtml: hydratedLoanJourney.sanctionLetterHtml || activeLoanJourneySnapshot?.sanctionLetterHtml || latestSanctionLetter?.html || ''
                    } : hydratedLoanJourney);
                    setSelectedAmount(resolveLoanJourneyPrincipalAmount(latestLoanJourney, {
                        requestedAmount,
                        approvedLimitAmount
                    }));
                    setSelectedTenure(resolvedJourneyTenureDays);
                    setBankDetails((prev) => ({
                        ...prev,
                        ifscCode: latestLoanJourney.ifscCode || latestLoanJourney.ifsc_code || prev.ifscCode || ''
                    }));
                    // loan_journeys is created as soon as Aadhaar verification (step 4) completes, still
                    // at its initial 'EMAIL_AADHAAR_VERIFIED' stage -- so hasPendingLoanJourney alone
                    // doesn't mean the borrower ever reached the summary/signature step. Only jump to 6
                    // once the stage has actually advanced past that initial value; otherwise this is the
                    // same "picked amount/tenure, didn't finish" case as the step-3 resume rule above.
                    // Note: stage stays EMAIL_AADHAAR_VERIFIED even while the borrower is live on the
                    // summary/sanction-letter screen (step 6) until they explicitly confirm -- refreshUserData
                    // runs on every step change, so without the `step < 5` guard this would yank a borrower
                    // who is genuinely sitting at step 5/6 in the current session back down to step 3.
                    if (latestLoanJourneyStage !== 'EMAIL_AADHAAR_VERIFIED') {
                        if (step < 6) {
                            setStep(6);
                        }
                    } else if (step < 5 && step !== 3) {
                        setStep(3);
                    }
                }
                if (camsProfile?.accountNumberMasked) {
                    setMoneyOneState((prev) => {
                        if (prev.accounts.length) return prev;
                        return {
                            ...prev,
                            accounts: [
                                {
                                    id: `cams-profile-${camsProfile.accountLast4 || 'account'}`,
                                    linkRefNumber: `cams-profile-${camsProfile.accountLast4 || 'account'}`,
                                    maskedAccountNumber: camsProfile.accountNumberMasked,
                                    bank: camsProfile.bankName || '',
                                    fiType: 'DEPOSIT',
                                    accountType: camsProfile.accountType || '',
                                    ifscCode: camsProfile.ifscCode || '',
                                    holderName: camsProfile.fullName || ''
                                }
                            ]
                        };
                    });
                }
                if (savedAadhaar && isAadhaarDetailsVerified(savedAadhaar)) {
                    setAadhaarFetchState({
                        loading: false,
                        fetched: true,
                        details: savedAadhaar
                    });
                    setFetchedData((prev) => ({
                        ...prev,
                        name: savedAadhaar.name || prev?.name || '',
                        dob: savedAadhaar.dob || prev?.dob || '',
                        panAddress: savedAadhaar.address || prev?.panAddress || '',
                        aadhaarVerification: savedAadhaar
                    }));
                } else if (!isAadhaarVerified) {
                    setAadhaarFetchState(DEFAULT_AADHAAR_FETCH_STATE);
                }
                setSavedBankSuggestion(savedSuggestion);
                return nextFetchedData;
            } else {
                const fallbackData = {
                    name: storedUser.full_name || 'User',
                    pan: storedUser.pan_number || '--',
                    mobile,
                    dob: '',
                    panAddress: '',
                    cibil: '--',
                    aadhaarLast4: '',
                    camsProfile: null,
                    aaStatus: null,
                    marketingLimit: 0,
                    loanTarget: 0,
                    currentOffer: null
                };
                setVerificationData((prev) => ({
                    ...prev,
                    officialEmail: prev.officialEmail || storedUser.email || ''
                }));
                setFetchedData(fallbackData);
                setSavedBankSuggestion(null);
                return fallbackData;
            }
        } catch (err) {
            console.error('Failed to fetch loan details:', err);
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const fallbackData = {
                name: storedUser.full_name || 'User',
                pan: storedUser.pan_number || '--',
                mobile: storedUser.mobile_number || '--',
                dob: '',
                panAddress: '',
                cibil: '--',
                aadhaarLast4: '',
                camsProfile: null,
                aaStatus: null,
                marketingLimit: 0,
                loanTarget: 0,
                currentOffer: null
            };
            setFetchedData(fallbackData);
            setSavedBankSuggestion(null);
            return fallbackData;
        } finally {
            if (withLoading) {
                setLoading(false);
            }
        }
    });
    const fetchActiveRepaymentLoan = useEffectEvent(async () => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const latestLoanJourney = fetchedData?.latestLoanJourney || storedUser.latest_loan_journey || null;
        const latestLoanJourneyId = String(latestLoanJourney?.loanId || latestLoanJourney?.loan_id || '').trim();
        const latestLoanJourneyStage = String(latestLoanJourney?.stage || '').trim().toUpperCase();
        const latestLoanJourneyPaymentStatus = String(latestLoanJourney?.paymentStatus || latestLoanJourney?.payment_status || '').trim().toUpperCase();
        const isOpenLoanJourney = Boolean(
            latestLoanJourneyId
            && latestLoanJourneyStage
            && !['PAID', 'OVERPAID', 'CLOSED'].includes(latestLoanJourneyPaymentStatus)
        );
        if (isOpenLoanJourney) {
            const sanctionedAmount = Number(
                latestLoanJourney?.sanctionedAmount
                || latestLoanJourney?.sanctioned_amount
                || latestLoanJourney?.finalDisbursement
                || latestLoanJourney?.final_disbursement
                || 0
            );
            const totalRepaymentAmount = Number(latestLoanJourney?.totalRepaymentAmount || latestLoanJourney?.total_repayment_amount || sanctionedAmount || 0);
            const dueAmount = Math.max(0, totalRepaymentAmount || sanctionedAmount);
            return {
                loanId: latestLoanJourneyId,
                stage: latestLoanJourneyStage || 'DISBURSED',
                paymentStatus: dueAmount > 0 ? 'PENDING' : 'PAID',
                dueAmount,
                baseRepaymentDue: dueAmount,
                collectionDueAmount: dueAmount,
                penaltyLateInterest: 0,
                totalRepaymentAmount,
                totalPaid: 0
            };
        }

        const query = new URLSearchParams();
        if (storedUser.id) {
            query.set('userId', String(storedUser.id));
        } else if (storedUser.mobile_number || fetchedData?.mobile) {
            query.set('mobile', String(storedUser.mobile_number || fetchedData?.mobile || '').trim());
        }

        if (![...query.keys()].length) return null;
        query.set('_ts', String(Date.now()));

        try {
            const response = await fetch(`${getApiBaseUrl()}/api/repayments?${query.toString()}`, {
                cache: 'no-store',
                headers: buildAuthHeaders({
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache'
                })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) return null;
            return (Array.isArray(data.loans) ? data.loans : []).find((loan) => {
                const stage = String(loan?.stage || '').trim().toUpperCase();
                const paymentStatus = String(loan?.paymentStatus || '').trim().toUpperCase();
                return stage === 'DISBURSED' && !['PAID', 'OVERPAID', 'CLOSED'].includes(paymentStatus);
            }) || null;
        } catch (_error) {
            return null;
        }
    });
    const refreshAaStatus = useEffectEvent(async ({ force = false } = {}) => {
        if (aaStatusRequestRef.current) return null;
        const now = Date.now();
        if (!force && aaStatusRefreshAtRef.current && (now - aaStatusRefreshAtRef.current) < 3000) {
            return null;
        }

        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const mobile = String(storedUser.mobile_number || fetchedData?.mobile || '').trim();
        if (!mobile) return null;

        aaStatusRequestRef.current = true;
        aaStatusRefreshAtRef.current = now;

        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/aa-status?mobile=${encodeURIComponent(mobile)}`, {
                method: 'GET',
                cache: 'no-store',
                headers: buildAuthHeaders({
                    'Cache-Control': 'no-cache'
                })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                return null;
            }

            setFetchedData((prev) => ({
                ...prev,
                aaStatus: data.aaStatus || prev?.aaStatus || null,
                camsProfile: data.camsProfile || prev?.camsProfile || null
            }));
            if (aaRestartRef.current.active) {
                const profileCreatedMs = new Date(data.camsProfile?.createdAt || 0).getTime();
                if (profileCreatedMs > aaRestartRef.current.startedAt) aaRestartRef.current.active = false;
            }

            const restartAwaitingNewConsent = aaRestartRef.current.active && !moneyOneState.consentHandle && !moneyOneState.connecting;
            const resetStaleAaSession = restartAwaitingNewConsent || shouldResetStaleAaSession({
                aaStatus: data.aaStatus || null,
                camsProfile: data.camsProfile || null,
                moneyOneState: {
                    ...moneyOneState,
                    consentId: data.aaStatus?.consentId || moneyOneState.consentId,
                    status: data.aaStatus?.consentStatus || data.aaStatus?.status || moneyOneState.status
                }
            });
            setMoneyOneState((prev) => ({
                ...prev,
                consentId: resetStaleAaSession ? '' : (data.aaStatus?.consentId || prev.consentId),
                consentHandle: resetStaleAaSession ? '' : (data.aaStatus?.consentHandle || prev.consentHandle),
                redirectUrl: resetStaleAaSession ? '' : prev.redirectUrl,
                status: resetStaleAaSession ? '' : (data.aaStatus?.consentStatus || prev.status),
                accounts: resetStaleAaSession ? [] : prev.accounts,
                selectedAccount: resetStaleAaSession ? null : prev.selectedAccount,
                selectedLinkRefNumber: resetStaleAaSession ? '' : prev.selectedLinkRefNumber,
                accountFetchComplete: resetStaleAaSession ? false : prev.accountFetchComplete,
                accountFetchBlocked: resetStaleAaSession ? false : prev.accountFetchBlocked,
                consentStartedAt: resetStaleAaSession ? '' : (prev.consentStartedAt || (data.aaStatus?.consentId ? new Date().toISOString() : '')),
                lastStatusCheckAt: resetStaleAaSession ? '' : new Date().toISOString(),
                nextStatusCheckAt: resetStaleAaSession ? '' : (step === 2 && !hasStrictAaVerified ? new Date(Date.now() + AA_STATUS_POLL_INTERVAL_MS).toISOString() : ''),
                session: resetStaleAaSession ? null : prev.session
            }));

            return data;
        } catch (error) {
            console.error('Failed to refresh AA status:', error);
            return null;
        } finally {
            aaStatusRequestRef.current = false;
        }
    });
    const scheduleAaCompletionRefresh = useEffectEvent((completionKey) => {
        if (step !== 2) return;

        const normalizedKey = String(completionKey || '').trim();
        if (!normalizedKey) return;

        if (
            aaCompletionRefreshRef.current.key === normalizedKey &&
            (aaCompletionRefreshRef.current.attempts > 0 || aaCompletionRefreshRef.current.timerId)
        ) {
            return;
        }

        stopAaCompletionRefresh();
        aaCompletionRefreshRef.current = { timerId: null, attempts: 0, key: normalizedKey };

        const runRefreshAttempt = async () => {
            if (step !== 2) {
                stopAaCompletionRefresh();
                return;
            }

            const nextAttempts = aaCompletionRefreshRef.current.attempts + 1;
            aaCompletionRefreshRef.current = {
                ...aaCompletionRefreshRef.current,
                attempts: nextAttempts,
                timerId: null
            };

            try {
                await refreshAaStatus();
            } catch (refreshError) {
                console.error('AA data refresh failed after consent update:', refreshError);
            }

            if (nextAttempts >= AA_STATUS_SETTLE_ATTEMPTS || step !== 2) {
                aaCompletionRefreshRef.current = {
                    timerId: null,
                    attempts: nextAttempts,
                    key: normalizedKey
                };
                return;
            }

            aaCompletionRefreshRef.current = {
                ...aaCompletionRefreshRef.current,
                timerId: window.setTimeout(() => {
                    runRefreshAttempt();
                }, AA_STATUS_POLL_INTERVAL_MS)
            };
        };

        runRefreshAttempt();
    });
    // Step-1 data fetch from backend (user + PAN details + latest eligibility)
    useEffect(() => {
        if (step !== 1) {
            profileStepFetchRef.current = false;
            return;
        }
        if (profileStepFetchRef.current) return;
        profileStepFetchRef.current = true;
        refreshUserData({ withLoading: true });
    }, [refreshUserData, step]);

    useEffect(() => {
        if (!isProgressHydrated) return;
        if (step === 1) return;
        refreshUserData({ withLoading: false });
    }, [isProgressHydrated, step, refreshUserData]);

    useEffect(() => {
        if (step !== 2) {
            bankStepSyncRef.current = false;
            return;
        }
        if (bankStepSyncRef.current) {
            return;
        }
        bankStepSyncRef.current = true;
        refreshUserData({ withLoading: false });
    }, [step, refreshUserData]);

    useEffect(() => {
        if (step !== 2) {
            aaVerifiedSyncRef.current = '';
            clearMoneyoneFetchRetry();
            return;
        }

        if (!hasStrictAaVerified) {
            aaVerifiedSyncRef.current = '';
            return;
        }

        const verificationKey = [
            fetchedData?.aaStatus?.consentId || '',
            fetchedData?.camsProfile?.createdAt || ''
        ].join('|');

        if (aaVerifiedSyncRef.current === verificationKey) {
            return;
        }

        aaVerifiedSyncRef.current = verificationKey;
        refreshUserData({ withLoading: false });
    }, [
        step,
        hasStrictAaVerified,
        fetchedData?.aaStatus?.consentId,
        fetchedData?.camsProfile?.createdAt,
        refreshUserData,
        clearMoneyoneFetchRetry
    ]);

    useEffect(() => {
        if (step !== 2) {
            return undefined;
        }

        const syncBankStepState = () => {
            refreshUserData({ withLoading: false });
        };

        const handleVisibilityChange = () => {
            if (document.hidden) return;
            syncBankStepState();
        };

        window.addEventListener('focus', syncBankStepState);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', syncBankStepState);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [step, refreshUserData]);

    useEffect(() => {
        if (step !== 2) {
            stopAaCompletionRefresh();
            stopAaStatusPolling();
            aaStatusRequestRef.current = false;
            return;
        }

        const aaStatusCode = String(fetchedData?.aaStatus?.status || '').trim().toUpperCase();
        const aaPhaseCode = String(fetchedData?.aaStatus?.phase || '').trim().toUpperCase();
        const hasBackendPendingAaState = ['PENDING'].includes(aaStatusCode)
            || ['PENDING', 'CONSENT_ACTIVE', 'DATA_PENDING'].includes(aaPhaseCode);
        const shouldPollAaStatus = Boolean(
            moneyOneState.consentId ||
            hasBackendPendingAaState
        );

        if (!shouldPollAaStatus || hasStrictAaVerified) {
            stopAaStatusPolling();
            return;
        }

        refreshAaStatus();
        stopAaStatusPolling();
        aaStatusPollRef.current = window.setInterval(() => {
            refreshAaStatus();
        }, AA_STATUS_POLL_INTERVAL_MS);

        return () => {
            stopAaStatusPolling();
        };
    }, [
        step,
        moneyOneState.consentId,
        fetchedData?.aaStatus?.status,
        fetchedData?.aaStatus?.phase,
        hasStrictAaVerified,
        refreshAaStatus,
        stopAaCompletionRefresh,
        stopAaStatusPolling
    ]);

    useEffect(() => () => {
        if (digilockerPollRef.current) {
            window.clearInterval(digilockerPollRef.current);
            digilockerPollRef.current = null;
        }
        if (digilockerPopupCloseTimerRef.current) {
            window.clearTimeout(digilockerPopupCloseTimerRef.current);
            digilockerPopupCloseTimerRef.current = null;
        }
        digilockerPollingRequestRef.current = '';
        if (digilockerPopupRef.current && !digilockerPopupRef.current.closed) {
            digilockerPopupRef.current.close();
        }
        digilockerPopupRef.current = null;
        setDigilockerModal({ open: false, url: '' });

        if (moneyOnePollRef.current) {
            window.clearInterval(moneyOnePollRef.current);
            moneyOnePollRef.current = null;
        }
        if (aaStatusPollRef.current) {
            window.clearInterval(aaStatusPollRef.current);
            aaStatusPollRef.current = null;
        }
        moneyOnePollingConsentRef.current = '';
        moneyOneStatusRequestRef.current = { inFlight: false, consentHandle: '' };
        moneyOnePollMetaRef.current = { attempts: 0 };
        if (aaCompletionRefreshRef.current.timerId) {
            window.clearTimeout(aaCompletionRefreshRef.current.timerId);
        }
        if (moneyOneFetchRetryRef.current) {
            window.clearTimeout(moneyOneFetchRetryRef.current);
        }
        aaCompletionRefreshRef.current = { timerId: null, attempts: 0, key: '' };
        aaStatusRequestRef.current = false;
    }, []);

    useEffect(() => {
        let session = null;
        try {
            const raw = sessionStorage.getItem(DIGILOCKER_SESSION_STORAGE_KEY);
            session = raw ? JSON.parse(raw) : null;
        } catch (_error) {
            session = null;
        }
        if (!session?.requestId) return;

        setDigilockerState((prev) => ({
            ...prev,
            url: session.url || prev.url,
            requestId: session.requestId,
            polling: false
        }));
    }, []);



    const pollDigilockerStatus = useEffectEvent(async (requestId, { silent = false } = {}) => {
        if (!requestId) return false;
        if (digilockerTerminalRequestRef.current === requestId) return false;
        const pollingStartedAt = Number(digilockerPollingStartedAtRef.current || 0);
        if (pollingStartedAt && (Date.now() - pollingStartedAt) >= DIGILOCKER_MAX_POLL_DURATION_MS) {
            digilockerTerminalRequestRef.current = requestId;
            stopDigilockerPolling({ closePopup: true, clearSession: true });
            if (!silent) {
                showPopup('Digilocker verification is taking longer than expected. Please complete Aadhaar approval and try again if needed.', 'error');
            }
            return false;
        }
        const lastPollAt = readDigilockerPollLock(requestId);
        if (lastPollAt && (Date.now() - lastPollAt) < DIGILOCKER_MIN_POLL_GAP_MS) {
            return false;
        }
        if (
            digilockerStatusRequestRef.current.inFlight &&
            digilockerStatusRequestRef.current.requestId === requestId
        ) {
            return false;
        }

        writeDigilockerPollLock(requestId);
        digilockerStatusRequestRef.current = { inFlight: true, requestId };

        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/digilocker/status`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    requestId,
                    userId: storedUser.id || null,
                    mobile: storedUser.mobile_number || fetchedData?.mobile || '',
                    email: verificationData.officialEmail || ''
                })
            });
            const data = await response.json();

            if (!response.ok) {
                if (response.status === 404) {
                    digilockerTerminalRequestRef.current = requestId;
                    setVerificationData((prev) => ({
                        ...prev,
                        aadhaarRequestId: prev.aadhaarRequestId === requestId ? '' : prev.aadhaarRequestId
                    }));
                    stopDigilockerPolling({ closePopup: true, clearSession: true });
                    setDigilockerState((prev) => ({ ...prev, requestId: '', url: '', polling: false }));
                    if (!silent) {
                        showPopup('Digilocker session was reset. Please start Aadhaar verification again.', 'info');
                    }
                    return false;
                }
                const isCreditsExhausted = response.status === 403 && /no remaining api credits/i.test(String(data.message || ''));
                const isAadhaarIdentityBlocked = response.status === 409 && ['AADHAAR_PAN_MISMATCH', 'AADHAAR_ALREADY_USED'].includes(data.code);
                if (isAadhaarIdentityBlocked) {
                    digilockerTerminalRequestRef.current = requestId;
                    setVerificationData((prev) => ({
                        ...prev,
                        aadhaarRequestId: prev.aadhaarRequestId === requestId ? '' : prev.aadhaarRequestId
                    }));
                    stopDigilockerPolling({ closePopup: true, clearSession: true });
                    setDigilockerState((prev) => ({ ...prev, requestId: '', url: '', polling: false }));
                    showPopup(data.message, 'error');
                    return false;
                }
                if (isCreditsExhausted) {
                    digilockerTerminalRequestRef.current = requestId;
                    stopDigilockerPolling({
                        closePopup: true,
                        clearSession: true
                    });
                    if (!silent) {
                        showPopup(data.message || "Unable to check Digilocker authorization status.", "error");
                    }
                    return false;
                }
                
                // Do NOT stop polling for 5xx or temporary errors. Signzy might be processing the XML.
                console.warn(`Temporary Digilocker error (HTTP ${response.status}):`, data.message || "Unknown error");
                setDigilockerState((prev) => ({ ...prev, polling: true }));
                return false;
            }

            if (data.success && data.authorized && isAadhaarDetailsVerified(data.aadhaar)) {
                digilockerTerminalRequestRef.current = requestId;
                if (digilockerPollRef.current) {
                    window.clearInterval(digilockerPollRef.current);
                    digilockerPollRef.current = null;
                }
                digilockerPollingRequestRef.current = '';
                digilockerStatusRequestRef.current = { inFlight: false, requestId: '' };
                clearStoredDigilockerSession();
                clearDigilockerPollLock(requestId);
                applyFetchedAadhaarDetails(data.aadhaar);
                setDigilockerState((prev) => ({ ...prev, polling: false, requestId, url: prev.url || '' }));
                closeDigilockerPopupWindow();
                scheduleDigilockerPopupClose(500);
                showPopup(`e-Aadhaar fetched for ${getLocalizedName(data.aadhaar.name, language) || data.aadhaar.name}`, "success");
                return true;
            }

            if (data.success && data.authorized && !isAadhaarDetailsVerified(data.aadhaar)) {
                console.warn("Digilocker returned authorized but missing Aadhaar details:", data);
                setDigilockerState((prev) => ({ ...prev, polling: false }));
                showPopup("e-Aadhaar details could not be retrieved. Please try verifying again.", "error");
                return false;
            }

            setDigilockerState((prev) => ({ ...prev, polling: Boolean(data.pending), requestId }));
            return false;
        } catch (error) {
            console.error("Digilocker status polling failed (network error):", error);
            // Do NOT stop polling on network error, allow it to retry
            setDigilockerState((prev) => ({ ...prev, polling: true }));
            return false;
        } finally {
            if (digilockerStatusRequestRef.current.requestId === requestId) {
                digilockerStatusRequestRef.current = { inFlight: false, requestId: '' };
            }
        }
    });

    useEffect(() => {
        const handleDigilockerCloseMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event?.data?.type !== 'DIGILOCKER_CLOSE') return;
            closeDigilockerPopupWindow();
            const activeRequestId = digilockerPollingRequestRef.current;
            if (activeRequestId) {
                // Immediately check status once when window is closed, slightly delayed to let backend settle
                window.setTimeout(() => pollDigilockerStatus(activeRequestId, { silent: true }), 1000);
            }
        };

        window.addEventListener('message', handleDigilockerCloseMessage);
        return () => window.removeEventListener('message', handleDigilockerCloseMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (step !== 4 || !verificationData.aadhaarRequestId || isAadhaarVerified) return;
        if (digilockerTerminalRequestRef.current === verificationData.aadhaarRequestId) return;
        if (
            digilockerPollingRequestRef.current === verificationData.aadhaarRequestId &&
            digilockerPollRef.current
        ) {
            return;
        }
        setDigilockerState((prev) => ({ ...prev, polling: true, requestId: verificationData.aadhaarRequestId }));
        digilockerPollingRequestRef.current = verificationData.aadhaarRequestId;
        if (!digilockerPollingStartedAtRef.current) {
            digilockerPollingStartedAtRef.current = Date.now();
        }
        pollDigilockerStatus(verificationData.aadhaarRequestId, { silent: true });
        if (digilockerPollRef.current) {
            window.clearInterval(digilockerPollRef.current);
        }
        digilockerPollRef.current = window.setInterval(() => {
            pollDigilockerStatus(verificationData.aadhaarRequestId, { silent: true });
        }, DIGILOCKER_POLL_INTERVAL_MS);
        return () => {
            if (digilockerPollRef.current) {
                window.clearInterval(digilockerPollRef.current);
                digilockerPollRef.current = null;
            }
            digilockerPollingRequestRef.current = '';
        };
    }, [step, verificationData.aadhaarRequestId, isAadhaarVerified, pollDigilockerStatus]);

    const pollMoneyoneSession = useEffectEvent(async (consentHandle) => {
        if (!consentHandle) return null;
        if (
            moneyOneStatusRequestRef.current.inFlight &&
            moneyOneStatusRequestRef.current.consentHandle === consentHandle
        ) {
            return null;
        }

        moneyOneStatusRequestRef.current = { inFlight: true, consentHandle };

        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(
                `${baseUrl}/api/moneyone/session-status?consentHandle=${encodeURIComponent(consentHandle)}&_ts=${Date.now()}`,
                {
                    method: 'GET',
                    cache: 'no-store',
                    headers: buildAuthHeaders({
                        'Cache-Control': 'no-cache'
                    })
                }
            );
            const data = await response.json();

            if (!response.ok || !data.success || !data.session) {
                return null;
            }

            const session = data.session;
            const nextStatus = String(session.last_event_status || session.status || '').trim();
            const nextConsentId = String(session.consent_id || '').trim();
            const isTerminalStatus = ['DATA_READY', 'FI_DATA_READY', 'DATA_DENIED', 'SESSION_FAILED', 'SESSION_EXPIRED', 'CONSENT_REJECTED', 'CONSENT_REVOKED', 'CONSENT_EXPIRED'].includes(nextStatus);
            const isStillPending = !nextConsentId && (!nextStatus || nextStatus === 'PENDING' || nextStatus === 'CONSENT' || nextStatus === 'CONSENT_PENDING');

            if (isStillPending) {
                moneyOnePollMetaRef.current.attempts += 1;
            } else {
                moneyOnePollMetaRef.current.attempts = 0;
            }

            const exceededPendingWindow = isStillPending && moneyOnePollMetaRef.current.attempts >= MONEYONE_MAX_POLL_ATTEMPTS;
            const shouldStopPolling = isTerminalStatus || Boolean(nextConsentId) || exceededPendingWindow;
            const shouldScheduleAaRefresh = step === 2 && (isTerminalStatus || Boolean(nextConsentId));
            const nowIso = new Date().toISOString();
            const nextCheckIso = shouldStopPolling ? '' : new Date(Date.now() + MONEYONE_POLL_INTERVAL_MS).toISOString();

            setMoneyOneState((prev) => ({
                ...prev,
                session,
                consentId: nextConsentId || prev.consentId,
                status: exceededPendingWindow ? 'WAITING_FOR_CONSENT' : (nextStatus || prev.status),
                polling: shouldStopPolling ? false : prev.polling,
                consentStartedAt: prev.consentStartedAt || nowIso,
                lastStatusCheckAt: nowIso,
                nextStatusCheckAt: nextCheckIso
            }));

            if (shouldStopPolling) {
                stopMoneyonePolling();
                if (shouldScheduleAaRefresh) {
                    scheduleAaCompletionRefresh(nextConsentId || `${consentHandle}:${nextStatus || 'completed'}`);
                }
            }

            if (nextConsentId && step === 2 && !moneyOneModal.open) {
                setMoneyOneState((prev) => prev.consentWindowOpen ? { ...prev, consentWindowOpen: false } : prev);
                setAaWaiting((prev) => prev.active ? prev : { active: true, countdown: 60, timedOut: false });
            }

            return session;
        } catch (error) {
            console.error('Consent session polling failed:', error);
            return null;
        } finally {
            if (moneyOneStatusRequestRef.current.consentHandle === consentHandle) {
                moneyOneStatusRequestRef.current = { inFlight: false, consentHandle: '' };
            }
        }
    });

    const handleMoneyoneConsent = async () => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const mobileNumber = String(storedUser.mobile_number || fetchedData?.mobile || '').trim();
        const panNumber = String(storedUser.pan_number || fetchedData?.pan || '').trim().toUpperCase();
        const aaHandle = String(moneyOneConfig.aaHandle || DEFAULT_MONEYONE_AA_HANDLE).trim() || DEFAULT_MONEYONE_AA_HANDLE;
        const useKioskJourneyWindow = isKioskBridgeAvailable();

        if (hasFreshAaProfile && !aaRestartRef.current.active) {
            showPopup("Today's Account Aggregator details are already available. Bank AA is verified.", "success");
            return;
        }

        if (!/^\d{10}$/.test(mobileNumber)) {
            showPopup("A valid mobile number is required for salary account consent.", "error");
            return;
        }

        setMoneyOneState((prev) => ({
            ...prev,
            connecting: true,
            status: '',
            polling: false,
            consentWindowOpen: false,
            accountFetchComplete: false,
            accountFetchBlocked: false,
            consentStartedAt: prev.consentStartedAt || new Date().toISOString(),
            lastStatusCheckAt: '',
            nextStatusCheckAt: '',
            accounts: [],
            selectedLinkRefNumber: '',
            selectedAccount: null
        }));
        moneyOneAutoFetchRef.current = '';

        try {
            const baseUrl = getApiBaseUrl();
            const requestBody = JSON.stringify({
                userId: storedUser.id || null,
                mobile: mobileNumber,
                email: storedUser.email || verificationData.officialEmail || '',
                vua: `${mobileNumber}@${aaHandle}`,
                partyIdentifierType: 'MOBILE',
                partyIdentifierValue: mobileNumber,
                accountID: `USER-${storedUser.id || mobileNumber}`,
                pan: panNumber || undefined,
                fetchType: 'PERIODIC',
                consent: 'Y',
                months: 12,
                frequencyUnit: 'MONTH',
                frequencyValue: 1,
                redirectUrl: `${window.location.origin}/apply-loan`
            });

            const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
            let timeoutId = null;
            const fetchPromise = fetch(`${baseUrl}/api/moneyone/consent`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                signal: controller?.signal,
                body: requestBody
            });
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = window.setTimeout(() => {
                    if (controller) controller.abort();
                    const timeoutSeconds = Math.max(1, Math.round(MONEYONE_CONSENT_REQUEST_TIMEOUT_MS / 1000));
                    const timeoutError = new Error(`Consent request timed out after ${timeoutSeconds} seconds.`);
                    timeoutError.name = 'TimeoutError';
                    reject(timeoutError);
                }, MONEYONE_CONSENT_REQUEST_TIMEOUT_MS);
            });

            const response = await Promise.race([fetchPromise, timeoutPromise]);
            if (timeoutId) window.clearTimeout(timeoutId);
            const data = await response.json();

            if (!response.ok || !data.success) {
                showPopup("AA consent could not be started right now. Please try again.", "warning");
                setMoneyOneState((prev) => ({ ...prev, connecting: false }));
                return;
            }

            const consentHandle = String(data?.consent?.consentHandle || '').trim();
            const redirectUrl = String(data?.consent?.redirectUrl || '').trim();

            setMoneyOneState((prev) => ({
                ...prev,
                connecting: false,
                consentHandle,
                redirectUrl,
                status: String(data?.consent?.status || 'PENDING').trim(),
                polling: false,
                consentWindowOpen: !useKioskJourneyWindow && Boolean(redirectUrl),
                consentStartedAt: prev.consentStartedAt || new Date().toISOString(),
                lastStatusCheckAt: '',
                nextStatusCheckAt: ''
            }));

            if (redirectUrl) {
                if (useKioskJourneyWindow) {
                    const launchedInKiosk = await launchExternalJourneyInKioskWrapper({
                        url: redirectUrl,
                        title: 'Validate Salary Account',
                        width: 520,
                        height: 760
                    });
                    if (!launchedInKiosk?.ok) {
                        showPopup("CAMS validation could not open in kiosk mode. Please restart the kiosk app and try again.", "error");
                    } else {
                        showPopup("CAMS validation opened in a separate window. Complete it there and we will track the status automatically here.", "info");
                    }
                } else {
                    setMoneyOneModal({ open: true, url: redirectUrl });
                    showPopup("Complete the CAMS consent in this window. We will keep checking the status automatically.", "info");
                }
            }

            if (consentHandle) {
                stopAaCompletionRefresh();
                if (moneyOnePollRef.current) {
                    window.clearInterval(moneyOnePollRef.current);
                    moneyOnePollRef.current = null;
                }
                moneyOnePollingConsentRef.current = consentHandle;
                moneyOneStatusRequestRef.current = { inFlight: false, consentHandle: '' };
                moneyOnePollMetaRef.current = { attempts: 0 };
                moneyOnePollRef.current = window.setInterval(() => {
                    if (moneyOnePollingConsentRef.current !== consentHandle) return;
                    setMoneyOneState((prev) => ({
                        ...prev,
                        nextStatusCheckAt: new Date(Date.now() + MONEYONE_POLL_INTERVAL_MS).toISOString()
                    }));
                    pollMoneyoneSession(consentHandle);
                }, MONEYONE_POLL_INTERVAL_MS);
                pollMoneyoneSession(consentHandle);
            }
        } catch (error) {
            console.error('Salary account consent creation failed:', error);
                showPopup(
                    error?.name === 'AbortError' || error?.name === 'TimeoutError'
                    ? `Consent request timed out after ${Math.max(1, Math.round(MONEYONE_CONSENT_REQUEST_TIMEOUT_MS / 1000))} seconds. Please try again.`
                    : "Unable to start salary account validation right now. Please try again.",
                "error"
            );
            setMoneyOneState((prev) => ({ ...prev, connecting: false }));
        }
    };

    const handleMoneyoneFetchAccounts = async () => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const consentId = String(moneyOneState.consentId || '').trim();
        const scheduleAaDataRetry = () => {
            setMoneyOneState((prev) => ({
                ...prev,
                fetchingData: false,
                accountFetchComplete: false,
                accountFetchBlocked: false,
                status: prev.status || 'DATA_PENDING',
                polling: Boolean(prev.polling || prev.consentId),
                consentWindowOpen: Boolean(prev.consentWindowOpen),
                accounts: [],
                selectedLinkRefNumber: '',
                selectedAccount: null
            }));
            scheduleMoneyoneFetchRetry(() => {
                if (step === 2) {
                    handleMoneyoneFetchAccountsEvent();
                }
            });
        };

        if (!consentId) {
            logoutAfterSessionEnd("We are unable to approve the offer right now. Our team will reach out to you soon.", 4000, 'info', false);
            return;
        }

        setMoneyOneState((prev) => ({ ...prev, fetchingData: true }));
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/moneyone/fi-data`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    userId: storedUser.id || null,
                    mobile: storedUser.mobile_number || fetchedData?.mobile || '',
                    email: storedUser.email || verificationData.officialEmail || '',
                    consentId,
                    panNumber: storedUser.pan_number || fetchedData?.pan || ''
                })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                scheduleAaDataRetry();
                return;
            }

            const accounts = normalizeMoneyoneAccounts(data.data || {});
            setMoneyOneState((prev) => ({
                ...prev,
                fetchingData: false,
                accounts,
                accountFetchComplete: true,
                accountFetchBlocked: false
            }));
            refreshAaStatus();

            if (!accounts.length) {
                const pendingAaData = isAaBankDataPending({
                    aaStatus: fetchedData?.aaStatus,
                    moneyOneState: { ...moneyOneState, fetchingData: true },
                    hasStrictAaVerified
                });

                if (pendingAaData) {
                    scheduleAaDataRetry();
                    return;
                }

                console.warn('No accounts were returned for this consent.', {
                    userId: storedUser.id || null,
                    mobile: storedUser.mobile_number || fetchedData?.mobile || '',
                    consentId,
                    step
                });
                scheduleAaDataRetry();
                return;
            }

            showPopup("Linked accounts fetched successfully.", "success");
            window.setTimeout(() => closePopup(true), 2000);
        } catch (error) {
            console.error('Account fetch failed:', error);
            scheduleAaDataRetry();
        }
    };
    const handleMoneyoneFetchAccountsEvent = useEffectEvent(() => {
        handleMoneyoneFetchAccounts();
    });

    const reopenMoneyoneConsent = () => {
        if (!moneyOneState.redirectUrl) {
            showPopup("Consent link is not available right now. Please start the validation again.", "error");
            return;
        }
        if (isKioskBridgeAvailable()) {
            launchExternalJourneyInKioskWrapper({
                url: moneyOneState.redirectUrl,
                title: 'Validate Salary Account',
                width: 520,
                height: 760
            }).catch((error) => {
                console.error('Failed to reopen CAMS consent in kiosk mode:', error);
                showPopup("Unable to reopen the CAMS verification window right now.", "error");
            });
        } else {
            setMoneyOneModal({ open: true, url: moneyOneState.redirectUrl });
        }
        setMoneyOneState((prev) => ({
            ...prev,
            consentWindowOpen: true
        }));
    };

    useEffect(() => {
        if (step !== 2) return;
        if (!moneyOneState.consentId) {
            moneyOneAutoFetchRef.current = '';
            return;
        }
        if (hasStrictAaVerified || moneyOneState.fetchingData || moneyOneState.accounts.length > 0 || moneyOneState.accountFetchComplete) {
            return;
        }
        if (moneyOneAutoFetchRef.current === moneyOneState.consentId) {
            return;
        }

        moneyOneAutoFetchRef.current = moneyOneState.consentId;
        handleMoneyoneFetchAccountsEvent();
    }, [
        step,
        moneyOneState.consentId,
        moneyOneState.fetchingData,
        moneyOneState.accounts.length,
        hasStrictAaVerified,
        moneyOneState.accountFetchComplete,
        handleMoneyoneFetchAccountsEvent
    ]);

    useEffect(() => {
        if (step !== 2) return;
        setBankDetails((prev) => ({
            ...prev,
            accountNumber: ''
        }));
    }, [step]);

    const stopCameraStream = () => {
        if (cameraRetryTimeoutRef.current) {
            window.clearTimeout(cameraRetryTimeoutRef.current);
            cameraRetryTimeoutRef.current = null;
        }
        const stream = videoRef.current?.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
            stream.getTracks().forEach(track => track.stop());
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraReady(false);
    };

    // Camera Access
    useEffect(() => {
        const startCamera = async () => {
            if (!videoRef.current) {
                cameraRetryTimeoutRef.current = window.setTimeout(() => {
                    if (step === 5 && !livePhoto) {
                        startCamera();
                    }
                }, 200);
                return;
            }
            if (!window.isSecureContext) {
                showPopup("Camera requires HTTPS (or localhost). Please open the app in a secure context.", "error");
                return;
            }
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                showPopup("This browser does not support camera access.", "error");
                return;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user' },
                    audio: false
                });
                if (!videoRef.current) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                setCameraReady(true);
            } catch (err) {
                console.error("Camera Error:", err);
                const msg = err?.name === 'NotAllowedError'
                    ? "Camera permission denied. Please allow camera access and retry."
                    : err?.name === 'NotFoundError'
                        ? "No camera found on this device."
                        : "Unable to access camera. Please retry.";
                showPopup(msg, "error");
                stopCameraStream();
            }
        };

        if (step === 5 && !livePhoto) {
            startCamera();
        } else {
            stopCameraStream();
        }

        return () => {
            stopCameraStream();
        };
    }, [step, livePhoto]);


    // Real penny drop at Step 7 before disbursement
    useEffect(() => {
        if (step !== 7 || pennyDropStatus.started || pennyDropStatus.completed) return;
        if (isDemoDisbursalUser) {
            setPennyDropStatus({
                started: true,
                completed: true,
                message: 'Demo account does not require penny drop.'
            });
            setStep(8);
            return;
        }
        if (!hasRenderableBankJourneyDetails(loanJourney, fetchedData, savedBankSuggestion, bankDetails)) {
            setPennyDropStatus({ started: false, completed: false, message: 'Bank details are missing.' });
            showPopup('Bank details are missing or not fully loaded. Please refresh the journey and continue again.', 'error');
            return;
        }

        const runPennyDrop = async () => {
            if (!validateEnteredAccountAgainstCamsEvent()) return;
            setPennyDropStatus({ started: true, completed: false, message: 'Initiating penny drop...' });
            setLoading(true);
            try {
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const resolvedJourneyBankDetails = resolveLoanJourneyBankDetails(loanJourney, { ...fetchedData, savedBankSuggestion }, bankDetails);
                const beneficiaryAccount = String(bankDetails.accountNumber || resolvedJourneyBankDetails.accountNumber || '').trim();
                const beneficiaryIFSC = String(bankDetails.ifscCode || resolvedJourneyBankDetails.ifscCode || '').trim().toUpperCase();
                const resolvedIdentity = getResolvedVerifiedIdentity({
                    fetchedData,
                    storedUserSnapshot: storedUser,
                    aadhaarDetails: aadhaarFetchState.details
                });
                const baseUrl = getApiBaseUrl();
                const response = await fetch(`${baseUrl}/api/penny-drop`, {
                    method: 'POST',
                    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({
                        beneficiaryAccount,
                        beneficiaryIFSC,
                        beneficiaryMobile: resolvedIdentity.mobile,
                        beneficiaryName: resolvedIdentity.name,
                        email: storedUser.email || '',
                        panNumber: resolvedIdentity.pan
                    })
                });
                const data = await response.json();
                if (!response.ok || !data.success) {
                    setPennyDropStatus({ started: true, completed: false, message: data.message || 'Penny drop failed.' });
                    showPopup(data.message || "Penny drop failed. Please try again.", "error");
                    return;
                }

                setPennyDropStatus({ started: true, completed: true, message: data.message || 'Penny drop completed successfully.' });
                setStep(8);
            } catch (error) {
                console.error("Penny drop failed:", error);
                setPennyDropStatus({ started: true, completed: false, message: 'Penny drop failed.' });
                showPopup("Unable to complete penny drop right now. Please try again.", "error");
            } finally {
                setLoading(false);
            }
        };

        const startTimer = window.requestAnimationFrame(() => {
            runPennyDrop();
        });

        return () => {
            window.cancelAnimationFrame(startTimer);
        };
    }, [step, pennyDropStatus.started, pennyDropStatus.completed, bankDetails, bankDetails.accountNumber, bankDetails.ifscCode, fetchedData, aadhaarFetchState.details, expectedAccountLast4, validateEnteredAccountAgainstCamsEvent, isDemoDisbursalUser, loanJourney, savedBankSuggestion]);

    // Celebration effect for final step
    useEffect(() => {
        if (step === 9) {
            // Generate confetti
            const colors = ['#f59e0b', '#fb923c', '#ef4444', '#ec4899', '#f43f5e', '#8b5cf6'];
            const pieces = Array.from({ length: 60 }).map((_, i) => ({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * 3,
                duration: 2 + Math.random() * 3,
                color: colors[Math.floor(Math.random() * colors.length)],
                rotate: Math.random() * 360
            }));
            setConfetti(pieces);

            // Auto logout after 6 seconds
            const timer = setTimeout(() => {
                navigate('/');
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [step, navigate]);

    useEffect(() => () => {
        if (salaryNotFoundLogoutTimerRef.current) {
            window.clearTimeout(salaryNotFoundLogoutTimerRef.current);
            salaryNotFoundLogoutTimerRef.current = null;
        }
    }, []);

    const handleProceedFromDetails = async () => {
        const activeLoan = await fetchActiveRepaymentLoan();
        if (activeLoan) {
            const dueAmount = Number(activeLoan.dueAmount || 0);
            const baseRepaymentDue = Number(activeLoan.baseRepaymentDue || Math.max(0, Number(activeLoan.totalRepaymentAmount || 0) - Number(activeLoan.totalPaid || 0)));
            const collectionDueAmount = Number(activeLoan.collectionDueAmount || dueAmount);
            const penaltyLateInterest = Number(activeLoan.penaltyLateInterest || Math.max(0, collectionDueAmount - baseRepaymentDue));
            showPopup(
                dueAmount > 0
                    ? `You already have a running loan with ₹${baseRepaymentDue.toLocaleString('en-IN')} base repayment due, ₹${collectionDueAmount.toLocaleString('en-IN')} payable now, and ₹${penaltyLateInterest.toLocaleString('en-IN')} late interest/penalty. Please complete repayment before applying again.`
                    : "You already have a running loan. Please complete repayment before applying again.",
                "error"
            );
            window.setTimeout(() => {
                navigate('/dashboard', { replace: true, state: { tab: 'repayment' } });
            }, 800);
            return;
        }
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        clearLoanApplicationProgress({
            id: storedUser.id || null,
            mobile: storedUser.mobile_number || fetchedData?.mobile || '',
            panNumber: storedUser.pan_number || fetchedData?.pan || ''
        }).catch((error) => {
            console.error('Failed to clear stale journey progress before new application:', error);
        });
        if (approvedLimitAmount <= 0) {
            setSelectedAmount(0);
            setSelectedTenure(clampLoanTenureDays(MAX_LOAN_TENURE_DAYS));
            setStep(2);
            return;
        }
        setSelectedAmount(requestedAmount > 0 ? requestedAmount : approvedOfferAmount);
        setSelectedTenure(clampLoanTenureDays(MAX_LOAN_TENURE_DAYS));
        setStep(2);
    };

    const handleBankDetailsSubmit = async (e) => {
        e.preventDefault();
        if (!isAaDecisionReady) {
            showPopup(AA_DECISION_FETCH_LOADER_MESSAGE, "loading");
            return;
        }
        setBankDetailsError('');
        setAccountNumberError('');
        setIfscCodeError('');
        const resolvedJourneyBankDetails = resolveLoanJourneyBankDetails(loanJourney, { ...fetchedData, savedBankSuggestion });
        const savedIfscCode = String(resolvedJourneyBankDetails.ifscCode || '').trim().toUpperCase();
        const accountNumber = String(bankDetails.accountNumber || '').trim();
        const ifscCode = String(bankDetails.ifscCode || '').trim().toUpperCase();
        const resolvedIdentity = getResolvedVerifiedIdentity({
            fetchedData,
            storedUserSnapshot,
            aadhaarDetails: aadhaarFetchState.details
        });
        const beneficiaryMobile = resolvedIdentity.mobile;
        const beneficiaryPan = resolvedIdentity.pan;
        const beneficiaryName = resolvedIdentity.name;

        if (!accountNumber && !ifscCode) {
            setBankDetailsError("Please enter salaried account number and IFSC code.");
            return;
        }

        if (!accountNumber) {
            setAccountNumberError("Please enter your account number.");
            return;
        }

        if (!ifscCode) {
            setBankDetailsError("Please enter your IFSC code.");
            return;
        }

        if (!IFSC_CODE_PATTERN.test(ifscCode)) {
            setIfscCodeError("Please enter your correct IFSC code.");
            return;
        }

        if (savedIfscCode && ifscCode !== savedIfscCode) {
            setIfscCodeError("Please enter your correct IFSC code.");
            return;
        }

        if (accountNumber.length < 10 || accountNumber.length > 20) {
            setBankDetailsError("Please enter a valid salaried account number of up to 20 digits.");
            return;
        }
        if (
            isPlaceholderIdentityValue(beneficiaryMobile)
            || !/^\d{10}$/.test(beneficiaryMobile)
            || isPlaceholderIdentityValue(beneficiaryPan)
            || beneficiaryPan.length !== 10
            || isPlaceholderIdentityValue(beneficiaryName)
        ) {
            showPopup("We are still loading your verified profile. Please wait a moment and try again.", "info");
            return;
        }
        const freshUserData = await refreshUserData({ withLoading: false });
        const effectiveCurrentOffer = freshUserData?.currentOffer || fetchedData?.currentOffer || null;
        const effectiveCurrentOfferAmount = Number(
            effectiveCurrentOffer?.offeredAmount
            || effectiveCurrentOffer?.amount
            || 0
        );
        const effectiveBackendApprovedOffer = Boolean(
            effectiveCurrentOffer?.isEligible
            || effectiveCurrentOffer?.approved
        ) && effectiveCurrentOfferAmount > 0;
        const pendingAaData = isAaBankDataPending({
            aaStatus: freshUserData?.aaStatus || fetchedData?.aaStatus,
            moneyOneState,
            hasVerifiedAaData
        });
        const shouldHoldForAaData = pendingAaData && (
            moneyOneState.accountFetchBlocked
            || moneyOneState.accountFetchComplete
            || !moneyOneState.accounts.length
        );
        console.log('[LOAN_FLOW_DEBUG] bank submit state', {
            approvedLimitAmount,
            hasBackendApprovedOffer,
            effectiveBackendApprovedOffer,
            pendingAaData,
            shouldHoldForAaData,
            accountFetchBlocked: moneyOneState.accountFetchBlocked,
            accountFetchComplete: moneyOneState.accountFetchComplete,
            accountsLength: moneyOneState.accounts.length,
            fetchedOfferStatus: fetchedData?.currentOffer?.decisionStatus || null,
            freshOfferStatus: freshUserData?.currentOffer?.decisionStatus || null
        });
        if (!effectiveBackendApprovedOffer && (shouldHoldForAaData || moneyOneState.accountFetchBlocked || (moneyOneState.accountFetchComplete && !moneyOneState.accounts.length))) {
            if (!pendingAaData) {
                logoutAfterSalaryNotFound("We are unable to approve the offer right now. Our team will reach out to you soon.");
                return;
            }

            clearMoneyoneFetchRetry();
            setMoneyOneState((prev) => ({
                ...prev,
                accountFetchBlocked: false,
                accountFetchComplete: false
            }));
            showPopup(AA_DECISION_FETCH_LOADER_MESSAGE, "loading");
            moneyOneFetchRetryRef.current = window.setTimeout(() => {
                if (step === 2) {
                    handleMoneyoneFetchAccountsEvent();
                }
            }, 1000);
            return;
        }
        if (!bankConsent) {
            showPopup("Please provide the consent to continue.", "error");
            return;
        }
        if (isDemoDisbursalUser) {
            const demoOfferAmount = Number(
                fetchedData?.currentOffer?.offeredAmount
                || fetchedData?.currentOffer?.amount
                || requestedAmountFromState
                || 50000
            );
            setSavedBankSuggestion({
                accountNumber,
                accountLast4: accountNumber.slice(-4),
                ifscCode,
                beneficiaryName: fetchedData?.name || storedUserSnapshot.full_name || '',
                verifiedAt: new Date().toISOString()
            });
            setSelectedAmount(demoOfferAmount);
            setBankVerificationCompleted(true);
            showPopup("Demo bank account verified successfully.", "success");
            setStep(3);
            return;
        }

        setLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/verify-bank-account`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    beneficiaryAccount: accountNumber,
                    beneficiaryIFSC: ifscCode,
                    beneficiaryMobile,
                    beneficiaryName,
                    email: String(storedUser.email || '').trim(),
                    panNumber: beneficiaryPan
                })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                if (data.requiresCrmApproval || data.reviewStatus === 'PENDING') {
                    setBankReviewPending({
                        isPending: true,
                        message: data.message || "Your bank account verification is under review by our admin team.",
                        accountHolderName: data.accountHolderName || '',
                        panHolderName: data.panHolderName || ''
                    });
                    showPopup(data.message || "Bank account is pending CRM approval.", "info");
                    return;
                }
                setBankReviewPending({ isPending: false, message: '', accountHolderName: '', panHolderName: '' });
                showPopup(data.message || "Bank account verification failed. Please check account number and IFSC.", "error");
                return;
            }

            setBankReviewPending({ isPending: false, message: '', accountHolderName: '', panHolderName: '' });

            setSavedBankSuggestion({
                accountNumber,
                accountLast4: accountNumber.slice(-4),
                ifscCode,
                beneficiaryName: fetchedData?.name || storedUser.full_name || '',
                verifiedAt: new Date().toISOString()
            });
            setBankVerificationCompleted(true);

            showPopup(AA_CRIF_LOADER_MESSAGE, "loading");
            if (AA_CRIF_LOADER_MIN_MS > 0) {
                await new Promise((resolve) => window.setTimeout(resolve, AA_CRIF_LOADER_MIN_MS));
            }
            const refreshDeadline = Date.now() + AA_OFFER_REFRESH_TOTAL_MS;
            let latestData = null;
            let refreshedOfferAmount = 0;
            let refreshedEligible = false;

            // Every read goes to the server (no throttle, no client cache): this loop exists to see
            // the decision change. It ends on the first final answer, approved or not; only a decision
            // still waiting on AA data keeps it going, up to the deadline.
            while (Date.now() < refreshDeadline) {
                latestData = await refreshUserData({ withLoading: false, fresh: true });
                refreshedOfferAmount = Number(latestData?.currentOffer?.offeredAmount || 0);
                refreshedEligible = Boolean(latestData?.currentOffer?.isEligible && refreshedOfferAmount > 0);

                if (refreshedEligible) {
                    break;
                }
                const polledOffer = latestData?.currentOffer || null;
                const polledRule = String(polledOffer?.ruleCode || '').trim().toUpperCase();
                const polledStatus = String(polledOffer?.decisionStatus || '').trim().toUpperCase();
                const decisionIsFinal = Boolean(polledOffer) && polledRule !== 'AA_PENDING' && polledStatus !== 'PENDING' && polledStatus !== '';
                if (decisionIsFinal) {
                    break;
                }

                const remainingMs = refreshDeadline - Date.now();
                if (remainingMs <= 0) break;
                await waitForMs(Math.min(AA_OFFER_REFRESH_INTERVAL_MS, remainingMs));
            }

            if (!refreshedEligible) {
                latestData = await refreshUserData({ withLoading: false, fresh: true });
                refreshedOfferAmount = Number(latestData?.currentOffer?.offeredAmount || 0);
                refreshedEligible = Boolean(latestData?.currentOffer?.isEligible && refreshedOfferAmount > 0);
            }

            const latestCurrentOffer = latestData?.currentOffer || null;
            const latestAaStatus = String(
                latestData?.aaStatus?.status
                || latestData?.aaStatus?.phase
                || fetchedData?.aaStatus?.status
                || fetchedData?.aaStatus?.phase
                || ''
            ).trim().toUpperCase();
            const latestOfferRule = String(latestCurrentOffer?.ruleCode || '').trim().toUpperCase();
            const latestOfferStatus = String(latestCurrentOffer?.decisionStatus || '').trim().toUpperCase();
            const refreshedSalaryAmount = Number(
                latestCurrentOffer?.salaryAmount
                || latestCurrentOffer?.monthlySalary
                || latestCurrentOffer?.salary_amount
                || 0
            );
            const cachedCurrentOffer = fetchedData?.currentOffer || null;
            const cachedCurrentOfferAmount = Number(
                cachedCurrentOffer?.offeredAmount
                || cachedCurrentOffer?.amount
                || 0
            );
            const cachedCurrentOfferApproved = Boolean(
                cachedCurrentOffer?.isEligible
                || cachedCurrentOffer?.approved
            ) && cachedCurrentOfferAmount > 0;
            console.log('[LOAN_FLOW_DEBUG] AA refresh result', {
                step,
                latestOfferStatus,
                latestOfferRule,
                refreshedEligible,
                refreshedSalaryAmount,
                cachedCurrentOfferApproved,
                cachedCurrentOfferAmount,
                latestCurrentOfferAmount: refreshedOfferAmount,
                aaStatus: latestAaStatus
            });
            if (cachedCurrentOfferApproved) {
                console.log('[LOAN_FLOW_DEBUG] Keeping cached approved offer and moving to next step');
                closePopup(true);
                setStep(3);
                return;
            }
            const isAaStillPending = isAaBankDataPending({
                aaStatus: latestData?.aaStatus || fetchedData?.aaStatus || null,
                moneyOneState,
                hasVerifiedAaData
            }) || (
                latestAaStatus
                && latestAaStatus !== 'VERIFIED'
                && latestAaStatus !== 'SUCCESS'
            ) || latestOfferRule === 'AA_PENDING'
                || latestOfferStatus === 'PENDING';
            if (isAaStillPending) {
                logoutAfterSessionEnd(AA_DATA_TIMEOUT_LOGOUT_MESSAGE, AA_DATA_TIMEOUT_LOGOUT_DELAY_MS, 'info', false);
                return;
            }
            if (!refreshedSalaryAmount) {
                console.log('[LOAN_FLOW_DEBUG] Logout path: salary missing in refreshed AA data', {
                    latestCurrentOffer
                });
                logoutAfterSalaryNotFound("We are unable to approve the offer right now. Our team will reach out to you soon.");
                return;
            }
            if (!refreshedEligible) {
                console.log('[LOAN_FLOW_DEBUG] Logout path: refreshed offer is not eligible', {
                    latestCurrentOffer
                });
                logoutAfterSessionEnd(
                    'We are unable to approve the offer right now. Our team will reach out to you soon.',
                    AA_DATA_TIMEOUT_LOGOUT_DELAY_MS,
                    'info',
                    false
                );
                setMoneyOneState((prev) => ({
                    ...prev,
                    consentHandle: '',
                    consentId: '',
                    redirectUrl: '',
                    status: 'FAILED',
                    polling: false,
                    consentWindowOpen: false,
                    accountFetchBlocked: true,
                    accountFetchComplete: true,
                    accounts: [],
                    selectedLinkRefNumber: '',
                    selectedAccount: null,
                    session: null
                }));
                return;
            }

            const nextStageAmount = refreshedOfferAmount > 0
                ? refreshedOfferAmount
                : (selectedAmount > 0 ? selectedAmount : (requestedAmount || DEFAULT_LOAN_PRINCIPAL_AMOUNT));

            setSelectedOffer(null);
            setSelectedAmount(nextStageAmount);
            closePopup(true);
            setStep(3);
        } catch (error) {
            console.error("Bank verification failed:", error);
            showPopup("Unable to verify bank account right now. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleLoanSelectionSubmit = () => {
        const spinFeeDiscountPercent = getSpinFeeDiscountPercent();
        const nextOffer = {
            bank: resolvedDisbursementBankName || '--',
            amount: formatINR(selectedAmount),
            tenure: `${selectedTenureDays} Days`,
            interest: `${interestPerDayLabel} per day`,
            interestPerDay: interestPerDayRate,
            processingFeePercent: processingFeeRate * 100 * (1 - spinFeeDiscountPercent / 100),
            gstApplicable: gstApplicableOnProcessingFee,
            productCode: resolvedDisbursalProductCode || null
        };
        setSelectedOffer(nextOffer);
        setStep(4);
    };

    const handleCapture = () => {
        if (!videoRef.current || !cameraReady) {
            showPopup("Camera is still loading. Please wait a second and try again.", "error");
            return;
        }
        if (!videoRef.current.videoWidth || !videoRef.current.videoHeight) {
            showPopup("Camera frame is not ready yet. Please try again.", "error");
            return;
        }
        const canvas = document.createElement('canvas');
        const sourceWidth = videoRef.current.videoWidth;
        const sourceHeight = videoRef.current.videoHeight;
        const targetAspectRatio = 3 / 4;
        const sourceAspectRatio = sourceWidth / sourceHeight;

        let cropWidth = sourceWidth;
        let cropHeight = sourceHeight;
        let cropX = 0;
        let cropY = 0;

        if (sourceAspectRatio > targetAspectRatio) {
            cropWidth = Math.round(sourceHeight * targetAspectRatio);
            cropX = Math.round((sourceWidth - cropWidth) / 2);
        } else if (sourceAspectRatio < targetAspectRatio) {
            cropHeight = Math.round(sourceWidth / targetAspectRatio);
            cropY = Math.round((sourceHeight - cropHeight) / 2);
        }

        canvas.width = cropWidth;
        canvas.height = cropHeight;
        canvas.getContext('2d').drawImage(
            videoRef.current,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight
        );
        setSelfieRecaptureBanner('');
        setLivePhoto(canvas.toDataURL('image/png'));
        stopCameraStream();
    };

    const handleRetake = () => {
        setSelfieRecaptureBanner('');
        setLivePhoto(null);
    };

    const submitFinalVerification = async (selfieUrlOverride = '') => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (isDemoDisbursalUser) {
            const demoSelectedAmount = Number(
                String(selectedOffer?.amount || '').replace(/[^\d.-]/g, '')
            ) || Number(selectedAmount || approvedLimitAmount || requestedAmount || 50000);
            const demoPrincipal = approvedLimitAmount > 0
                ? Math.min(demoSelectedAmount, approvedLimitAmount)
                : demoSelectedAmount;
            setLoanJourney(buildDemoLoanJourney({
                fetchedData,
                storedUser,
                principal: demoPrincipal,
                tenureDays
            }));
            setStep(6);
            return true;
        }

        const email = String(verificationData.officialEmail || storedUser.email || '').trim();
        const baseUrl = getApiBaseUrl();
        const selfieUrl = String(selfieUrlOverride || verificationData.selfieTempUrl || '').trim();
        if (!selfieUrl) {
            showPopup("Please capture your selfie before continuing.", "error");
            return false;
        }
        let resolvedAadhaarRequestId = String(
            verificationData?.aadhaarRequestId || loanJourney?.aadhaarRequestId || ''
        ).trim();
        const enteredAccountNumber = String(bankDetails.accountNumber || '').replace(/\D/g, '').trim();
        const enteredIfscCode = String(bankDetails.ifscCode || '').trim().toUpperCase();
        if (!enteredAccountNumber || !enteredIfscCode) {
            showPopup("Please verify your account number and IFSC code before continuing.", "error");
            setStep(2);
            return false;
        }
        if (enteredAccountNumber.length < 10 || enteredAccountNumber.length > 20) {
            showPopup("Please enter a valid salaried account number before continuing.", "error");
            setStep(2);
            return false;
        }
        if (!IFSC_CODE_PATTERN.test(enteredIfscCode)) {
            showPopup("Please enter a valid IFSC code before continuing.", "error");
            setStep(2);
            return false;
        }
        // Face match never blocks the customer here: the server records the result, and a loan
        // without a recent MATCHED result is held for manual KYC review before disbursal.
        let journeySelfieUrl = selfieUrl;
        try {
            const faceMatchResponse = await fetch(`${baseUrl}/api/face-match/selfie-vs-aadhaar`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    userId: storedUser.id || null,
                    mobile: storedUser.mobile_number || fetchedData?.mobile || '',
                    email,
                    aadhaarRequestId: resolvedAadhaarRequestId,
                    selfieUrl
                })
            });
            const faceMatchPayload = await faceMatchResponse.json().catch(() => ({}));
            if (faceMatchPayload?.matched) {
                resolvedAadhaarRequestId = String(faceMatchPayload.aadhaarRequestId || resolvedAadhaarRequestId).trim();
                journeySelfieUrl = String(faceMatchPayload.selfieUrl || selfieUrl).trim();
            }
            setVerificationData((prev) => ({
                ...prev,
                faceMatchDone: Boolean(faceMatchPayload?.matched),
                faceMatchSimilarity: faceMatchPayload?.similarity ?? null
            }));
        } catch (faceMatchError) {
            console.warn('Face match request failed; continuing for manual review', faceMatchError);
        }
        setVerificationData((prev) => ({
            ...prev,
            selfieTempUrl: prev.selfieTempUrl || journeySelfieUrl
        }));
        const selectedOfferAmount = Number(String(selectedOffer?.amount || '').replace(/[^\d.-]/g, '')) || 0;
        const requestedJourneyAmount = Number(selectedOfferAmount || selectedAmount || approvedLimitAmount || requestedAmount || 0);
        const requestAmount = approvedLimitAmount > 0
            ? Math.min(requestedJourneyAmount, approvedLimitAmount)
            : requestedJourneyAmount;
        const customerNameForLoan = String(
            aadhaarFetchState.details?.name ||
            fetchedData?.name ||
            storedUser.full_name ||
            fetchedData?.creditReport?.applicant_name ||
            'Customer'
        ).trim();
        const existingJourneyLoanId = String(
            loanJourney?.loanId ||
            fetchedData?.latestLoanJourney?.loanId ||
            resumeLoanJourneyId ||
            ''
        ).trim();
        const spinFeeDiscountPercent = getSpinFeeDiscountPercent();
        const journeyResponse = await fetch(`${baseUrl}/api/loan-journey/initiate`, {
            method: 'POST',
            headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                loanId: existingJourneyLoanId || undefined,
                userId: storedUser.id || null,
                mobile: storedUser.mobile_number || fetchedData?.mobile || '',
                email,
                panNumber: storedUser.pan_number || fetchedData?.pan || '',
                aadhaarRequestId: resolvedAadhaarRequestId,
                requestedAmount: requestAmount,
                tenureDays,
                selectedTenureDays,
                bankName: resolvedDisbursementBankName || 'Pawansut Holding Limited',
                interestPerDay: interestPerDayRate,
                processingFeePercent: processingFeeRate * 100 * (1 - spinFeeDiscountPercent / 100),
                spinDiscountPercent: spinFeeDiscountPercent,
                gstApplicable: gstApplicableOnProcessingFee,
                accountNumber: enteredAccountNumber,
                ifscCode: enteredIfscCode,
                productCode: String(selectedOffer?.productCode || fetchedData?.currentOffer?.productCode || fetchedData?.currentOffer?.product || '').trim(),
                customerName: customerNameForLoan,
                customerAddress: fetchedData?.panAddress || '',
                dateOfBirth: fetchedData?.dob || '',
                kioskId: localStorage.getItem('kiosk_id') || null,
                selfieUrl: journeySelfieUrl,
                reference1,
                reference2,
                reference1_name: reference1?.name || null,
                reference1_number: reference1?.mobile || null,
                reference1_relation: reference1?.relation || null,
                reference2_name: reference2?.name || null,
                reference2_number: reference2?.mobile || null,
                reference2_relation: reference2?.relation || null
            })
        });
        const journeyData = await journeyResponse.json();
        if (!journeyResponse.ok || !journeyData.success || !journeyData.loan?.loanId) {
            showPopup(journeyData.message || "Could not create loan ID. Please retry.", "error");
            return false;
        }
        setLoanJourney(journeyData.loan);
        setStep(6);
        return true;
    };

    const handleSelfieSubmit = async () => {

        if (!livePhoto || loading) return;
        if (isDemoDisbursalUser) {
            setSelfieRecaptureBanner('');
            setVerificationData((prev) => ({
                ...prev,
                selfieTempUrl: prev.selfieTempUrl || 'demo-selfie.png',
                faceMatchDone: true,
                faceMatchSimilarity: prev.faceMatchSimilarity ?? 99
            }));
            await submitFinalVerification('demo-selfie.png');
            return;
        }

        setLoading(true);
        try {
            // Get user info for pathing
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const mobile = user.mobile_number || fetchedData?.mobile || '9999999999';

            // Convert Data URL to Blob safely across browsers.
            const dataUrlToBlob = (dataUrl) => {
                const parts = dataUrl.split(',');
                const mime = (parts[0].match(/:(.*?);/) || [])[1] || 'image/png';
                const binary = atob(parts[1] || '');
                const len = binary.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
                return new Blob([bytes], { type: mime });
            };
            const blob = dataUrlToBlob(livePhoto);

            const formData = new FormData();

            // IMPORTANT: Append fields BEFORE the file so Multer-S3 can use them for the S3 key
            formData.append('mobile', mobile);
            formData.append('documentType', 'SELFIE_TEMP');
            formData.append('userId', user.id || '');
            formData.append('applicationId', location.state?.applicationId || 'null'); // might be null in some flows

            formData.append('document', blob, `selfie_${Date.now()}.png`);

            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/upload-temp-selfie`, {
                method: 'POST',
                body: formData
            });

            const raw = await response.text();
            let result = {};
            try {
                result = raw ? JSON.parse(raw) : {};
            } catch (_e) {
                result = { error: raw || 'Unexpected server response' };
            }

            if (!response.ok || !result.success) {
                const reason = result.error || result.message || `Upload failed (HTTP ${response.status})`;
                throw new Error(reason);
            }

            if (result.success) {
                setSelfieRecaptureBanner('');
                const selfieUrl = result.url || '';
                setVerificationData((prev) => ({
                    ...prev,
                    selfieTempUrl: selfieUrl,
                    faceMatchDone: false,
                    faceMatchSimilarity: null
                }));
                await submitFinalVerification(selfieUrl);
            } else {

                throw new Error(result.error || "Upload failed");
            }
        } catch (error) {
            console.error("Selfie Upload Error:", error);
            showPopup(`Failed to upload selfie: ${error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };
    const handleCreateDigilockerUrl = async () => {
        if (isDemoAadhaarBypassEnabled) {
            applyDemoAadhaarBypass();
            showPopup('Demo Aadhaar verification completed with the preset demo profile.', 'success');
            return;
        }

        if (isAadhaarVerified) {
            showPopup("e-Aadhaar is already verified for this journey.", "info");
            return;
        }

        const useKioskJourneyWindow = isKioskBridgeAvailable();
        const preparedPopup = useKioskJourneyWindow ? null : primeDigilockerPopup();
        setDigilockerState((prev) => ({ ...prev, creating: true }));
        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/digilocker/create-url`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    userId: storedUser.id || null,
                    mobile: storedUser.mobile_number || fetchedData?.mobile || '',
                    email: verificationData.officialEmail || '',
                    internalId: storedUser.id ? `loan-user-${storedUser.id}` : undefined
                })
            });
            const data = await response.json();
            const url = data?.digilocker?.url || '';
            const requestId = data?.digilocker?.requestId || '';

            if (!response.ok || !data.success || !url || !requestId) {
                if (preparedPopup && !preparedPopup.closed) {
                    preparedPopup.close();
                }
                setDigilockerState({ creating: false, url: '', requestId: '', polling: false });
                showPopup(data.message || "Failed to create Digilocker URL.", "error");
                return;
            }

            setVerificationData((prev) => ({ ...prev, aadhaarRequestId: requestId }));
            setDigilockerState({
                creating: false,
                url,
                requestId,
                polling: true
            });
            digilockerPollingStartedAtRef.current = Date.now();
            clearDigilockerPollLock(requestId);
            digilockerTerminalRequestRef.current = '';
            digilockerStatusRequestRef.current = { inFlight: false, requestId: '' };
            setAadhaarFetchState({ loading: false, fetched: false, details: null });
            saveDigilockerSession({ requestId, url });

            clearDigilockerPendingState();
            if (useKioskJourneyWindow) {
                const launchedInKiosk = await launchExternalJourneyInKioskWrapper({
                    url,
                    title: 'Aadhaar Verification',
                    width: 520,
                    height: 760
                });
                if (!launchedInKiosk?.ok) {
                    showPopup("Aadhaar verification could not open in kiosk mode. Please restart the kiosk app and try again.", "error");
                } else {
                    showPopup("Aadhaar verification opened in the fixed verification window. Complete the authorization there.", "info");
                }
            } else {
                const popupLaunch = openDigilockerPopup(url);
                if (!popupLaunch?.ok) {
                    if (preparedPopup && !preparedPopup.closed) {
                        preparedPopup.close();
                    }
                    showPopup("Unable to open the Aadhaar verification window. Please allow popups and try again.", "error");
                } else {
                    showPopup("Aadhaar verification opened in a separate verification window. Complete the authorization there.", "info");
                }
            }
        } catch (error) {
            if (preparedPopup && !preparedPopup.closed) {
                preparedPopup.close();
            }
            console.error("Digilocker URL creation failed:", error);
            clearStoredDigilockerSession();
            setDigilockerState({ creating: false, url: '', requestId: '', polling: false });
            showPopup("Unable to create Digilocker URL. Please try again.", "error");
        }
    };

    const handleAadhaarVerificationContinue = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (isDemoAadhaarBypassEnabled) {
            applyDemoAadhaarBypass();
            return;
        }

        const verifiedDetails = (isAadhaarDetailsVerified(aadhaarFetchState.details) ? aadhaarFetchState.details : null)
            || (isAadhaarDetailsVerified(fetchedData?.aadhaarVerification) ? fetchedData.aadhaarVerification : null);
        const verifiedAadhaarName = String(verifiedDetails?.name || '').trim();
        const aadhaarRequestId = verificationData.aadhaarRequestId || verifiedDetails?.requestId || '';
        const officialEmail = String(officialEmailDraft || verificationData.officialEmail || '').trim().toLowerCase();
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const expectedBorrowerName = String(storedUser.full_name || fetchedData?.name || '').trim();

        setVerificationData((prev) => ({
            ...prev,
            officialEmail,
            aadhaarRequestId: prev.aadhaarRequestId || aadhaarRequestId
        }));

        if (!aadhaarConsent) {
            showPopup("Please provide the consent to continue.", "error");
            return;
        }

        if (officialEmail && !isValidEmail(officialEmail)) {
            showPopup("Please enter a valid email address.", "error");
            return;
        }

        if (officialEmail) {
            const cleanMobile = String(storedUser.mobile_number || storedUser.mobile || fetchedData?.mobile || '').replace(/\D/g, '').slice(-10);
            const savedEmail = getCustomerVerifiedEmail(cleanMobile);
            const isVerifiedMatch = isEmailVerified && verifiedEmailAddress.toLowerCase() === officialEmail.toLowerCase();
            const isSavedMatch = savedEmail && savedEmail.toLowerCase() === officialEmail.toLowerCase();

            if (!isVerifiedMatch && !isSavedMatch) {
                showPopup("Please verify your email address using OTP before continuing.", "error");
                return;
            }
            if (!isEmailVerified && isSavedMatch) {
                setIsEmailVerified(true);
                setVerifiedEmailAddress(officialEmail);
            }
        }

        if (!isAadhaarVerified || !verifiedDetails || !verifiedAadhaarName || verifiedAadhaarName.toLowerCase() === 'customer') {
            showPopup("e-Aadhaar is not verified yet. Please complete e-Aadhaar verification to continue.", "error");
            return;
        }

        if (!aadhaarRequestId) {
            showPopup("Please complete e-Aadhaar verification before continuing.", "error");
            return;
        }

        if (expectedBorrowerName && verifiedAadhaarName) {
            const isNameMatching = (a, b) => {
                if (!a || !b) return false;
                if (normalizeIdentityName(a) === normalizeIdentityName(b)) return true;
                const tokenize = (v) => String(v || '').toUpperCase().replace(/[^A-Z\s]/g, ' ').split(/\s+/).filter(Boolean);
                const aTokens = tokenize(a);
                const bTokens = tokenize(b);
                if (!aTokens.length || !bTokens.length) return false;
                if (aTokens.join('') === bTokens.join('')) return true;

                const isSpellingVariant = (w1, w2) => {
                    if (w1 === w2) return true;
                    if (Math.abs(w1.length - w2.length) > 1) return false;
                    if (w1.length === w2.length) {
                        let diffs = 0;
                        for (let i = 0; i < w1.length; i++) {
                            if (w1[i] !== w2[i]) diffs++;
                            if (diffs > 1) return false;
                        }
                        return diffs === 1;
                    }
                    const [s, l] = w1.length < w2.length ? [w1, w2] : [w2, w1];
                    let si = 0;
                    let li = 0;
                    let diffs = 0;
                    while (si < s.length && li < l.length) {
                        if (s[si] === l[li]) {
                            si++;
                            li++;
                        } else {
                            diffs++;
                            li++;
                            if (diffs > 1) return false;
                        }
                    }
                    return true;
                };

                const aFull = aTokens.filter(t => t.length > 1).length;
                const bFull = bTokens.filter(t => t.length > 1).length;
                const [shorter, longer] = (aTokens.length < bTokens.length || (aTokens.length === bTokens.length && aFull <= bFull)) ? [aTokens, bTokens] : [bTokens, aTokens];
                const pool = [...longer];
                let fullMatches = 0;
                let initialMatches = 0;
                for (const word of shorter) {
                    let idx = pool.indexOf(word);
                    if (idx < 0) idx = pool.findIndex(cand => isSpellingVariant(word, cand));
                    if (idx >= 0) {
                        fullMatches += 1;
                    } else {
                        idx = pool.findIndex(cand => word.length === 1 ? cand.startsWith(word) : (cand.length === 1 && word.startsWith(cand)));
                        if (idx >= 0) initialMatches += 1;
                    }
                    if (idx < 0) return false;
                    pool.splice(idx, 1);
                }
                return (fullMatches >= Math.min(2, shorter.length)) || (fullMatches >= 1 && (fullMatches + initialMatches) === shorter.length);
            };

            if (!isNameMatching(expectedBorrowerName, verifiedAadhaarName)) {
                let isIdentityApproved = false;
                try {
                    const apiBase = getApiBaseUrl();
                    const checkMobile = storedUser.mobile_number || storedUser.mobile || storedUserSnapshotMobile || fetchedData?.mobile || fetchedData?.mobile_number || '';
                    const checkUserId = storedUser.id || fetchedData?.id || fetchedData?.userId || fetchedData?.user?.id || '';
                    const checkRes = await fetch(`${apiBase}/api/identity-approval-status?mobile=${encodeURIComponent(checkMobile)}&userId=${encodeURIComponent(checkUserId)}`);
                    const checkData = await checkRes.json();
                    isIdentityApproved = Boolean(checkData?.success && checkData?.isApproved);
                } catch (err) {
                    console.warn('Failed to check identity approval status:', err);
                }

                if (!isIdentityApproved) {
                    setIdentityReviewPending({
                        isPending: true,
                        aadhaarName: verifiedAadhaarName,
                        expectedName: expectedBorrowerName
                    });
                    showPopup("Verified Aadhaar name does not match this account holder. If approved by CRM, please try again or contact support.", "error");
                    return;
                }
            }
        }
        setIdentityReviewPending({ isPending: false, aadhaarName: '', expectedName: '' });
        setStep(5);
    };

    const handleDisburse = async () => {
        const loanId = String(loanJourney?.loanId || '').trim();
        const disbursalProductCode = resolvedDisbursalProductCode;
        const disbursalAmount = Math.max(0, Math.round(Number(finalDisbursement || 0)));

        if (!loanId) {
            showPopup("Loan ID not found. Please retry the summary step.", "error");
            return;
        }
        if (isDemoDisbursalUser) {
            const completionMobile = String(
                fetchedData?.mobile
                || storedUserSnapshot.mobile_number
                || ''
            ).trim();
            const completionKey = getJourneyCompletionStorageKey(completionMobile);

            if (completionKey) {
                try {
                    localStorage.setItem(completionKey, String(Date.now()));
                } catch (_error) {
                }
            }
            showPopup('We have accepted your disbursal request. Your loan will be disbursed within the next 30 minutes.', 'success', {
                onClose: () => setStep(9)
            });
            return;
        }
        if (disbursalAmount <= 0) {
            showPopup("Disbursement amount is invalid. Please refresh the loan summary.", "error");
            return;
        }

        const alreadyHasVoucher = pendingDisbursalVoucher?.loanId === loanId;

        setLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const baseUrl = getApiBaseUrl();
            let referenceId = alreadyHasVoucher ? pendingDisbursalVoucher.referenceId : null;

            if (!alreadyHasVoucher) {
                const response = await fetch(`${baseUrl}/api/loan-journey/send-voucher`, {
                    method: 'POST',
                    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({
                        loanId,
                    termsRevision: loanJourney?.termsRevision || 0,
                        productId: disbursalProductCode,
                        amount: disbursalAmount,
                        denominationDetails: [
                            {
                                amount: disbursalAmount,
                                quantity: 1
                            }
                        ],
                        customerDetails: {
                            name: resolvedCustomerName,
                            phoneNumber: displayMobileNumber,
                            email: String(storedUser.email || fetchedData?.email || '').trim().toLowerCase()
                        }
                    })
                });

                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data?.success) {
                    showPopup(data?.message || 'Disbursement failed. Please try again.', 'error');
                    return;
                }

                referenceId = data?.voucherOrder?.reference_id || null;
                setPendingDisbursalVoucher({ loanId, referenceId });
            }

            const markResponse = await fetch(`${baseUrl}/api/loan-journey/mark-disbursed`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    loanId,
                    termsRevision: loanJourney?.termsRevision || 0,
                    productId: disbursalProductCode,
                    amount: disbursalAmount,
                    referenceId
                })
            });

            const markData = await markResponse.json().catch(() => ({}));
            if (!markResponse.ok || !markData?.success) {
                showPopup(markData?.message || 'Voucher was already created. Tap Confirm Disbursal to retry confirmation — funds will not be sent again.', 'error');
                return;
            }

            setPendingDisbursalVoucher(null);

            const completionMobile = String(
                fetchedData?.mobile
                || storedUser.mobile_number
                || ''
            ).trim();
            const completionKey = getJourneyCompletionStorageKey(completionMobile);

            if (completionKey) {
                try {
                    localStorage.setItem(completionKey, String(Date.now()));
                } catch (_error) {
                }
            }
            clearPersistedLoanApplicationState();
            showPopup('We have accepted your disbursal request. Your loan will be disbursed within the next 30 minutes.', 'success', {
                onClose: () => setStep(9)
            });
        } catch (error) {
            console.error('Disbursement failed:', error);
            showPopup('Unable to complete disbursement right now. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const saveBorrowerSignatureAndPreview = async () => {
        if (!loanJourney?.loanId) {
            showPopup("Loan ID not found. Please retry verification step.", "error");
            return false;
        }
        setLoading(true);
        try {
            const signatureDataUrl = captureBorrowerSignatureDataUrl();
            if (!signatureDataUrl) {
                showPopup('Please draw your signature in the box before continuing.', 'error');
                return false;
            }
            const resolvedJourneyPrincipalAmount = resolveLoanJourneyPrincipalAmount(loanJourney, {
                requestedAmount: selectedAmount || requestedAmount,
                approvedLimitAmount
            });
            const resolvedJourneyBankDetails = resolveLoanJourneyBankDetails(loanJourney, { ...fetchedData, savedBankSuggestion });
            const resolvedJourneyTenureDays = resolveLoanJourneyTenureDays(loanJourney, fetchedData, selectedTenureDays);
            if (!resolvedJourneyTenureDays) {
                showPopup('Tenure is missing. Please select tenure again and continue.', 'error');
                return false;
            }
            const resolvedProcessingFee = toPositiveNumber(loanJourney?.processingFee ?? breakdown?.processingFee) ?? 0;
            const resolvedGstAmount = toPositiveNumber(loanJourney?.gstAmount ?? breakdown?.gst) ?? 0;
            const resolvedFinalDisbursement = toPositiveNumber(
                loanJourney?.finalDisbursement
                ?? breakdown?.finalDisbursement
                ?? (resolvedJourneyPrincipalAmount - resolvedProcessingFee - resolvedGstAmount)
            ) ?? Math.max(0, resolvedJourneyPrincipalAmount - resolvedProcessingFee - resolvedGstAmount);
            const resolvedTotalInterestCharge = toPositiveNumber(
                loanJourney?.totalInterestCharge
                ?? ((resolvedJourneyPrincipalAmount * interestPerDayRate * resolvedJourneyTenureDays))
            ) ?? Math.round(resolvedJourneyPrincipalAmount * interestPerDayRate * resolvedJourneyTenureDays);
            const resolvedTotalRepaymentAmount = toPositiveNumber(
                loanJourney?.totalRepaymentAmount
                ?? (resolvedJourneyPrincipalAmount + resolvedTotalInterestCharge)
            ) ?? Math.round(resolvedJourneyPrincipalAmount + resolvedTotalInterestCharge);
            const resolvedSanctionDateText = loanJourney?.sanctionDate
                || loanJourney?.sanction_date
                || fetchedData?.latestSanctionLetter?.sentAt
                || fetchedData?.latestSanctionLetter?.sanctionDate
                || new Date().toISOString();
            let resolvedSanctionLetterHtml = '';

            // Demo flow: skip the API call since the demo loanId doesn't exist in the DB
            if (isDemoDisbursalUser) {
                const demoSignedAtText = new Date().toLocaleString('en-IN', {
                    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short'
                });
                setBorrowerSignaturePreviewDataUrl(signatureDataUrl);
                setBorrowerSignaturePreviewSignedAtText(demoSignedAtText);
                setBorrowerSignatureSubmitted(true);
                if (borrowerSignatureStorageKey) {
                    try {
                        localStorage.setItem(
                            borrowerSignatureStorageKey,
                            JSON.stringify({ dataUrl: signatureDataUrl, signedAtText: demoSignedAtText })
                        );
                    } catch (_error) {}
                }
                const baseSanctionLetterHtml = String(
                    fetchedData?.latestSanctionLetter?.html
                    || loanJourney?.sanctionLetterHtml
                    || ''
                ).trim();
                resolvedSanctionLetterHtml = patchSanctionLetterHtmlAmounts(baseSanctionLetterHtml, {
                    principalAmount: resolvedJourneyPrincipalAmount,
                    processingFee: resolvedProcessingFee,
                    gstAmount: resolvedGstAmount,
                    finalDisbursement: resolvedFinalDisbursement,
                    totalInterestCharge: resolvedTotalInterestCharge,
                    totalRepaymentAmount: resolvedTotalRepaymentAmount,
                    tenureDays: resolvedJourneyTenureDays
                }) || baseSanctionLetterHtml || '';
                setLoanJourney((prev) => prev ? {
                ...prev,
                sanctionDate: demoSignedAtText,
                sanctionedAmount: resolvedJourneyPrincipalAmount,
                tenureDays: resolvedJourneyTenureDays,
                selectedTenureDays: resolvedJourneyTenureDays,
                sanctionLetterHtml: resolvedSanctionLetterHtml
            } : prev);
                setFetchedData((prev) => prev ? {
                    ...prev,
                    latestSanctionLetter: {
                        ...(prev.latestSanctionLetter || {}),
                        loanId: loanJourney.loanId || prev.latestSanctionLetter?.loanId || '',
                        customerName: loanJourney.customerName || prev.latestSanctionLetter?.customerName || prev.name || '',
                        sanctionedAmount: resolvedJourneyPrincipalAmount,
                        finalDisbursement: loanJourney.finalDisbursement ?? prev.latestSanctionLetter?.finalDisbursement ?? null,
                        repaymentDate: loanJourney.repaymentDate || prev.latestSanctionLetter?.repaymentDate || '',
                        bankName: loanJourney.bankName || resolvedJourneyBankDetails.bankName || prev.latestSanctionLetter?.bankName || '',
                        ifscCode: loanJourney.ifscCode || resolvedJourneyBankDetails.ifscCode || prev.latestSanctionLetter?.ifscCode || '',
                        accountLast4: loanJourney.accountLast4 || resolvedJourneyBankDetails.accountLast4 || prev.latestSanctionLetter?.accountLast4 || '',
                        sanctionDate: demoSignedAtText,
                        html: resolvedSanctionLetterHtml,
                        sentAt: demoSignedAtText,
                        viewUrl: prev.latestSanctionLetter?.viewUrl || '',
                        downloadUrl: prev.latestSanctionLetter?.downloadUrl || ''
                    }
                } : prev);
                setPennyDropStatus({ started: false, completed: false, message: '' });
                return true;
            }

            const baseSanctionLetterHtmlForSave = String(
                fetchedData?.latestSanctionLetter?.html
                || loanJourney?.sanctionLetterHtml
                || ''
            ).trim();
            resolvedSanctionLetterHtml = patchSanctionLetterHtmlAmounts(baseSanctionLetterHtmlForSave, {
                principalAmount: resolvedJourneyPrincipalAmount,
                processingFee: resolvedProcessingFee,
                gstAmount: resolvedGstAmount,
                finalDisbursement: resolvedFinalDisbursement,
                totalInterestCharge: resolvedTotalInterestCharge,
                totalRepaymentAmount: resolvedTotalRepaymentAmount,
                tenureDays: resolvedJourneyTenureDays
            }) || baseSanctionLetterHtmlForSave || '';

            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/loan-journey/borrower-signature`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    loanId: loanJourney.loanId,
                    termsRevision: loanJourney.termsRevision || 0,
                    signatureDataUrl
                })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data?.success) {
                throw new Error(data?.message || 'Failed to save borrower signature');
            }

            const backendAcceptedAtText = data?.acceptedAtText || data?.sanctionDate || resolvedSanctionDateText;
            const baseSanctionLetterHtml = String(
                data?.sanctionLetterHtml
                || fetchedData?.latestSanctionLetter?.html
                || loanJourney?.sanctionLetterHtml
                || ''
            ).trim();
            resolvedSanctionLetterHtml = patchSanctionLetterHtmlAmounts(baseSanctionLetterHtml, {
                principalAmount: resolvedJourneyPrincipalAmount,
                processingFee: resolvedProcessingFee,
                gstAmount: resolvedGstAmount,
                finalDisbursement: resolvedFinalDisbursement,
                totalInterestCharge: resolvedTotalInterestCharge,
                totalRepaymentAmount: resolvedTotalRepaymentAmount,
                tenureDays: resolvedJourneyTenureDays
            }) || baseSanctionLetterHtml || '';
            setBorrowerSignaturePreviewDataUrl(signatureDataUrl);
            setBorrowerSignaturePreviewSignedAtText(backendAcceptedAtText);
            setBorrowerSignatureSubmitted(true);
            setLoanJourney((prev) => prev ? {
                ...prev,
                bankName: prev.bankName || resolvedJourneyBankDetails.bankName || '',
                ifscCode: prev.ifscCode || resolvedJourneyBankDetails.ifscCode || '',
                accountLast4: prev.accountLast4 || resolvedJourneyBankDetails.accountLast4 || '',
                sanctionDate: backendAcceptedAtText || prev.sanctionDate || '',
                sanctionedAmount: resolvedJourneyPrincipalAmount,
                tenureDays: resolvedJourneyTenureDays,
                selectedTenureDays: resolvedJourneyTenureDays,
                sanctionLetterHtml: resolvedSanctionLetterHtml
            } : prev);
            setFetchedData((prev) => prev ? {
                ...prev,
                latestSanctionLetter: {
                    ...(prev.latestSanctionLetter || {}),
                    loanId: loanJourney.loanId || prev.latestSanctionLetter?.loanId || '',
                    customerName: loanJourney.customerName || prev.latestSanctionLetter?.customerName || prev.name || '',
                    sanctionedAmount: resolvedJourneyPrincipalAmount,
                    finalDisbursement: loanJourney.finalDisbursement ?? prev.latestSanctionLetter?.finalDisbursement ?? null,
                    repaymentDate: loanJourney.repaymentDate || prev.latestSanctionLetter?.repaymentDate || '',
                    bankName: loanJourney.bankName || resolvedJourneyBankDetails.bankName || prev.latestSanctionLetter?.bankName || '',
                    ifscCode: loanJourney.ifscCode || resolvedJourneyBankDetails.ifscCode || prev.latestSanctionLetter?.ifscCode || '',
                    accountLast4: loanJourney.accountLast4 || resolvedJourneyBankDetails.accountLast4 || prev.latestSanctionLetter?.accountLast4 || '',
                    sanctionDate: backendAcceptedAtText || prev.latestSanctionLetter?.sanctionDate || '',
                    html: resolvedSanctionLetterHtml,
                    sentAt: backendAcceptedAtText || prev.latestSanctionLetter?.sentAt || '',
                    viewUrl: prev.latestSanctionLetter?.viewUrl || '',
                    downloadUrl: prev.latestSanctionLetter?.downloadUrl || ''
                }
            } : prev);
            if (borrowerSignatureStorageKey) {
                try {
                    localStorage.setItem(
                        borrowerSignatureStorageKey,
                        JSON.stringify({
                            dataUrl: signatureDataUrl,
                            signedAtText: data?.acceptedAtText || ''
                        })
                    );
                } catch (_error) {
                }
            }
            setPennyDropStatus({ started: false, completed: false, message: '' });
            return true;
        } catch (error) {
            console.error('Borrower signature save failed:', error);
            showPopup('Unable to save the borrower signature right now. Please try again.', 'error');
            setPennyDropStatus({ started: false, completed: false, message: '' });
            return false;
        } finally {
            setLoading(false);
        }
    };

    const continueToPennyDrop = () => {
        if (!borrowerSignatureSubmitted) {
            showPopup('Please save the borrower signature before continuing.', 'error');
            return;
        }
        if (!sanctionConsent) {
            showPopup('Please provide the consent before moving to Penny Drop.', 'error');
            return;
        }
        if (!hasRenderableBankJourneyDetails(loanJourney, fetchedData, savedBankSuggestion, bankDetails)) {
            showPopup('Bank details are missing or not fully loaded. Please refresh the journey and continue again.', 'error');
            return;
        }
        setShowSanctionPreview(false);
        setPennyDropStatus({ started: false, completed: false, message: '' });
        setStep(7);
        window.requestAnimationFrame(() => {
            const section = document.querySelector('.penny-drop-section');
            if (section && typeof section.scrollIntoView === 'function') {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    };

    const handleBorrowerSignatureSubmit = async () => {
        await saveBorrowerSignatureAndPreview();
    };

    const formatINR = (amt) => `₹${Number(amt).toLocaleString('en-IN')}`;
    const amountSliderStep = 500;
    const sliderLimitAmount = approvedLimitAmount || requestedAmount;
    // Anchor the slider grid to the approved limit so the limit itself is always selectable.
    // With a bare minimum (e.g. 10) the 500-step grid stopped at limit - 490.
    const minimumSelectableAmount = (() => {
        const floorAmount = Math.min(DEFAULT_LOAN_PRINCIPAL_AMOUNT, sliderLimitAmount);
        const steps = Math.floor((sliderLimitAmount - floorAmount) / amountSliderStep);
        return sliderLimitAmount - steps * amountSliderStep;
    })();
    const computeCharges = (amount) => {
        let processingFee = amount * processingFeeRate;
        // Apply the processing-fee discount earned from the spin wheel (5/10/15/20%)
        const spinFeeDiscountPercent = getSpinFeeDiscountPercent();
        if (spinFeeDiscountPercent > 0) {
            processingFee = processingFee * (1 - spinFeeDiscountPercent / 100);
        }
        const gst = gstApplicableOnProcessingFee ? processingFee * GST_ON_FEE_RATE : 0;
        return { processingFee, gst, hasProcessorFeeDiscount: spinFeeDiscountPercent > 0, spinFeeDiscountPercent };
    };


    // Calculate Loan Numbers
    const calculateBreakdown = () => {
        const principal = Number(
            selectedOffer
                ? String(selectedOffer.amount || '').replace(/[^\d.-]/g, '')
                : selectedAmount
        ) || 0;
        const { processingFee, gst } = computeCharges(principal);
        const finalDisbursement = principal - (processingFee + gst);

        return {
            principal,
            processingFee,
            gst,
            finalDisbursement
        };
    };

    const breakdown = calculateBreakdown();
    const spinFeeDiscountPercent = getSpinFeeDiscountPercent();
    const hasProcessorFeeDiscount = spinFeeDiscountPercent > 0;
    let storedUserName = '';
    try {
        const storedUserData = JSON.parse(localStorage.getItem('user') || '{}') || {};
        storedUserName = storedUserData?.full_name || '';
    } catch (_e) {
        storedUserName = '';
    }
    const resolvedCustomerName = (fetchedData?.name || storedUserName || 'Customer').trim();
    const displayCustomerName = resolvedCustomerName.split(' ')[0] || 'Customer';
    const localizedDisplayCustomerName = getLocalizedName(displayCustomerName, language) || displayCustomerName;
    const localizedFetchedName = getLocalizedName(fetchedData?.name || '', language) || fetchedData?.name || '--';
    const resolvedJourneyPrincipalAmount = resolveLoanJourneyPrincipalAmount(loanJourney, {
        requestedAmount: selectedAmount || requestedAmount,
        approvedLimitAmount
    });
    const tenureDays = resolveLoanJourneyTenureDays(loanJourney, fetchedData, selectedTenureDays);
    const sanctionedAmount = resolvedJourneyPrincipalAmount || breakdown?.principal || loanJourney?.sanctionedAmount || requestedAmount;
    // When loan is already initiated use DB fees; breakdown is only for pre-initiation preview
    const storedProcessingFee = toPositiveNumber(loanJourney?.processingFee ?? loanJourney?.processing_fee);
    const storedGstAmount = toPositiveNumber(loanJourney?.gstAmount ?? loanJourney?.gst_amount);
    const storedFinalDisbursement = toPositiveNumber(loanJourney?.finalDisbursement ?? loanJourney?.final_disbursement);
    const loanJourneyHasFees = storedProcessingFee != null && storedProcessingFee > 0;
    const fallbackPf = breakdown?.processingFee ?? Math.round(sanctionedAmount * processingFeeRate);
    const processingFee = loanJourneyHasFees ? storedProcessingFee : fallbackPf;
    const fallbackGst = breakdown?.gst ?? Math.round(processingFee * 0.18);
    const gstAmount = loanJourneyHasFees ? (storedGstAmount || Math.round(storedProcessingFee * 0.18)) : fallbackGst;
    const finalDisbursement = loanJourneyHasFees
        ? (storedFinalDisbursement || Math.max(0, sanctionedAmount - storedProcessingFee - gstAmount))
        : (breakdown?.finalDisbursement ?? Math.max(0, sanctionedAmount - processingFee - gstAmount));
    const savedSanctionLetterHtml = String(
        fetchedData?.latestSanctionLetter?.html
        || loanJourney?.sanctionLetterHtml
        || ''
    ).trim();
    const resolvedLoanJourneyBankDetails = resolveLoanJourneyBankDetails(loanJourney, { ...fetchedData, savedBankSuggestion }, bankDetails);
    const hasRenderableJourneyBankDetails = hasRenderableBankJourneyDetails(loanJourney, fetchedData, savedBankSuggestion, bankDetails);
    const resolvedSanctionDate = String(
        loanJourney?.sanctionDate
        || loanJourney?.sanction_date
        || fetchedData?.latestSanctionLetter?.sentAt
        || fetchedData?.latestSanctionLetter?.sanction_letter_sent_at
        || fetchedData?.latestLoanJourney?.sanctionDate
        || ''
    ).trim();
    const getJourneyPosition = () => {
        if (step === 1) return 1;
        if (step === 2) return 2;
        if (step === 3) return 3;
        if (step === 4) return 4;
        if (step === 5) return 5;
        if (step === 6) return 6;
        if (step === 7) return 7;
        if (step === 8 || step === 9) return 8;
        return 1;
    };
    const journeyPosition = getJourneyPosition();

    const renderJourneyTracker = () => (
        <div className="journey-tracker-wrap">
            <div className="journey-tracker-header">
                <h3>Journey Progress</h3>
                <span>Step {journeyPosition} of {JOURNEY_STAGES.length}</span>
            </div>
            <div className="journey-tracker-bar">
                <div
                    className="journey-tracker-fill"
                    style={{ width: `${(journeyPosition / JOURNEY_STAGES.length) * 100}%` }}
                />
            </div>
            <div className="journey-tracker-steps">
                {JOURNEY_STAGES.map((stage) => {
                    const completed = stage.id < journeyPosition;
                    const current = stage.id === journeyPosition;
                    return (
                        <div
                            key={stage.id}
                            className={`journey-tracker-step ${completed ? 'completed' : ''} ${current ? 'current' : ''}`}
                            title={stage.title}
                            aria-label={`Step ${stage.id}: ${stage.title}${completed ? ' (done)' : current ? ' (current)' : ''}`}
                        >
                            <div className="journey-tracker-dot">
                                {completed ? '✓' : current ? stage.icon : stage.id}
                            </div>
                            <p>{stage.title}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    if (step === 9) {
        return (
            <div className="kiosk-theme la-page la-success-screen loan-app-container" style={{ textAlign: 'center', padding: '100px 40px', background: 'white', minHeight: '100vh' }}>
                {/* Confetti Effect */}
                {confetti.length > 0 && (
                    <div className="confetti-container">
                        {confetti.map(p => (
                            <div
                                key={p.id}
                                className="confetti-piece"
                                style={{
                                    left: `${p.left}%`,
                                    backgroundColor: p.color,
                                    animationDelay: `${p.delay}s`,
                                    animationDuration: `${p.duration}s`,
                                    transform: `rotate(${p.rotate}deg)`
                                }}
                            />
                        ))}
                    </div>
                )}

                {popup.show && (
                    <div
                        className="popup-overlay"
                        style={{ zIndex: 12070, pointerEvents: 'auto' }}
                        onClick={closePopup}
                    >
                        <div
                            className={`popup-box ${popup.type}`}
                            style={{ zIndex: 12071, pointerEvents: 'auto', position: 'relative' }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="popup-icon">
                                {popup.type === 'success' ? '✓' : popup.type === 'error' ? '✕' : '!'}
                            </div>
                            <p>{popup.message}</p>
                            {popup.dismissible ? <button type="button" onClick={closePopup}>OK</button> : null}
                        </div>
                    </div>
                )}

                <div className="success-content slide-down" style={{ position: 'relative', zIndex: 10 }}>
                    {renderJourneyTracker()}
                    <div style={{ fontSize: '6rem', marginBottom: '30px' }}>🎊</div>
                    <h1 className="congrats-title" style={{ fontSize: '3rem', marginBottom: '20px' }}>Congratulations, {localizedDisplayCustomerName}!</h1>
                    <h2 style={{ color: '#10b981', fontSize: '2rem', marginBottom: '20px' }}>Money Disbursed Successfully!</h2>

                    <div style={{ background: '#ecfdf5', padding: '30px', borderRadius: '24px', display: 'inline-block', border: '2px dashed #10b981', margin: '20px 0' }}>
                        <p style={{ color: '#047857', fontSize: '1.2rem', margin: 0 }}>Amount Credited to Account:</p>
                        <strong style={{ fontSize: '3.5rem', color: '#065f46' }}>{formatINR(finalDisbursement)}</strong>
                    </div>

                    <p style={{ color: '#64748b', fontSize: '1.1rem', marginTop: '40px' }}>
                        Your transaction is complete. You will be automatically logged out for security.
                    </p>
                    <div style={{ marginTop: '20px' }}>
                        <div className="spinner" style={{ width: '40px', height: '40px', margin: '0 auto', borderTopColor: '#f59e0b' }}></div>
                        <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '10px' }}>Redirecting to home...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="kiosk-theme la-page loan-app-page">
            {entryLoaderMessage && <FullPageLoader message={entryLoaderMessage} />}
            {loading && (step === 2 || step === 5) && (
                <div className="app-page-loader-overlay" aria-live="polite" aria-busy="true">
                    <div className="app-page-loader-card">
                        <span className="app-page-loader-spinner" aria-hidden="true"></span>
                        <p>{step === 2 ? 'Verifying your bank details...' : 'Verifying your details...'}</p>
                    </div>
                </div>
            )}
            <div className="loan-app-container">
                {isSavingProgress && (
                    <div className="popup-overlay" style={{ zIndex: 12060, pointerEvents: 'auto' }}>
                        <div className="popup-box" style={{ zIndex: 12061, pointerEvents: 'auto', position: 'relative' }}>
                            <div className="spinner" style={{ width: '44px', height: '44px', margin: '0 auto 18px', borderTopColor: '#f59e0b' }}></div>
                            <p>Saving progress till now...</p>
                        </div>
                    </div>
                )}
                {/* AA Waiting Screen */}
                {aaWaiting.active && (
                    <div className="aa-waiting-overlay">
                        <div className="aa-waiting-card">
                            {!aaWaiting.timedOut && (
                                <div className="aa-waiting-loader" aria-hidden="true"></div>
                            )}
                            <h3 className="aa-waiting-card__title">
                                {aaWaiting.timedOut ? 'Unable to Fetch Account Data' : 'Fetching Your Account Data'}
                            </h3>
                            <p className="aa-waiting-card__subtitle">
                                {aaWaiting.timedOut
                                    ? 'We are unable to fetch the Account Aggregator data for the provided mobile number. Please retry.'
                                    : 'Please wait while we retrieve your account information from the Account Aggregator.'}
                            </p>
                            {!aaWaiting.timedOut ? (
                                <div className="aa-waiting-card__countdown">
                                    <span className="aa-waiting-card__countdown-number">{aaWaiting.countdown}</span>
                                    <span className="aa-waiting-card__countdown-label">seconds remaining</span>
                                </div>
                            ) : (
                                <div className="aa-waiting-card__timeout">
                                    <button
                                        type="button"
                                        className="action-btn aa-waiting-card__retry-btn"
                                        onClick={() => {
                                            setAaWaiting({ active: false, countdown: 60, timedOut: false });
                                            handleMoneyoneConsent();
                                        }}
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {/* Rejection Thank You Screen */}
                {rejectionThankYou && (
                    <div className="rejection-thankyou-overlay">
                        <div className="rejection-thankyou-card rejection-thankyou-card--guided">
                            <div className="rejection-thankyou-icon">
                                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <circle cx="32" cy="32" r="30" stroke="#f97316" strokeWidth="3" fill="#fff7ed" />
                                    <path d="M20 32c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" />
                                    <rect x="24" y="34" width="16" height="12" rx="2" fill="#f97316" />
                                    <path d="M24 36l8 6 8-6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="rejection-thankyou-card__title">{isHindi ? 'आवेदन के लिए धन्यवाद!' : 'Thanks for Applying!'}</h3>
                            <p className="rejection-thankyou-card__subtitle">
                                {isHindi
                                    ? <>अभी <strong>Finncube</strong> से ऑफर तैयार नहीं हो पाया। नीचे देखें कि क्यों, और आगे क्या किया जा सकता है।</>
                                    : <>We could not prepare a <strong>Finncube</strong> offer right now. Here is why, and what you can do next.</>}
                            </p>
                            <SalaryGuidanceCard
                                offer={fetchedData?.currentOffer}
                                fallbackReason={rejectionReason}
                                isHindi={isHindi}
                                compact
                                onLinkAccount={beginAaRestart}
                                onUploadSlip={openSalarySlipUploadFromRejection}
                            />
                            <p className="rejection-thankyou-card__note">
                                {isHindi
                                    ? 'कुछ न करने पर यह सेशन 60 सेकंड में अपने आप बंद हो जाएगा।'
                                    : 'This session will close by itself in 60 seconds if nothing is done.'}
                            </p>
                        </div>
                    </div>
                )}
                <SalarySlipUploadModal
                    open={showSalarySlipModal}
                    mobile={String(fetchedData?.mobile || '')}
                    isHindi={isHindi}
                    onClose={() => setShowSalarySlipModal(false)}
                    onUploaded={handleSalarySlipUploaded}
                />
                {/* Custom Popup Modal */}
                {popup.show && (
                    <div
                        className="popup-overlay"
                        style={{ zIndex: 12070, pointerEvents: 'auto' }}
                        onClick={isBlockingFetchPopup(popup) ? undefined : closePopup}
                    >
                        <div
                            className={`popup-box ${popup.type}`}
                            style={{ zIndex: 12071, pointerEvents: 'auto', position: 'relative' }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {popup.type === 'loading' ? (
                                <div className="popup-loading-wrap">
                                    <div className="spinner popup-spinner"></div>
                                </div>
                            ) : (
                                <div className="popup-icon">
                                    {popup.type === 'success' ? '✓' : popup.type === 'error' ? '✕' : '!'}
                                </div>
                            )}
                            <p>{popup.message}</p>
                            {popup.type === 'loading' && isBlockingFetchPopup(popup) ? (
                                <p style={{ marginTop: '6px', fontSize: '0.88rem', opacity: 0.85 }}>
                                    {`Retrying in ${fetchDetailsCountdown}s`}
                                </p>
                            ) : null}
                            {popup.dismissible ? <button type="button" onClick={closePopup}>OK</button> : null}
                        </div>
                    </div>
                )}
                {moneyOneModal.open && (
                    <div className="moneyone-modal-overlay">
                        <div className="moneyone-modal">
                            <div className="moneyone-modal__header">
                                <div>
                                    <h3>Validate Salary Account</h3>
                                    <p>Complete the account aggregator consent journey in this window.</p>
                                </div>
                                <button
                                    type="button"
                                    className="moneyone-modal__close"
                                    onClick={closeMoneyOneModal}
                                    aria-label="Close salary account validation"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="moneyone-modal__body">
                                {moneyOneModal.url ? (
                                    <iframe
                                        ref={moneyOneIframeRef}
                                        title="Salary account validation"
                                        src={moneyOneModal.url}
                                        className="moneyone-modal__iframe"
                                        allow="camera; microphone; clipboard-read; clipboard-write"
                                        onLoad={() => {
                                            const handled = processMoneyOneIframeReturn();
                                            if (handled) {
                                                setMoneyOneModal({ open: false, url: '' });
                                                setMoneyOneState((prev) => ({ ...prev, consentWindowOpen: false }));
                                            }
                                        }}
                                    />
                                ) : (
                                    <div className="moneyone-modal__empty">Unable to open the validation journey.</div>
                                )}
                            </div>
                            <div className="moneyone-modal__footer">
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={handleMoneyOneModalDone}
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {digilockerModal.open && (
                    <div className="moneyone-modal-overlay">
                        <div className="moneyone-modal">
                            <div className="moneyone-modal__header">
                                <div>
                                    <h3>Aadhaar Verification</h3>
                                    <p>Complete the DigiLocker journey in this popup window.</p>
                                </div>
                                <button
                                    type="button"
                                    className="moneyone-modal__close"
                                    onClick={closeDigilockerModal}
                                    aria-label="Close Aadhaar verification"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="moneyone-modal__body">
                                {digilockerModal.url ? (
                                    <iframe
                                        title="Aadhaar verification"
                                        src={digilockerModal.url}
                                        className="moneyone-modal__iframe"
                                        allow="camera; microphone; clipboard-read; clipboard-write"
                                    />
                                ) : (
                                    <div className="moneyone-modal__empty">Unable to open the Aadhaar verification journey.</div>
                                )}
                            </div>
                            <div className="moneyone-modal__footer">
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={closeDigilockerModal}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className="loan-app-header">
                    <h2>
                        {step === 1 ? (isHindi ? 'ऑफर विवरण' : 'Offer Details') :
                            step === 2 ? (isHindi ? 'अकाउंट एग्रीगेटर' : 'Account Aggregator') :
                                step === 3 ? (isHindi ? 'लोन विवरण' : 'Loan Details') :
                                    step === 4 ? (isHindi ? 'आधार सत्यापन' : 'E-Aadhaar Verification') :
                                        step === 5 ? (isHindi ? 'सत्यापन पूरा करें' : 'Selfie Verification') :
                                            step === 6 ? (isHindi ? 'लोन सारांश' : 'Loan Summary') :
                                                step === 7 ? (isHindi ? 'पेनी ड्रॉप सत्यापन' : 'Penny Drop Verification') : (isHindi ? 'राशि वितरित करें' : 'Disburse Funds')}
                    </h2>
                    {step === 3 && (
                        <p className="loan-app-subtitle">
                            {isHindi ? 'लोन राशि और अवधि चुनें' : 'Choose Loan Amount & Tenure'}
                        </p>
                    )}
                    {renderJourneyTracker()}
                </div>

                {step === 1 && (
                    <div className="fetching-state">
                        {loading ? (
                            <div className="spinner"></div>
                        ) : (
                            <div className="user-details-grid">
                                <div className="detail-card">
                                    <label>{isHindi ? 'पूरा नाम' : 'Full Name'}</label>
                                    <strong>{localizedFetchedName}</strong>
                                </div>
                                <div className="detail-card">
                                    <label>{isHindi ? 'मोबाइल नंबर' : 'Mobile Number'}</label>
                                    <div className="detail-card-value-row">
                                        <strong>{showSensitiveFields.mobileNumber ? displayMobileNumber : maskMobileNumber(displayMobileNumber)}</strong>
                                        {displayMobileNumber && (
                                            <button
                                                type="button"
                                                className="detail-visibility-btn"
                                                onClick={() => toggleFieldVisibility('mobileNumber')}
                                                aria-label={showSensitiveFields.mobileNumber
                                                    ? (isHindi ? 'मोबाइल नंबर छुपाएं' : 'Hide mobile number')
                                                    : (isHindi ? 'मोबाइल नंबर दिखाएं' : 'Show mobile number')}
                                            >
                                                <VisibilityIcon visible={showSensitiveFields.mobileNumber} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="detail-card">
                                    <label>{isHindi ? 'पैन नंबर' : 'PAN Number'}</label>
                                    <div className="detail-card-value-row">
                                        <strong>{showSensitiveFields.panNumber ? displayPanNumber : maskPanNumber(displayPanNumber)}</strong>
                                        {displayPanNumber && (
                                            <button
                                                type="button"
                                                className="detail-visibility-btn"
                                                onClick={() => toggleFieldVisibility('panNumber')}
                                                aria-label={showSensitiveFields.panNumber
                                                    ? (isHindi ? 'पैन नंबर छुपाएं' : 'Hide PAN number')
                                                    : (isHindi ? 'पैन नंबर दिखाएं' : 'Show PAN number')}
                                            >
                                                <VisibilityIcon visible={showSensitiveFields.panNumber} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="detail-card" style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
                                    <label style={{ color: '#ea580c' }}>{isHindi ? 'ऋण सीमा तक' : 'Loan Limit Upto'}</label>
                                    <strong style={{ color: '#c2410c' }}>{formatINR(offerDetailsPreviewAmount)}</strong>
                                </div>
                                <div className="detail-card" style={{ gridColumn: '1 / -1', background: '#ecfdf5', borderColor: '#10b981' }}>
                                    <label style={{ color: '#047857' }}>{isHindi ? 'सिबिल स्कोर' : 'CIBIL Score'}</label>
                                    <div className="detail-card-value-row">
                                        <strong style={{ color: '#065f46', fontSize: '1.5rem' }}>
                                            {showSensitiveFields.cibilScore
                                                ? (fetchedData?.cibil ?? '--')
                                                : (fetchedData?.cibil != null && fetchedData?.cibil !== '--' ? '•••' : '--')}
                                        </strong>
                                        {fetchedData?.cibil != null && fetchedData?.cibil !== '--' && (
                                            <button
                                                type="button"
                                                className="detail-visibility-btn"
                                                onClick={() => toggleFieldVisibility('cibilScore')}
                                                aria-label={showSensitiveFields.cibilScore
                                                    ? (isHindi ? 'सिबिल स्कोर छुपाएं' : 'Hide CIBIL score')
                                                    : (isHindi ? 'सिबिल स्कोर दिखाएं' : 'Show CIBIL score')}
                                            >
                                                <VisibilityIcon visible={showSensitiveFields.cibilScore} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {!hasVerifiedAaData && (
                                    <div className="detail-card" style={{ gridColumn: '1 / -1', background: '#eff6ff', borderColor: '#93c5fd' }}>
                                        <label style={{ color: '#1d4ed8' }}>{isHindi ? 'महत्वपूर्ण सूचना' : 'Important Note'}</label>
                                        <strong style={{ color: '#1e3a8a', fontSize: '1rem' }}>
                                            {isHindi
                                                ? 'यह केवल प्रारंभिक सीमा है। अकाउंट एग्रीगेटर देने के बाद वास्तविक पात्र राशि दिखाई जाएगी।'
                                                : 'This is only an indicative limit. After Account Aggregator verification, the actual eligible amount will be shown.'}
                                        </strong>
                                    </div>
                                )}
                                {/* Indicative Loan Breakdown Summary */}
                                {(() => {
                                    const tenure = selectedTenureDays;
                                    const { processingFee, gst } = computeCharges(offerDetailsPreviewAmount);
                                    const interest = offerDetailsPreviewAmount * 0.01 * tenure;
                                    const youReceive = offerDetailsPreviewAmount - processingFee - gst;
                                    const totalRepayable = offerDetailsPreviewAmount + interest;
                                    const fmt = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
                                    return (
                                        <div style={{ gridColumn: '1 / -1', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', fontSize: '0.9rem' }}>
                                            <p style={{ fontWeight: '700', color: '#1e293b', margin: '0 0 14px', fontSize: '1rem' }}>{isHindi ? '📋 सांकेतिक लोन ब्रेकडाउन' : '📋 Indicative Loan Breakdown'}</p>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#475569' }}>
                                                <span>{isHindi ? 'ऋण सीमा तक' : 'Loan Limit Upto'}</span>
                                                <strong style={{ color: '#1e293b' }}>{formatINR(offerDetailsPreviewAmount)}</strong>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#475569' }}>
                                                <span>{isHindi ? `प्रोसेसिंग शुल्क (${processingFeePercentLabel})` : `Processing Fee (${processingFeePercentLabel})`}{hasProcessorFeeDiscount && <span style={{ color: '#10b981', marginLeft: '6px', fontSize: '0.85rem' }}>{isHindi ? `🎁 ${spinFeeDiscountPercent}% छूट` : `🎁 ${spinFeeDiscountPercent}% OFF`}</span>}</span>
                                                <strong style={{ color: '#ef4444' }}>− {fmt(processingFee)}</strong>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#475569' }}>
                                                <span>{isHindi ? 'प्रोसेसिंग शुल्क पर GST (18%)' : 'GST on Processing Fee (18%)'}</span>
                                                <strong style={{ color: '#ef4444' }}>− {fmt(gst)}</strong>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px dashed #e2e8f0', color: '#475569' }}>
                                                <span>{isHindi ? `ब्याज (${interestPerDayLabel}/दिन × ${tenure} दिन)` : `Interest (${interestPerDayLabel}/day × ${tenure} days)`}</span>
                                                <strong style={{ color: '#f59e0b' }}>+ {fmt(interest)}</strong>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                <span style={{ fontWeight: '700', color: '#1e293b' }}>{isHindi ? 'आपको प्राप्त होगा' : 'You Will Receive'}</span>
                                                <strong style={{ color: '#10b981', fontSize: '1.05rem' }}>{fmt(youReceive)}</strong>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', background: '#fff7ed', padding: '10px 14px', borderRadius: '10px' }}>
                                                <span style={{ fontWeight: '700', color: '#ea580c' }}>{isHindi ? 'कुल देय राशि (लोन राशि + ब्याज)' : 'Total Repayable (Loan Amount + Interest)'}</span>
                                                <strong style={{ color: '#ea580c', fontSize: '1.1rem' }}>{fmt(totalRepayable)}</strong>
                                            </div>
                                        </div>
                                    );
                                })()}

                                <div className="details-action-row">
                                    {renderDashboardSaveButton({ fullWidth: true, marginTop: 0, justifyContent: 'center' })}
                                    <button
                                        type="button"
                                        className="action-btn details-action-btn"
                                        onClick={handleProceedFromDetails}
                                    >
                                        {isHindi ? 'आगे बढ़ें →' : 'Proceed →'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div className="bank-details-section">
                        <form className="bank-form" onSubmit={handleBankDetailsSubmit} noValidate>
                            <div className="moneyone-panel">
                                {(() => {
                                    const aaRestartActive = aaRestartRef.current.active;
                                    const aaVerifiedToday = (hasStrictAaVerified || hasBackendApprovedOffer) && !aaRestartActive;
                                    const aaTrackingStartMs = moneyOneState.consentStartedAt
                                        ? new Date(moneyOneState.consentStartedAt).getTime()
                                        : 0;
                                    const trackingElapsedSeconds = aaTrackingStartMs
                                        ? Math.max(0, Math.floor((aaTrackerNow - aaTrackingStartMs) / 1000))
                                        : 0;
                                    const aaStatusView = getAaStatusViewModel({
                                        aaStatus: aaRestartActive ? null : fetchedData?.aaStatus,
                                        hasFreshAaProfile: hasFreshAaProfile && !aaRestartActive,
                                        verifiedByOffer: hasBackendApprovedOffer && !aaRestartActive,
                                        moneyOneState,
                                        trackingElapsedSeconds
                                    });
                                    const aaPhase = aaRestartActive ? '' : String(fetchedData?.aaStatus?.phase || '').trim().toUpperCase();
                                    const hasReusableConsentLink = Boolean(moneyOneState.redirectUrl);
                                    const hasPendingConsentSession = Boolean(moneyOneState.consentHandle || moneyOneState.consentId);
                                    const hasConfirmedConsent = Boolean(
                                        String(moneyOneState.consentId || (aaRestartActive ? '' : fetchedData?.aaStatus?.consentId) || '').trim()
                                    );
                                    const isAaDataPending = aaPhase === 'DATA_PENDING' && hasConfirmedConsent;
                                    const isConsentAwaitingUserAction = !aaVerifiedToday
                                        && hasPendingConsentSession
                                        && !hasConfirmedConsent
                                        && !moneyOneState.consentWindowOpen
                                        && !moneyOneState.polling
                                        && !moneyOneState.fetchingData;
                                    const isAaJourneyLocked = Boolean(
                                        moneyOneState.connecting ||
                                        moneyOneState.polling ||
                                        moneyOneState.fetchingData
                                    );
                                    const isAutoTracking = !aaVerifiedToday && (
                                        !moneyOneState.accountFetchBlocked && (
                                            moneyOneState.connecting ||
                                            moneyOneState.polling ||
                                            moneyOneState.fetchingData ||
                                            (!moneyOneState.accountFetchComplete && Boolean(moneyOneState.consentId)) ||
                                            Boolean(moneyOneState.consentId)
                                        )
                                    );
                                    const showAaWaitingLoader = !aaVerifiedToday && (
                                        moneyOneState.connecting ||
                                        moneyOneState.polling ||
                                        moneyOneState.fetchingData ||
                                        Boolean(moneyOneState.consentId) ||
                                        isAaDataPending
                                    );
                                    const trackerSubtext = aaVerifiedToday
                                        ? 'Bank AA verified'
                                        : moneyOneState.connecting
                                            ? 'Opening CAMS journey'
                                            : moneyOneState.consentWindowOpen
                                                ? 'Consent popup is open'
                                                : moneyOneState.fetchingData
                                                    ? 'Fetching bank data automatically'
                                                    : isAutoTracking
                                                        ? 'Tracking automatically'
                                                        : 'Awaiting AA data';
                                    const primaryButtonLabel = aaStatusView.label === 'Verified'
                                        ? 'Verified'
                                        : moneyOneState.connecting
                                            ? 'Opening...'
                                            : moneyOneState.consentWindowOpen
                                                ? 'Complete Consent...'
                                                : moneyOneState.polling
                                                    ? 'Tracking Status...'
                                                    : moneyOneState.fetchingData
                                                        ? 'Fetching Data...'
                                                        : isAaDataPending
                                                            ? 'Waiting for Data...'
                                                            : isAutoTracking
                                                                ? 'Tracking Status...'
                                                                : isConsentAwaitingUserAction
                                                                    ? 'Start Validation'
                                                                : 'Start Validation';
                                    return (
                                        <div className="moneyone-panel__header">
                                            <div className="moneyone-panel__topline">
                                                <div className="moneyone-panel__intro">
                                                    <h3>Validate Salary Account</h3>
                                                    <p>Verify salary inflow from your bank before disbursement.</p>
                                                </div>
                                                <div className={`moneyone-status-chip moneyone-status-chip--${aaStatusView.tone}`}>
                                                    <span className="moneyone-status-chip__dot"></span>
                                                    {aaStatusView.label}
                                                </div>
                                            </div>
                                            {(moneyOneState.status || moneyOneState.consentId || moneyOneState.connecting || fetchedData?.aaStatus?.status) && (
                                                <div className="moneyone-meta">
                                                    <div className="moneyone-meta__message">
                                                        <span className="moneyone-meta__icon">
                                                            {aaStatusView.tone === 'success' ? '✓' : aaStatusView.tone === 'error' ? '!' : 'i'}
                                                        </span>
                                                        <div>
                                                            <span>{aaStatusView.helperMessage}</span>
                                                            <span style={{ display: 'block', marginTop: '6px', fontSize: '0.84rem', opacity: 0.8, minHeight: '1.1em' }}>
                                                                {trackerSubtext}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {!aaVerifiedToday && moneyOneState.status === 'WAITING_FOR_CONSENT' && hasReusableConsentLink && !isAutoTracking && (
                                                        <div className="moneyone-meta__actions">
                                                            <button
                                                                type="button"
                                                                className="secondary-btn moneyone-secondary-btn"
                                                                onClick={reopenMoneyoneConsent}
                                                                disabled={loading || moneyOneState.connecting || moneyOneState.fetchingData}
                                                            >
                                                                Reopen AA Window
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {showAaWaitingLoader && (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px',
                                                    marginTop: '14px',
                                                    padding: '12px 14px',
                                                    borderRadius: '14px',
                                                    background: '#eff6ff',
                                                    border: '1px solid #bfdbfe',
                                                    color: '#1d4ed8'
                                                }}>
                                                    <div className="spinner" style={{ width: '20px', height: '20px', minWidth: '20px' }}></div>
                                                    <div style={{ fontSize: '0.92rem', fontWeight: 600 }}>
                                                        Fetching AA bank data. Please wait and do not press the button again.
                                                    </div>
                                                </div>
                                            )}
                                            {!aaVerifiedToday && (
                                                <div className="moneyone-actions">
                                                    <button
                                                        type="button"
                                                        className="action-btn moneyone-primary-btn"
                                                        onClick={isAutoTracking ? undefined : handleMoneyoneConsent}
                                                        disabled={loading || isAaDataPending || isAaJourneyLocked || isAutoTracking}
                                                    >
                                                        {primaryButtonLabel}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            {savedBankSuggestion?.accountNumber && savedBankSuggestion?.ifscCode && (
                                <div
                                    style={{
                                        marginBottom: '16px',
                                        padding: '12px 14px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '12px',
                                        background: '#f8fafc'
                                    }}
                                >
                                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a', marginBottom: '6px' }}>
                                        Suggested verified bank details
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#334155', marginBottom: '10px' }}>
                                        Salaried Account: {maskTrailingVisible(savedBankSuggestion.accountNumber)} | IFSC: {maskTrailingVisible(savedBankSuggestion.ifscCode)}
                                    </div>
                                    <button
                                        type="button"
                                        className="action-btn"
                                        style={{ minWidth: 'auto', padding: '10px 14px' }}
                                        onClick={() => setBankDetails({
                                            accountNumber: savedBankSuggestion.accountNumber,
                                            ifscCode: savedBankSuggestion.ifscCode
                                        })}
                                    >
                                        Use Saved Details
                                    </button>
                                </div>
                            )}
                            {!isAaDecisionReady && (
                                <div style={{
                                    marginBottom: '14px',
                                    padding: '10px 12px',
                                    borderRadius: '10px',
                                    border: '1px solid #bfdbfe',
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    fontSize: '0.9rem',
                                    fontWeight: 600
                                }}>
                                    We are fetching and analyzing your AA data. Account verification will unlock automatically once this step is complete.
                                </div>
                            )}
                            <div className="form-group" ref={accountFieldRef}>
                                <label htmlFor="accountNumber" className="bank-field-heading">Enter Your Salaried Account Number Only</label>
                                <div className="field-helper-text">Required for penny drop, bank verification, and disbursement. The account aggregator does not expose the full account number here.</div>
                                <div className="input-with-toggle">
                                    <input
                                        type={showSensitiveFields.accountNumber ? 'tel' : 'password'}
                                        id="accountNumber"
                                        placeholder="Enter your salaried account number"
                                        value={bankDetails.accountNumber}
                                        onChange={(e) => {
                                            setBankDetailsError('');
                                            setAccountNumberError('');
                                            setBankVerificationCompleted(false);
                                            setBankReviewPending({ isPending: false, message: '', accountHolderName: '', panHolderName: '' });
                                            setBankDetails({ ...bankDetails, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 20) });
                                        }}
                                        onFocus={() => {
                                            scrollFieldIntoKeyboardView(accountFieldRef);
                                        }}
                                        required
                                        maxLength="20"
                                        inputMode="numeric"
                                        data-layout="numeric"
                                        autoComplete="off"
                                        disabled={!isAaDecisionReady || loading}
                                    />
                                    <button
                                        type="button"
                                        className="visibility-toggle-btn"
                                        onClick={() => toggleFieldVisibility('accountNumber')}
                                        aria-label={showSensitiveFields.accountNumber ? 'Hide account number' : 'Show account number'}
                                        disabled={!isAaDecisionReady || loading}
                                    >
                                        <VisibilityIcon visible={showSensitiveFields.accountNumber} />
                                    </button>
                                </div>
                                {accountNumberError ? (
                                    <div className="bank-validation-error" style={{ marginTop: '8px' }}>
                                        {accountNumberError}
                                    </div>
                                ) : null}
                            </div>
                            <div className="form-group" ref={ifscFieldRef}>
                                <label htmlFor="ifscCode" className="bank-field-heading">IFSC Code</label>
                                <div className="input-with-toggle">
                                    <input
                                        type={showSensitiveFields.ifscCode ? 'text' : 'password'}
                                        id="ifscCode"
                                        placeholder="e.g. HDFC0001234"
                                        value={bankDetails.ifscCode}
                                        onChange={(e) => {
                                            setBankDetailsError('');
                                            setIfscCodeError('');
                                            setBankVerificationCompleted(false);
                                            setBankReviewPending({ isPending: false, message: '', accountHolderName: '', panHolderName: '' });
                                            setBankDetails({ ...bankDetails, ifscCode: e.target.value.toUpperCase().slice(0, 11) });
                                        }}
                                        onFocus={() => {
                                            scrollFieldIntoKeyboardView(ifscFieldRef);
                                        }}
                                        required
                                        autoComplete="off"
                                        spellCheck="false"
                                        inputMode="none"
                                        data-layout="alphanumeric"
                                        disabled={!isAaDecisionReady || loading}
                                    />
                                    <button
                                        type="button"
                                        className="visibility-toggle-btn"
                                        onClick={() => toggleFieldVisibility('ifscCode')}
                                        aria-label={showSensitiveFields.ifscCode ? 'Hide IFSC code' : 'Show IFSC code'}
                                        disabled={!isAaDecisionReady || loading}
                                    >
                                        <VisibilityIcon visible={showSensitiveFields.ifscCode} />
                                    </button>
                                </div>
                                {ifscCodeError ? (
                                    <div className="bank-validation-error" style={{ marginTop: '8px' }}>
                                        {ifscCodeError}
                                    </div>
                                ) : null}
                            </div>
                            {bankDetailsError && (
                                <div className="bank-validation-error">
                                    {bankDetailsError}
                                </div>
                            )}
                            {bankReviewPending?.isPending && (
                                <div style={{
                                    marginBottom: '16px',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    background: '#fffbeb',
                                    border: '1.5px solid #f59e0b',
                                    color: '#92400e'
                                }}>
                                    <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>⏳</span> Bank Account Under CRM Review
                                    </div>
                                    <div style={{ fontSize: '0.9rem', lineHeight: 1.5, marginBottom: '10px' }}>
                                        {bankReviewPending.message || "Aapka bank account name verification ke liye submit ho gaya hai. CRM team se approval milte hi aap aage proceed kar payenge."}
                                    </div>
                                    {bankReviewPending.accountHolderName && bankReviewPending.panHolderName && (
                                        <div style={{ fontSize: '0.84rem', color: '#b45309', marginBottom: '10px', background: '#fef3c7', padding: '8px 10px', borderRadius: '8px' }}>
                                            <div><strong>Name on Bank:</strong> {bankReviewPending.accountHolderName}</div>
                                            <div><strong>Name on PAN:</strong> {bankReviewPending.panHolderName}</div>
                                        </div>
                                    )}
                                    <div style={{ fontSize: '0.82rem', color: '#78350f', fontWeight: '500' }}>
                                        Please wait for CRM approval. Click below to check status.
                                    </div>
                                </div>
                            )}
                            <div className="form-group" style={{ marginTop: '-4px' }}>
                                <label style={{ marginBottom: '8px', color: '#000' }}>Consent <span style={{ color: '#ef4444' }}>*</span></label>
                                <label htmlFor="bankConsent" style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#000', fontSize: '1.08rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        id="bankConsent"
                                        name="bankConsent"
                                        checked={bankConsent}
                                        onChange={(e) => setBankConsent(e.target.checked)}
                                        style={{ display: 'none' }}
                                    />
                                    <span
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            border: '2px solid #64748b',
                                            borderRadius: '3px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: bankConsent ? '#16a34a' : '#fff',
                                            color: '#fff',
                                            fontWeight: '800',
                                            fontSize: '16px',
                                            lineHeight: 1
                                        }}
                                    >
                                        {bankConsent ? '✓' : ''}
                                    </span>
                                    I consent to bank account verification and data processing.
                                </label>
                            </div>
                            <div className="details-action-row">
                                <button type="button" className="secondary-btn" onClick={() => setStep(1)} style={{ flex: 0.35 }}>Back</button>
                                <button type="submit" className="action-btn" style={{ flex: 1, marginTop: 0 }} disabled={!isAaDecisionReady || loading}>
                                    {bankReviewPending?.isPending ? (loading ? 'Checking Approval...' : 'Check Approval Status 🔄') : 'Verify Account & Continue →'}
                                </button>
                            </div>
                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-start' }}>
                                <button type="button" className="secondary-btn dashboard-save-btn" onClick={handleBackToDashboard} style={{ color: '#000' }} disabled={isSavingProgress}>
                                    {isSavingProgress ? 'Returning...' : 'Back to Dashboard'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {step === 3 && (
                    <div className="loan-breakdown-section">
                        <div className="summary-card sanction-summary-card" style={{ background: '#f8fafc', padding: '30px', borderRadius: '24px', border: '2px solid #e2e8f0' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '15px', textAlign: 'center' }}>🎚️</div>
                            <h3 style={{ fontSize: '1.5rem', color: '#1e293b', textAlign: 'center', marginBottom: '8px' }}>Customize Your Loan</h3>
                                <p style={{ color: '#64748b', textAlign: 'center', marginBottom: '24px' }}>
                                Choose the amount and tenure. Your due date is calculated from today and the selected tenure.
                            </p>
                            <div style={{ background: '#fff7ed', color: '#9a3412', padding: '10px 14px', borderRadius: '999px', display: 'inline-flex', margin: '0 auto 24px', fontWeight: '700' }}>
                                Approved limit: {formatINR(approvedLimitAmount)} | Max repayment date: {maxRepaymentDateLabel}
                            </div>
                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#1e293b', fontWeight: '700' }}>
                                    <label style={{ marginBottom: 0 }}>{isHindi ? 'लोन राशि' : 'Loan Amount'}</label>
                                    <span>{formatINR(selectedAmount)}</span>
                                </div>
                                <input
                                    type="range"
                                    min={minimumSelectableAmount}
                                    max={approvedLimitAmount}
                                    step={amountSliderStep}
                                    value={selectedAmount}
                                    onChange={(e) => setSelectedAmount(Number(e.target.value))}
                                    className="loan-range amount-range"
                                    style={{
                                        background: `linear-gradient(to right, #ea580c 0%, #ea580c ${(selectedAmount / Math.max(approvedLimitAmount, 1)) * 100}%, #e2e8f0 ${(selectedAmount / Math.max(approvedLimitAmount, 1)) * 100}%, #e2e8f0 100%)`
                                    }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '0.9rem' }}>
                                    <span>{formatINR(minimumSelectableAmount)}</span>
                                    <span>{formatINR(approvedLimitAmount)}</span>
                                </div>
                            </div>
                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#1e293b', fontWeight: '700' }}>
                                    <label style={{ marginBottom: 0 }}>{isHindi ? 'लोन अवधि' : 'Loan Tenure'}</label>
                                    <span>{selectedTenureDays} {isHindi ? 'दिन' : 'Days'}</span>
                                </div>
                                <input
                                    type="range"
                                    min={MIN_LOAN_TENURE_DAYS}
                                    max={maxRepaymentTenureDays}
                                    step="1"
                                    value={selectedTenureDays}
                                    onChange={(e) => {
                                        const rawVal = Number(e.target.value);
                                        const direction = rawVal > selectedTenureDays ? 1 : (rawVal < selectedTenureDays ? -1 : 0);
                                        const nextTenure = resolveNonSundayTenure(rawVal, {
                                            baseDate: calculationAnchorDate,
                                            minDays: MIN_LOAN_TENURE_DAYS,
                                            maxDays: maxRepaymentTenureDays,
                                            preferredDirection: direction
                                        });
                                        setSelectedTenure(nextTenure);
                                    }}
                                    className="loan-range tenure-range"
                                    style={{
                                        background: `linear-gradient(to right, #ea580c 0%, #ea580c ${((selectedTenureDays - MIN_LOAN_TENURE_DAYS) / Math.max(maxRepaymentTenureDays - MIN_LOAN_TENURE_DAYS, 1)) * 100}%, #e2e8f0 ${((selectedTenureDays - MIN_LOAN_TENURE_DAYS) / Math.max(maxRepaymentTenureDays - MIN_LOAN_TENURE_DAYS, 1)) * 100}%, #e2e8f0 100%)`
                                    }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '0.88rem', marginTop: '8px', lineHeight: 1.4 }}>
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{isHindi ? 'लोन तिथि' : 'Loan date:'}</div>
                                        <div style={{ fontWeight: 600, color: '#334155' }}>{loanDateLabel}</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#ea580c', fontWeight: 600 }}>{isHindi ? 'चयनित पुनर्भुगतान तिथि' : 'Selected repayment date:'}</div>
                                        <div style={{ fontWeight: 700, color: '#ea580c' }}>{selectedRepaymentDateLabel}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{isHindi ? 'अंतिम पुनर्भुगतान तिथि' : 'Latest repayment date:'}</div>
                                        <div style={{ fontWeight: 600, color: '#334155' }}>{maxRepaymentDateLabel}</div>
                                    </div>
                                </div>
                                <div style={{ marginTop: '8px', color: '#9a3412', fontSize: '0.85rem', lineHeight: 1.5 }}>
                                    {isHindi
                                        ? 'यदि भुगतान देय तिथि के बाद किया जाता है, तो देय तिथि के बाद भुगतान की वास्तविक तिथि तक अतिरिक्त 1% प्रति दिन पेनल्टी लागू होगी। (रविवार देय तिथि के लिए मान्य नहीं है)'
                                        : 'If repayment is made after the due date, an additional 1% per day penalty applies until the actual payment date. (Sundays are excluded for repayment)'}
                                </div>
                            </div>
                            {(() => {
                                const { processingFee, gst } = computeCharges(selectedAmount);
                                const baseInterest = selectedAmount * interestPerDayRate * selectedTenureDays;
                                const interest = baseInterest;
                                const youReceive = selectedAmount - processingFee - gst;
                                const totalRepayable = selectedAmount + interest;
                                const fmt = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
                                return (
                                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginTop: '18px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#475569' }}>
                                            <span>{isHindi ? 'लोन राशि' : 'Loan Amount'}</span>
                                            <strong style={{ color: '#1e293b' }}>{fmt(selectedAmount)}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#475569' }}>
                                            <span>{isHindi ? `प्रोसेसिंग शुल्क (${processingFeePercentLabel})` : `Processing Fee (${processingFeePercentLabel})`}{hasProcessorFeeDiscount && <span style={{ color: '#10b981', marginLeft: '6px', fontSize: '0.85rem' }}>{isHindi ? `🎁 ${spinFeeDiscountPercent}% छूट` : `🎁 ${spinFeeDiscountPercent}% OFF`}</span>}</span>
                                            <strong style={{ color: '#ef4444' }}>− {fmt(processingFee)}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#475569' }}>
                                            <span>{isHindi ? 'प्रोसेसिंग शुल्क पर GST (18%)' : 'GST on Processing Fee (18%)'}</span>
                                            <strong style={{ color: '#ef4444' }}>− {fmt(gst)}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#475569' }}>
                                            <span>{isHindi ? `ब्याज (देय तिथि तक)` : 'Interest until due date'}</span>
                                            <strong style={{ color: '#f59e0b' }}>+ {fmt(baseInterest)}</strong>
                                        </div>
                                        <div style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px dashed #e2e8f0', color: '#475569', fontSize: '0.88rem', lineHeight: 1.5 }}>
                                            {isHindi
                                                ? `यदि भुगतान देय तिथि के बाद किया जाता है, तो देय तिथि के बाद भुगतान की वास्तविक तिथि तक अतिरिक्त 1% प्रति दिन पेनल्टी लागू होगी।`
                                                : 'If payment is made after the due date, an additional 1% per day penalty applies from the due date until the actual payment date.'}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                            <span style={{ fontWeight: '700', color: '#1e293b' }}>{isHindi ? 'आपको प्राप्त होगा' : 'You Will Receive'}</span>
                                            <strong style={{ color: '#10b981', fontSize: '1.05rem' }}>{fmt(youReceive)}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', background: '#fff7ed', padding: '10px 14px', borderRadius: '10px' }}>
                                            <span style={{ fontWeight: '700', color: '#ea580c' }}>{isHindi ? 'कुल देय राशि (लोन राशि + ब्याज)' : 'Total Repayable on Due Date (Loan Amount + Interest)'}</span>
                                            <strong style={{ color: '#ea580c', fontSize: '1.1rem' }}>{fmt(totalRepayable)}</strong>
                                        </div>
                                    </div>
                                );
                            })()}
                            <div style={{ display: 'flex', gap: '15px', marginTop: '24px' }}>
                                <button type="button" className="secondary-btn" onClick={() => setStep(2)} style={{ flex: 0.35, fontSize: '1rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>Back</button>
                                <button type="button" className="action-btn" onClick={handleLoanSelectionSubmit} style={{ flex: 1, marginTop: 0, fontSize: '1rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>Proceed to E-Aadhaar Verification →</button>
                            </div>
                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-start' }}>
                                <button type="button" className="secondary-btn dashboard-save-btn" onClick={handleBackToDashboard} style={{ color: '#000' }} disabled={isSavingProgress}>
                                    {isSavingProgress ? 'Returning...' : 'Back to Dashboard'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {step === 5 && (
                    <div className="kyc-section">
                        {selfieRecaptureBanner && (
                            <div style={{
                                background: '#fff7ed',
                                border: '1px solid #fdba74',
                                color: '#9a3412',
                                padding: '12px 16px',
                                borderRadius: '14px',
                                marginBottom: '18px',
                                fontSize: '0.95rem',
                                fontWeight: '600'
                            }}>
                                {selfieRecaptureBanner}
                            </div>
                        )}
                        <div className="live-photo-container">
                            {livePhoto ? (
                                <div className="captured-photo-shell">
                                    <img src={livePhoto} alt="Captured" className="camera-feed" />
                                </div>
                            ) : (
                                <>
                                    <video ref={videoRef} autoPlay playsInline muted className="camera-feed"></video>
                                    <button className="capture-btn" onClick={handleCapture}></button>
                                </>
                            )}
                        </div>
                        {livePhoto && (
                            <div className="retake-btn-row">
                                <button onClick={handleRetake} className="retake-btn" type="button">
                                    <span>🔄</span> Retake
                                </button>
                            </div>
                        )}

                        <div style={{
                            background: '#fef2f2',
                            border: '1px solid #fee2e2',
                            padding: '12px 20px',
                            borderRadius: '16px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            animation: 'fadeIn 0.5s ease-out'
                        }}>
                            <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
                            <p style={{ color: '#991b1b', fontSize: '0.95rem', margin: 0, fontWeight: '500' }}>
                                Please make sure that the selfie is clean and no other person is present in the frame.
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '15px' }}>
                            <button className="secondary-btn" onClick={() => setStep(4)} style={{ flex: 1, minHeight: '88px', fontSize: '1rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>Back</button>
                            <button className="action-btn" onClick={handleSelfieSubmit} disabled={!livePhoto} style={{ flex: 1, minHeight: '88px', marginTop: 0, fontSize: '1rem', fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>
                                {livePhoto ? 'Verify Selfie & Continue →' : 'Proceed'}
                            </button>
                        </div>
                        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-start' }}>
                            <button type="button" className="secondary-btn dashboard-save-btn" onClick={handleBackToDashboard} style={{ color: '#000' }} disabled={isSavingProgress}>
                                {isSavingProgress ? 'Returning...' : 'Back to Dashboard'}
                            </button>
                        </div>
                    </div>
                )}


                {step === 4 && (
                    <div className="aadhaar-section">
                        <div className="aadhaar-hero" style={{ background: '#f8fafc', padding: '30px', borderRadius: '24px', marginBottom: '30px', border: '2px solid #e2e8f0' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '15px', textAlign: 'center' }}>🛡️</div>
                            <h3 style={{ fontSize: '1.5rem', color: '#1e293b', textAlign: 'center' }}>Final Verification</h3>
                            <p style={{ color: '#64748b', marginBottom: '25px', textAlign: 'center' }}>
                                Complete e-Aadhaar verification before selfie KYC.
                            </p>

                            <form onSubmit={handleAadhaarVerificationContinue} noValidate>
                                <div className="form-group">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label className="email-section-label" style={{ margin: 0 }}>Email</label>
                                        {isEmailVerified && (
                                            <span style={{ color: '#16a34a', fontWeight: 'bold', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                ✓ Verified
                                            </span>
                                        )}
                                    </div>
                                    <div className="email-verification-card" style={{ background: '#ffffff', border: isEmailVerified ? '2px solid #86efac' : '2px solid #e2e8f0', borderRadius: '16px', padding: '16px' }}>
                                        <div className="email-verification-card__body">
                                            <div className="email-verification-card__row" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    id="officialEmail"
                                                    className="email-verification-card__input"
                                                    placeholder="Enter email address"
                                                    data-layout="email"
                                                    value={officialEmailDraft}
                                                    disabled={isEmailVerified}
                                                    onFocus={() => setIsEmailEditing(true)}
                                                    onChange={(e) => {
                                                        const nextEmail = e.target.value;
                                                        setOfficialEmailDraft(nextEmail);
                                                        if (isEmailVerified) {
                                                            setIsEmailVerified(false);
                                                            setVerifiedEmailAddress('');
                                                            setEmailOtpSent(false);
                                                            setEmailOtp('');
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        setIsEmailEditing(false);
                                                        const normalizedEmail = e.target.value.trim().toLowerCase();
                                                        setOfficialEmailDraft(normalizedEmail);
                                                        setVerificationData((prev) => ({
                                                            ...prev,
                                                            officialEmail: normalizedEmail
                                                        }));
                                                    }}
                                                    maxLength={254}
                                                    autoComplete="email"
                                                />
                                                {!isEmailVerified ? (
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        style={{
                                                            background: '#ea580c',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '10px',
                                                            padding: '12px 18px',
                                                            fontWeight: '600',
                                                            cursor: (!officialEmailDraft || !isValidEmail(officialEmailDraft) || isSendingEmailOtp || emailOtpCountdown > 0) ? 'not-allowed' : 'pointer',
                                                            opacity: (!officialEmailDraft || !isValidEmail(officialEmailDraft) || isSendingEmailOtp || emailOtpCountdown > 0) ? 0.6 : 1,
                                                            whiteSpace: 'nowrap',
                                                            minWidth: '110px'
                                                        }}
                                                        disabled={!officialEmailDraft || !isValidEmail(officialEmailDraft) || isSendingEmailOtp || emailOtpCountdown > 0}
                                                        onClick={handleSendEmailOtp}
                                                    >
                                                        {isSendingEmailOtp ? 'Sending...' : emailOtpCountdown > 0 ? `Resend (${emailOtpCountdown}s)` : emailOtpSent ? 'Resend OTP' : 'Send OTP'}
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        style={{
                                                            background: '#f1f5f9',
                                                            color: '#475569',
                                                            border: '1px solid #cbd5e1',
                                                            borderRadius: '10px',
                                                            padding: '10px 16px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            fontSize: '0.85rem'
                                                        }}
                                                        onClick={() => {
                                                            setIsEmailVerified(false);
                                                            setVerifiedEmailAddress('');
                                                            setEmailOtpSent(false);
                                                            setEmailOtp('');
                                                            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                                                            const cleanMobile = String(storedUser.mobile_number || storedUser.mobile || fetchedData?.mobile || '').replace(/\D/g, '').slice(-10);
                                                            if (cleanMobile) {
                                                                clearCustomerVerifiedEmail(cleanMobile);
                                                            }
                                                        }}
                                                    >
                                                        Change
                                                    </button>
                                                )}
                                            </div>

                                            {/* Email OTP Verification box */}
                                            {emailOtpSent && !isEmailVerified && (
                                                <div style={{ marginTop: '12px', padding: '14px', background: '#fff7ed', borderRadius: '12px', border: '1px solid #fed7aa' }}>
                                                    <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#c2410c', fontWeight: '500' }}>
                                                        OTP has been sent to <strong>{officialEmailDraft}</strong>. Please check your inbox.
                                                    </p>
                                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            maxLength={4}
                                                            placeholder="Enter 4-digit OTP"
                                                            value={emailOtp}
                                                            onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                                            style={{
                                                                width: '180px',
                                                                padding: '10px 14px',
                                                                borderRadius: '8px',
                                                                border: '2px solid #fdba74',
                                                                fontSize: '1.1rem',
                                                                letterSpacing: '4px',
                                                                textAlign: 'center',
                                                                fontWeight: '700',
                                                                background: '#fff'
                                                            }}
                                                            data-layout="numeric"
                                                        />
                                                        <button
                                                            type="button"
                                                            style={{
                                                                background: emailOtp.length === 4 ? '#16a34a' : '#94a3b8',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                padding: '10px 20px',
                                                                fontWeight: '600',
                                                                cursor: emailOtp.length === 4 && !isVerifyingEmailOtp ? 'pointer' : 'not-allowed',
                                                                minWidth: '110px'
                                                            }}
                                                            disabled={emailOtp.length !== 4 || isVerifyingEmailOtp}
                                                            onClick={handleVerifyEmailOtp}
                                                        >
                                                            {isVerifyingEmailOtp ? 'Verifying...' : 'Verify OTP'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            <span className="email-verification-card__hint" style={{ color: isEmailVerified ? '#16a34a' : '#64748b' }}>
                                                {isEmailVerified
                                                    ? '✓ This email is verified and ready for loan communication.'
                                                    : officialEmailDraft && !isValidEmail(officialEmailDraft)
                                                        ? getEmailValidationMessage(officialEmailDraft)
                                                        : emailOtpSent
                                                            ? 'Please enter the 4-digit OTP sent to your email.'
                                                            : 'Enter email and click Send OTP to verify.'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>e-Aadhaar Verification</label>
                                    <div className="digilocker-panel">
                                        <div className="digilocker-panel-header">
                                            <div>
                                                <h4>Aadhaar verification</h4>
                                                <p>Click the button below to continue e-Aadhaar verification in a separate verification window.</p>
                                            </div>
                                            <div className={`digilocker-status-circle ${isAadhaarVerified ? 'success' : digilockerState.polling ? 'pending' : 'idle'}`}>
                                                {isAadhaarVerified ? '✓' : ''}
                                            </div>
                                        </div>
                                        <div className="digilocker-status-text">
                                            {isAadhaarVerified ? 'e-Aadhaar check done' : digilockerState.polling ? 'Waiting for e-Aadhaar approval' : 'Not started'}
                                        </div>

                                        <div className="digilocker-actions">
                                            <button
                                                type="button"
                                                className="action-btn digilocker-primary-btn"
                                                onClick={handleCreateDigilockerUrl}
                                                disabled={digilockerState.creating || isAadhaarVerified}
                                            >
                                                {isAadhaarVerified
                                                    ? 'e-Aadhaar Already Verified'
                                                    : digilockerState.creating
                                                        ? 'Starting verification...'
                                                        : 'Start e-Aadhaar Verification'}
                                            </button>
                                        </div>

                                        {digilockerState.polling && (
                                            <div className="digilocker-auto-note">
                                                Verification will update here automatically once e-Aadhaar is approved.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {isAadhaarVerified && aadhaarFetchState.details?.name && (
                                    <div className="form-group slide-down">
                                        <label>Verified e-Aadhaar Details</label>
                                        <div className="digilocker-result-card">
                                            <div><strong>Name:</strong> {getLocalizedName(aadhaarFetchState.details.name, language) || aadhaarFetchState.details.name}</div>
                                            {aadhaarFetchState.details.aadhaarLast4 && (
                                                <div style={{ marginTop: '4px' }}><strong>Aadhaar Last 4:</strong> **** **** {aadhaarFetchState.details.aadhaarLast4}</div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="form-group aadhaar-consent-group">
                                    <label className="aadhaar-consent-title">Consent <span style={{ color: '#ef4444' }}>*</span></label>
                                    <label htmlFor="aadhaarConsent" className="aadhaar-consent-card">
                                        <input
                                            type="checkbox"
                                            id="aadhaarConsent"
                                            name="aadhaarConsent"
                                            checked={aadhaarConsent}
                                            onChange={(e) => setAadhaarConsent(e.target.checked)}
                                            style={{ display: 'none' }}
                                        />
                                        <span className={`aadhaar-consent-check ${aadhaarConsent ? 'aadhaar-consent-check--checked' : ''}`}>
                                            {aadhaarConsent ? '✓' : ''}
                                        </span>
                                        <span className="aadhaar-consent-text">
                                            I consent to e-Aadhaar verification and data processing.
                                        </span>
                                    </label>
                                </div>

                                {identityReviewPending.isPending && (
                                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '14px', padding: '16px', marginTop: '16px', color: '#92400e' }}>
                                        <div style={{ fontWeight: '700', marginBottom: '6px', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span>⏳</span> Aadhaar Name Under CRM Review
                                        </div>
                                        <div style={{ fontSize: '0.9rem', marginBottom: '8px', lineHeight: '1.4' }}>
                                            Aadhaar name <strong>{identityReviewPending.aadhaarName}</strong> aur PAN name <strong>{identityReviewPending.expectedName}</strong> match nahi hue hain.
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#b45309' }}>
                                            CRM team se approve hone ke baad neeche <strong>Check Approval Status 🔄</strong> dabayein.
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="action-btn"
                                    style={{ width: '100%', padding: '18px', fontSize: '1.1rem', marginTop: '20px' }}
                                    disabled={loading || isCheckingEmailActive}
                                >
                                    {isCheckingEmailActive
                                        ? 'Verifying Active Email...'
                                        : loading
                                            ? 'Verifying Details...'
                                            : identityReviewPending.isPending
                                                ? 'Check Approval Status 🔄'
                                                : 'Continue to Selfie KYC →'}
                                </button>
                                {renderDashboardSaveButton()}
                            </form>
                        </div>
                    </div>
                )}

                {step === 6 && (
                    <div className="loan-breakdown-section">
                        <div className="summary-card" style={{ background: '#f8fafc', padding: '30px', borderRadius: '24px', border: '2px solid #e2e8f0' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '15px', textAlign: 'center' }}>📄</div>
                            <h3 style={{ fontSize: '1.5rem', color: '#1e293b', textAlign: 'center', marginBottom: '25px' }}>Loan Summary</h3>

                            {loanJourney?.loanId && (
                                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', marginBottom: '18px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#64748b' }}>Loan ID</span>
                                        <strong style={{ color: '#0f172a' }}>{loanJourney.loanId}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#64748b' }}>Sanction Date</span>
                                        <strong style={{ color: '#0f172a' }}>{resolvedSanctionDate || '--'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#64748b' }}>Date of Birth</span>
                                        <strong style={{ color: '#0f172a' }}>{loanJourney.dateOfBirth || fetchedData?.dob || '--'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#64748b' }}>Bank / IFSC</span>
                                        <strong style={{ color: '#0f172a' }}>{resolvedLoanJourneyBankDetails.bankName || '--'} / {resolvedLoanJourneyBankDetails.ifscCode || '--'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#64748b' }}>Account</span>
                                        <strong style={{ color: '#0f172a' }}>XXXXXX{resolvedLoanJourneyBankDetails.accountLast4 || '----'}</strong>
                                    </div>
                                </div>
                            )}

                            <div className="breakdown-list" style={{ color: '#475569' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', fontSize: '1.1rem' }}>
                                    <span>Sanctioned Loan Amount</span>
                                    <strong style={{ color: '#1e293b' }}>{formatINR(sanctionedAmount)}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <span>Processing Fee ({processingFeePercentLabel}) {hasProcessorFeeDiscount && <span style={{ color: '#10b981', fontSize: '1rem' }}>🎁 {spinFeeDiscountPercent}% OFF</span>}</span>
                                    <strong>- {formatINR(processingFee)}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
                                    <span>GST on Processing Fee (18%)</span>
                                    <strong>- {formatINR(gstAmount)}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.4rem' }}>
                                    <span style={{ fontWeight: '600', color: '#1e293b' }}>Final Disbursement Amount</span>
                                    <strong style={{ color: '#10b981' }}>{formatINR(finalDisbursement)}</strong>
                                </div>
                            </div>

                            <div className="signature-card">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
                                    <div>
                                        <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Borrower Signature</div>
                                        <div style={{ color: '#64748b', fontSize: '0.95rem' }}>Draw your signature in the box below. It will be saved and printed in the sanction letter.</div>
                                    </div>
                                </div>
                                {!borrowerSignatureSubmitted ? (
                                    <div
                                        className="signature-canvas-shell"
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            height: '220px',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '14px',
                                            overflow: 'hidden',
                                            background: '#fff',
                                            touchAction: 'none',
                                            userSelect: 'none',
                                            WebkitUserSelect: 'none'
                                        }}
                                    >
                                        <svg
                                            ref={borrowerSignatureCanvasRef}
                                            width="100%"
                                            height="100%"
                                            viewBox={`0 0 ${Math.max(1, Math.round(borrowerSignatureSurfaceSize.width || 900))} ${Math.max(1, Math.round(borrowerSignatureSurfaceSize.height || 220))}`}
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                pointerEvents: 'auto',
                                                touchAction: 'none',
                                                userSelect: 'none',
                                                WebkitUserSelect: 'none',
                                                WebkitTouchCallout: 'none',
                                                display: 'block',
                                                cursor: 'crosshair',
                                                background: '#fff'
                                            }}
                                        >
                                            <rect
                                                x="0"
                                                y="0"
                                                width="100%"
                                                height="100%"
                                                fill="transparent"
                                            />
                                            {!borrowerSignaturePath && (
                                                <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fill="#94a3b8" style={{ font: '600 18px Inter, sans-serif' }}>
                                                    Sign inside this box
                                                </text>
                                            )}
                                            {borrowerSignaturePath ? (
                                                <path
                                                    d={borrowerSignaturePath}
                                                    fill="none"
                                                    stroke="#0f172a"
                                                    strokeWidth={Math.max(2, Math.round((borrowerSignatureSurfaceSize.width || 900) / 220))}
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            ) : null}
                                        </svg>
                                    </div>
                                ) : (
                                    <div className="signature-preview-box" style={{ width: '100%', minHeight: '220px', border: '1px solid #cbd5e1', borderRadius: '14px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                                        <img
                                            src={borrowerSignaturePreviewDataUrl}
                                            alt="Borrower signature preview"
                                            style={{ maxWidth: '100%', maxHeight: '180px', objectFit: 'contain', display: 'block' }}
                                        />
                                    </div>
                                )}

                                <div className="form-group sanction-consent-group" style={{ marginTop: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}>
                                        Consent <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <label
                                        htmlFor="sanctionConsent"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            cursor: 'pointer',
                                            padding: '14px 16px',
                                            borderRadius: '14px',
                                            border: '1px solid #fdba74',
                                            background: '#fff'
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            id="sanctionConsent"
                                            name="sanctionConsent"
                                            checked={sanctionConsent}
                                            onChange={(e) => setSanctionConsent(e.target.checked)}
                                            style={{ display: 'none' }}
                                        />
                                        <span
                                            style={{
                                                width: '28px',
                                                height: '28px',
                                                border: '2px solid #64748b',
                                                borderRadius: '6px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: sanctionConsent ? '#16a34a' : '#fff',
                                                color: '#fff',
                                                fontWeight: 800,
                                                fontSize: '18px',
                                                lineHeight: 1
                                            }}
                                        >
                                            {sanctionConsent ? '✓' : ''}
                                        </span>
                                        <span style={{ fontWeight: 600 }}>
                                            I have read and agree to the terms and conditions and acknowledge the Sanction Letter by selecting the checkbox.
                                        </span>
                                    </label>
                                </div>

                                {borrowerSignatureSubmitted ? (
                                    <div className="sanction-preview-inline" style={{ marginTop: '14px' }}>
                                        <div>
                                            <strong>Sanction letter ready</strong>
                                            <p>Preview the same signed sanction letter that will be available from the Dashboard side menu after disbursement.</p>
                                        </div>
                                        <button
                                            type="button"
                                            className="secondary-btn"
                                            onClick={() => {
                                                setSanctionPreviewHtmlSnapshot(savedSanctionLetterHtml);
                                                setShowSanctionPreview(true);
                                            }}
                                            disabled={!savedSanctionLetterHtml}
                                        >
                                            Preview Sanction Letter
                                        </button>
                                    </div>
                                ) : (
                                    <p className="final-submission-note" style={{ marginTop: '14px' }}>
                                        Save the signature first to update the sanction preview.
                                    </p>
                                )}

                                <div className="signature-status-line" style={{ marginTop: '8px', color: borrowerSignatureSubmitted ? '#16a34a' : (borrowerSignaturePresent ? '#16a34a' : '#94a3b8'), fontSize: '0.9rem', fontWeight: 600 }}>
                                    {borrowerSignatureSubmitted
                                        ? 'Signature saved'
                                        : borrowerSignaturePresent
                                            ? 'Signature ready to submit'
                                            : 'Signature pending'}
                                </div>
                                {borrowerSignatureSubmitted && borrowerSignaturePreviewSignedAtText ? (
                                    <div className="signature-timestamp" style={{ marginTop: '4px', color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}>
                                        Saved on: {borrowerSignaturePreviewSignedAtText}
                                    </div>
                                ) : null}
                                <div className="signature-actions" style={{ display: 'flex', gap: '12px', marginTop: '14px' }}>
                                    <button
                                        type="button"
                                        className="secondary-btn"
                                        onClick={clearBorrowerSignature}
                                        style={{ flex: '0 0 160px' }}
                                    >
                                        Clear
                                    </button>
                                    {!borrowerSignatureSubmitted ? (
                                        <button
                                            type="button"
                                            className="action-btn"
                                            onClick={handleBorrowerSignatureSubmit}
                                            disabled={loading || !borrowerSignaturePresent}
                                            style={{ flex: 1, marginTop: 0 }}
                                        >
                                            {loading ? 'Saving Signature...' : 'Save Signature'}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="action-btn"
                                            onClick={continueToPennyDrop}
                                            disabled={loading || !hasRenderableJourneyBankDetails}
                                            style={{ flex: 1, marginTop: 0 }}
                                        >
                                            {loading ? 'Recording Sanction Acceptance...' : 'Continue to Penny Drop →'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {renderDashboardSaveButton()}
                        </div>
                    </div>
                )}

                {showSanctionPreview && (
                    <div className="sanction-preview-overlay">
                        <div className="sanction-preview-window">
                            <div className="sanction-preview-header" style={{ position: 'relative', paddingRight: '56px' }}>
                                <div>
                                    <h3>Sanction Letter Preview</h3>
                                    <p>{resolvedCustomerName} | {loanJourney?.loanId || 'Pending Loan ID'}</p>
                                </div>
                                <button
                                    type="button"
                                    className="sanction-preview-close"
                                    onClick={() => {
                                        setShowSanctionPreview(false);
                                        setSanctionPreviewHtmlSnapshot('');
                                    }}
                                    aria-label="Close sanction letter preview"
                                >
                                    ×
                                </button>
                            </div>
                            <div
                                className="sanction-preview-body"
                                dangerouslySetInnerHTML={{ __html: sanctionPreviewHtmlSnapshot || savedSanctionLetterHtml || '' }}
                            />
                            <div className="sanction-preview-actions">
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={() => {
                                        setShowSanctionPreview(false);
                                        setSanctionPreviewHtmlSnapshot('');
                                    }}
                                >
                                    Close Preview
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {step === 7 && (
                    <div className="penny-drop-section" style={{ textAlign: 'center', padding: '40px 0' }}>
                        {borrowerSignaturePreviewDataUrl ? (
                            <div style={{ maxWidth: '640px', margin: '0 auto 20px', padding: '16px', borderRadius: '18px', border: '1px solid #e2e8f0', background: '#fff', textAlign: 'left' }}>
                                <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>Borrower Signature Saved</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                    <div style={{ flex: '1 1 220px', minWidth: '220px', minHeight: '120px', border: '1px solid #cbd5e1', borderRadius: '14px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
                                        <img
                                            src={borrowerSignaturePreviewDataUrl}
                                            alt="Borrower signature preview"
                                            style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain', display: 'block' }}
                                        />
                                    </div>
                                    <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                        <div style={{ color: '#16a34a', fontWeight: 700, marginBottom: '4px' }}>Signature captured</div>
                                        {borrowerSignaturePreviewSignedAtText ? (
                                            <div style={{ color: '#64748b', fontSize: '0.9rem' }}>
                                                Saved on: {borrowerSignaturePreviewSignedAtText}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        <div className="spinner" style={{ margin: '0 auto 30px', width: '60px', height: '60px', borderWidth: '5px' }}></div>
                        <h3>Penny Drop Verification</h3>
                        <p style={{ color: '#64748b', maxWidth: '400px', margin: '15px auto' }}>
                            We are transferring ₹1 to <strong>{resolvedLoanJourneyBankDetails.accountLast4 ? `XXXXXX${resolvedLoanJourneyBankDetails.accountLast4}` : String(bankDetails.accountNumber || '').slice(-4).padStart(String(bankDetails.accountNumber || '').length, 'X')}</strong> to verify your account.
                        </p>
                        {pennyDropStatus.message && (
                            <p style={{ color: '#0f172a', fontWeight: '600', maxWidth: '460px', margin: '0 auto 16px' }}>
                                {pennyDropStatus.message}
                            </p>
                        )}
                        <div className="verification-steps">
                            <div className="v-step active">Checking Bank Status...</div>
                            <div className="v-step">Initiating ₹1 Transfer...</div>
                            <div className="v-step">Verifying Account Name...</div>
                        </div>
                        {renderDashboardSaveButton()}
                    </div>
                )}

                {step === 8 && (
                    <div className="disbursement-section" style={{ textAlign: 'center' }}>
                        <div className="disbursement-hero" style={{ background: '#f8fafc', padding: '30px', borderRadius: '24px', marginBottom: '30px', border: '2px dashed #e2e8f0' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>💸</div>
                            <h3 style={{ fontSize: '1.5rem', color: '#1e293b' }}>Ready for Disbursement</h3>
                            <p style={{ color: '#64748b' }}>Verification complete.</p>

                            <div style={{ margin: '25px 0', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                                <small style={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Amount to be Disbursed</small>
                                <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#10b981', marginTop: '5px' }}>{formatINR(finalDisbursement)}</div>
                            </div>

                            <div style={{ background: 'white', padding: '15px', borderRadius: '16px', textAlign: 'left', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ color: '#64748b' }}>Bank</span>
                                    <strong style={{ color: '#1e293b' }}>{resolvedDisbursementBankName || resolvedLoanJourneyBankDetails.bankName || '--'}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#64748b' }}>Account</span>
                                    <strong style={{ color: '#1e293b' }}>
                                        {resolvedLoanJourneyBankDetails.accountLast4
                                            ? `XXXXXX${resolvedLoanJourneyBankDetails.accountLast4}`
                                            : String(bankDetails.accountNumber || '').slice(-4).padStart(12, 'X')}
                                    </strong>
                                </div>
                            </div>
                        </div>

                        {pendingDisbursalVoucher?.loanId === String(loanJourney?.loanId || '').trim() && (
                            <p style={{ color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '10px 14px', margin: '0 0 16px', fontSize: '0.9rem' }}>
                                Voucher already created — funds will not be sent again. Tap below to retry confirmation.
                            </p>
                        )}
                        <button
                        className="action-btn disburse-btn"
                        onClick={handleDisburse}
                        style={{ width: '100%', padding: '20px', fontSize: '1.2rem', background: '#10b981' }}
                        disabled={loading || finalDisbursement <= 0}
                        aria-busy={loading}
                    >
                            {loading ? (
                                <span className="disburse-btn__content">
                                    <span className="disburse-btn__spinner" aria-hidden="true"></span>
                                    {pendingDisbursalVoucher?.loanId === String(loanJourney?.loanId || '').trim() ? 'Confirming Disbursal...' : 'Processing Disbursal...'}
                                </span>
                            ) : (pendingDisbursalVoucher?.loanId === String(loanJourney?.loanId || '').trim() ? 'Confirm Disbursal →' : 'Disburse All Amount Now →')}
                        </button>
                        {renderDashboardSaveButton()}
                    </div>
                )}
            </div>
        </div>
    );
}

export default LoanApplication;
